import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import * as api from './api';
import { APP_VERSION } from './version';

// Umbrella-site link, environment-aware: the test app points at the test
// umbrella so each environment stays self-contained.
const GIP_URL = (typeof window !== "undefined" && (window.location.hostname.startsWith("test.") || window.location.hostname === "localhost"))
  ? "https://test.gamesinparadise.com"
  : "https://gamesinparadise.com";

// ── Heritage: the club began as Men of Margaritaville (MoM). Everything played
// before the Dinks in Paradise rebrand is the "MoM era"; the annual "MoM
// Classic" (any tournament with that phrase in its name) flies the old banner.
const MOM_ERA_CUTOFF = "2026-07-05";
// Men's tournaments only — MoM was Men of Margaritaville, so the mixed/women's
// events of that period are not part of the heritage era.
const isMomEra = (t) => {
  const d = t?.finishedAt || t?.createdAt || "";
  return d !== "" && d.slice(0, 10) < MOM_ERA_CUTOFF && (t?.division || "mens") === "mens";
};
// The four who started it all; everyone else from the era is an original member.
const MOM_FOUNDERS = ["ray ross", "paul martino", "rob cowden", "tony gregorio"];
const isMomFounder = (name) => MOM_FOUNDERS.includes((name || "").trim().toLowerCase());
const isMomClassic = (t) => /mom\s+classic/i.test(t?.name || "");
function MomEraBadge({ label = "MoM Era" }) {
  return (
    <span title="Played in the Men of Margaritaville era — where it all started" style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 9.5, fontWeight: 700, letterSpacing: "0.05em", color: C.textSec, border: `1px solid ${C.border}`, padding: "2px 7px", borderRadius: 10, whiteSpace: "nowrap", opacity: 0.85 }}>
      <img src="/mom-logo.png" alt="" style={{ height: 13, width: "auto" }} />{label}
    </span>
  );
}

function uid() { return crypto.randomUUID(); }
function fmtLabel(format) {
  if (format === "roundrobin") return "Round Robin by Team";
  if (format === "rrplayer") return "PBR";
  if (format === "bracket") return "Bracket";
  return "King of the Hill";
}

// ════════════════════════════════════════════════════════════════════
// COURT MOVEMENT LOGIC
// ════════════════════════════════════════════════════════════════════
function buildNextRoundCourts(prevCourts, partnerHistory = [], genderLookup = null) {
  // Build a set of past partner pairs for quick lookup
  const pastPairs = new Set();
  (partnerHistory || []).forEach(pair => {
    const key = [pair[0], pair[1]].sort().join("|");
    pastPairs.add(key);
  });
  const hadPartner = (a, b) => pastPairs.has([a, b].sort().join("|"));

  const results = prevCourts.map(c => {
    const aWins = (c.scoreA || 0) >= (c.scoreB || 0);
    return {
      courtId: c.courtId, courtNumber: c.courtNumber,
      winners: aWins ? [...c.teamA] : [...c.teamB],
      losers: aWins ? [...c.teamB] : [...c.teamA],
    };
  });
  const n = results.length;

  // Given 4 players, pick the best pairing that minimizes repeat partnerships
  function bestPairing(four) {
    if (four.length < 4) return { teamA: [four[0], four[1] || four[0]], teamB: [four[2] || four[0], four[3] || four[1]] };
    let pairings = [
      [[0,1],[2,3]], [[0,2],[1,3]], [[0,3],[1,2]]
    ];
    // Mixed division: only consider splits where BOTH teams are one man + one woman.
    // With 2M+2F on a court exactly 2 of the 3 splits qualify. If none qualify
    // (shouldn't happen with balanced courts), fall back to unconstrained.
    if (genderLookup) {
      const g = (i) => genderLookup(four[i].id);
      const isMF = (a, b) => (g(a) === "M" && g(b) === "F") || (g(a) === "F" && g(b) === "M");
      const valid = pairings.filter(([pA, pB]) => isMF(pA[0], pA[1]) && isMF(pB[0], pB[1]));
      if (valid.length > 0) pairings = valid;
    }
    let best = null, bestScore = 999;
    for (const [pA, pB] of pairings) {
      let score = 0;
      if (hadPartner(four[pA[0]].id, four[pA[1]].id)) score++;
      if (hadPartner(four[pB[0]].id, four[pB[1]].id)) score++;
      if (score < bestScore) { bestScore = score; best = { teamA: [four[pA[0]], four[pA[1]]], teamB: [four[pB[0]], four[pB[1]]] }; }
      if (bestScore === 0) break;
    }
    return best;
  }

  const newCourts = [];
  for (let i = 0; i < n; i++) {
    let four = [];
    if (n === 1) {
      const w = results[0].winners, l = results[0].losers;
      four = [w[0], l[0], w[1], l[1]];
    } else if (i === 0) {
      const stay = results[0].winners, moveDown = results[1].winners;
      four = [stay[0], moveDown[0], stay[1], moveDown[1]];
    } else if (i === n - 1) {
      const stay = results[i].losers, moveUp = results[i - 1].losers;
      four = [stay[0], moveUp[0], stay[1], moveUp[1]];
    } else {
      const fromBelow = results[i + 1].winners, fromAbove = results[i - 1].losers;
      four = [fromBelow[0], fromAbove[0], fromBelow[1], fromAbove[1]];
    }
    const paired = bestPairing(four);
    newCourts.push({
      courtId: results[i].courtId, courtNumber: results[i].courtNumber,
      teamA: paired.teamA, teamB: paired.teamB,
      scoreA: null, scoreB: null,
    });
  }
  return newCourts;
}

function exportResultsImage({ name, format, rounds, players, finishedAt, board, isTeamFormat, teamWinner, indivWinner }) {
  const W = 800, PAD = 40, ROW_H = 36;
  const standings = board.slice(0, 16);
  const headerH = 160;
  const championH = isTeamFormat && teamWinner ? 100 : indivWinner ? 80 : 0;
  const standingsH = 40 + standings.length * ROW_H + 20;
  const footerH = 50;
  const H = PAD + headerH + championH + standingsH + footerH + PAD;
  const canvas = document.createElement("canvas");
  canvas.width = W * 2; canvas.height = H * 2;
  const ctx = canvas.getContext("2d"); ctx.scale(2, 2);
  ctx.fillStyle = "#0f1923"; ctx.fillRect(0, 0, W, H);
  let y = PAD;
  ctx.fillStyle = "#ffffff"; ctx.font = "bold 28px -apple-system, system-ui, sans-serif";
  ctx.fillText("\u{1F3D3} " + name, PAD, y + 30);
  const fmt = fmtLabel(format);
  ctx.fillStyle = "#8899aa"; ctx.font = "15px -apple-system, system-ui, sans-serif";
  ctx.fillText(fmt + " \u00B7 " + (rounds||[]).length + " rounds \u00B7 " + (players||[]).length + " players", PAD, y + 56);
  const dateStr = finishedAt ? new Date(finishedAt).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" }) : "";
  ctx.fillText(dateStr, PAD, y + 78);
  y += 100; ctx.fillStyle = "#22c55e"; ctx.fillRect(PAD, y, 60, 3); y += 20;
  if ((isTeamFormat && teamWinner) || indivWinner) {
    ctx.fillStyle = "#ffd700"; ctx.font = "bold 20px -apple-system, system-ui, sans-serif";
    ctx.fillText("\u{1F3C6} CHAMPION", PAD, y + 24);
    if (isTeamFormat && teamWinner) {
      ctx.fillStyle = "#ffffff"; ctx.font = "bold 24px -apple-system, system-ui, sans-serif";
      ctx.fillText(teamWinner.name, PAD + 10, y + 56);
      if (teamWinner.players) { ctx.fillStyle = "#8899aa"; ctx.font = "15px -apple-system, system-ui, sans-serif"; ctx.fillText(teamWinner.players.map(p => p.name).join(" & ") + (teamWinner.wins != null ? " \u00B7 " + teamWinner.wins + "W-" + teamWinner.losses + "L" : ""), PAD + 10, y + 80); }
      y += championH;
    } else if (indivWinner) {
      ctx.fillStyle = "#ffffff"; ctx.font = "bold 24px -apple-system, system-ui, sans-serif";
      ctx.fillText(indivWinner.name, PAD + 10, y + 56);
      ctx.fillStyle = "#8899aa"; ctx.font = "15px -apple-system, system-ui, sans-serif";
      ctx.fillText(indivWinner.pts + " pts \u00B7 " + indivWinner.wins + "W-" + indivWinner.losses + "L \u00B7 " + (indivWinner.diff > 0 ? "+" : "") + indivWinner.diff + " diff", PAD + 10, y + 76);
      y += championH;
    }
  }
  ctx.fillStyle = "#8899aa"; ctx.font = "bold 12px -apple-system, system-ui, sans-serif"; ctx.fillText("FINAL STANDINGS", PAD, y + 16); y += 32;
  ctx.fillStyle = "#556677"; ctx.font = "bold 11px -apple-system, system-ui, sans-serif";
  ctx.fillText("#", PAD, y + 12); ctx.fillText("Player", PAD + 30, y + 12);
  ctx.textAlign = "right"; ctx.fillText("PTS", W-PAD-200, y+12); ctx.fillText("W-L", W-PAD-130, y+12); ctx.fillText("DIFF", W-PAD-60, y+12); ctx.fillText("GP", W-PAD, y+12); ctx.textAlign = "left";
  y += 24; ctx.fillStyle = "#1a2a3a"; ctx.fillRect(PAD, y-2, W-PAD*2, 1); y += 6;
  standings.forEach((p, i) => {
    const rowY = y + i * ROW_H;
    if (i % 2 === 0) { ctx.fillStyle = "#141f2b"; ctx.fillRect(PAD, rowY-4, W-PAD*2, ROW_H); }
    const medal = i===0?"\u{1F947}":i===1?"\u{1F948}":i===2?"\u{1F949}":"";
    ctx.font = "14px -apple-system, system-ui, sans-serif";
    if (medal) { ctx.fillText(medal, PAD, rowY+18); } else { ctx.fillStyle="#556677"; ctx.font="bold 13px -apple-system, system-ui, sans-serif"; ctx.fillText(String(i+1), PAD+4, rowY+18); }
    ctx.fillStyle = i<3?"#ffffff":"#ccdaee"; ctx.font = (i<3?"bold ":"") + "14px -apple-system, system-ui, sans-serif"; ctx.fillText(p.name, PAD+30, rowY+18);
    ctx.textAlign="right"; ctx.font="bold 14px -apple-system, system-ui, monospace";
    ctx.fillStyle=i===0?"#ffd700":"#ccdaee"; ctx.fillText(String(p.pts), W-PAD-200, rowY+18);
    ctx.fillStyle=p.wins>p.losses?"#22c55e":p.losses>p.wins?"#ef4444":"#ccdaee"; ctx.fillText(p.wins+"-"+p.losses, W-PAD-130, rowY+18);
    ctx.fillStyle=p.diff>0?"#22c55e":p.diff<0?"#ef4444":"#8899aa"; ctx.fillText((p.diff>0?"+":"")+p.diff, W-PAD-60, rowY+18);
    ctx.fillStyle="#8899aa"; ctx.fillText(String(p.gp), W-PAD, rowY+18); ctx.textAlign="left";
  });
  const footerY = y + standings.length * ROW_H + 20;
  ctx.fillStyle = "#334455"; ctx.font = "12px -apple-system, system-ui, sans-serif"; ctx.fillText("Dinks In Paradise \u00B7 dinksinparadise.com", PAD, footerY + 16);
  canvas.toBlob(blob => { const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = (name||"tournament").replace(/[^a-zA-Z0-9 ]/g,"").replace(/\s+/g,"-").toLowerCase()+"-results.png"; a.click(); URL.revokeObjectURL(url); }, "image/png");
}

function calcLeaderboard(rounds, mode = "pts") {
  const s = {};
  // Track head-to-head results for tiebreaking
  const h2h = {}; // "idA|idB" -> { aWins: 0, bWins: 0 }
  rounds.forEach(round => {
    (round.courts || []).forEach(c => {
      const add = (team, score, opp) => {
        team.forEach(p => {
          if (!s[p.id]) s[p.id] = { ...p, pts: 0, wins: 0, losses: 0, gp: 0, diff: 0 };
          if (score !== null && opp !== null) {
            s[p.id].pts += score || 0;
            s[p.id].diff += (score || 0) - (opp || 0);
            s[p.id].gp += 1;
            if (score > opp) s[p.id].wins += 1;
            else if (score < opp) s[p.id].losses += 1;
          }
        });
      };
      add(c.teamA, c.scoreA, c.scoreB);
      add(c.teamB, c.scoreB, c.scoreA);
      // Record h2h between all players on opposite teams
      if (c.scoreA !== null && c.scoreB !== null) {
        (c.teamA || []).forEach(pa => {
          (c.teamB || []).forEach(pb => {
            const key = [pa.id, pb.id].sort().join("|");
            if (!h2h[key]) h2h[key] = {};
            if ((c.scoreA || 0) > (c.scoreB || 0)) { h2h[key][pa.id] = (h2h[key][pa.id] || 0) + 1; }
            else if ((c.scoreB || 0) > (c.scoreA || 0)) { h2h[key][pb.id] = (h2h[key][pb.id] || 0) + 1; }
          });
        });
      }
    });
  });
  const arr = Object.values(s);
  // H2H comparator: returns negative if a beats b head-to-head
  const h2hCompare = (a, b) => {
    const key = [a.id, b.id].sort().join("|");
    const rec = h2h[key];
    if (!rec) return 0;
    return (rec[b.id] || 0) - (rec[a.id] || 0);
  };
  if (mode === "dtr") {
    arr.forEach(p => { p.dtr = (10 * p.wins) + p.pts + p.diff; });
    return arr.sort((a, b) => b.dtr - a.dtr || b.wins - a.wins || h2hCompare(a, b) || b.diff - a.diff || b.pts - a.pts);
  }
  return arr.sort((a, b) => b.pts - a.pts || b.wins - a.wins || h2hCompare(a, b) || b.diff - a.diff);
}

// ════════════════════════════════════════════════════════════════════
// DPR (DINK POWER RATING) — Rolling Elo-style rating from tournament results
// ════════════════════════════════════════════════════════════════════
// Base: 3.0 on a 0-5 scale. K-factor of 0.4 per tournament.
// Formula per tournament: adjust = K * (winRate - expectedWinRate) + marginBonus
// winRate = wins / gamesPlayed (0 to 1)
// expectedWinRate = currentDPR / 5.0 (maps rating to expected performance)
// marginBonus = avgPointDiff / 50 (small bonus/penalty for dominance)
// Recent tournaments weighted more: last 3 get 2x weight, 4-10 get 1x
//
// SPLIT RATING: two independent pools maintained in one chronological walk.
//   - DPR pool  ("dpr"):   single-gender divisions (mens/womens; missing division = mens)
//   - Mixed pool ("mixed"): mixed division → mDPR
// Routing: non-mixed → DPR pool only. Mixed before GRANDFATHER_CUTOFF → BOTH
// pools (each pool evolves independently with its own ratings). Mixed on/after
// the cutoff → mixed pool only.
function calculateAllDPR(allTournaments, allPlayers) {
  const BASE_DPR = 3.0;
  const MIN_TOURNEYS = 2;   // DPR appears from 2 tournaments (shown faintly until 3 — see PROVISIONAL_TOURNEYS in the UI)
  const K = 0.15;        // Per-game K-factor (smaller since we update every game, not per tourney)
  const D = 1.5;         // Elo scaling divisor (compressed for 0.5-5.0 range)
  const MIN_DPR = 0.5;
  const MAX_DPR = 5.0;
  // Mixed tournaments finished before this date also count in the single-gender
  // pool (one-time grandfather for the club's first mixed event, which
  // historically fed DPR).
  const GRANDFATHER_CUTOFF = "2026-07-06";

  // Get all finished KotH tournaments sorted chronologically (ratings use KotH only)
  const finished = allTournaments
    .filter(t => t.status === "finished" && t.format === "kingofhill" && t.rounds && t.rounds.length > 0)
    .sort((a, b) => (a.finishedAt || a.createdAt || "").localeCompare(b.finishedAt || b.createdAt || ""));

  // Roster ranking lookup — fallback seed for the mixed pool
  const rankingById = {};
  (allPlayers || []).forEach(p => { if (p && p.id) rankingById[p.id] = p.ranking; });

  // Two independent rating pools, each with its own live ratings, per-pool
  // trend snapshots (rating before the player's most recent tournament IN THAT
  // POOL), and per-pool tournament participation counts.
  const makePool = () => ({
    ratings: {},               // playerId -> current rating
    ratingBeforeLastTourney: {}, // playerId -> rating snapshot for trend
    tourneySets: {},           // playerId -> Set of tourneyIds
    gameCounts: {},            // playerId -> games played (confidence)
    lastPlayed: {},            // playerId -> date of most recent tournament (recency)
    currentTourneyId: null,
  });
  const pools = { dpr: makePool(), mixed: makePool() };
  const mixedSeed = {};       // playerId -> value the mixed rating was seeded from
  const mixedSeedSource = {}; // playerId -> "dpr" | "ranking" | "base"

  const getRating = (pool, pid) => pool.ratings[pid] !== undefined ? pool.ratings[pid] : BASE_DPR;

  // Apply one game to one pool — Elo math identical to the original single-pool version.
  const processGame = (pool, game) => {
    // Snapshot ratings at the boundary of each new tournament (per pool)
    if (game.tourneyId !== pool.currentTourneyId) {
      pool.currentTourneyId = game.tourneyId;
      [...game.teamA, ...game.teamB].forEach(pid => {
        // Keep overwriting — by the end this holds the rating just before the
        // player's most recent tournament in this pool
        pool.ratingBeforeLastTourney[pid] = getRating(pool, pid);
      });
    } else {
      // Within same tourney, snapshot any player we haven't seen yet this tourney
      [...game.teamA, ...game.teamB].forEach(pid => {
        if (pool.ratingBeforeLastTourney[pid] === undefined) {
          pool.ratingBeforeLastTourney[pid] = getRating(pool, pid);
        }
      });
    }

    // Calculate team DPR = average of partners' current ratings
    const teamADpr = game.teamA.reduce((sum, pid) => sum + getRating(pool, pid), 0) / game.teamA.length;
    const teamBDpr = game.teamB.reduce((sum, pid) => sum + getRating(pool, pid), 0) / game.teamB.length;

    // Elo expected score: E = 1 / (1 + 10^((oppDPR - yourDPR) / D))
    const expectedA = 1 / (1 + Math.pow(10, (teamBDpr - teamADpr) / D));
    const expectedB = 1 - expectedA;

    // Actual result: 1 for win, 0 for loss
    const actualA = game.scoreA > game.scoreB ? 1 : 0;
    const actualB = 1 - actualA;

    // Margin multiplier: winning big = bigger boost, close game = small adjustment
    const maxScore = Math.max(game.scoreA, game.scoreB, 1);
    const pointDiff = Math.abs(game.scoreA - game.scoreB);
    const marginMult = 1 + (pointDiff / maxScore);

    // Update each player's rating
    game.teamA.forEach(pid => {
      const adjust = K * marginMult * (actualA - expectedA);
      pool.ratings[pid] = Math.max(MIN_DPR, Math.min(MAX_DPR, getRating(pool, pid) + adjust));
      pool.gameCounts[pid] = (pool.gameCounts[pid] || 0) + 1;
    });
    game.teamB.forEach(pid => {
      const adjust = K * marginMult * (actualB - expectedB);
      pool.ratings[pid] = Math.max(MIN_DPR, Math.min(MAX_DPR, getRating(pool, pid) + adjust));
      pool.gameCounts[pid] = (pool.gameCounts[pid] || 0) + 1;
    });
  };

  // Single chronological walk over tournaments, routing each to one or both pools
  finished.forEach(t => {
    const division = t.division || "mens";
    const dateKey = t.finishedAt || t.createdAt || "";
    let targets;
    if (division !== "mixed") targets = ["dpr"];
    else if (dateKey < GRANDFATHER_CUTOFF) targets = ["dpr", "mixed"]; // grandfathered: counts in BOTH pools
    else targets = ["mixed"];

    // Flatten this tournament's completed games (same shape/order as before)
    const games = [];
    (t.rounds || []).forEach(r => {
      (r.courts || []).forEach(c => {
        if (c.scoreA === null || c.scoreB === null) return;
        const teamAIds = (c.teamA || []).map(p => p.id).filter(Boolean);
        const teamBIds = (c.teamB || []).map(p => p.id).filter(Boolean);
        if (teamAIds.length === 0 || teamBIds.length === 0) return;
        games.push({ teamA: teamAIds, teamB: teamBIds, scoreA: c.scoreA || 0, scoreB: c.scoreB || 0, tourneyId: t.id });
      });
    });
    if (games.length === 0) return;

    const participants = new Set();
    games.forEach(g => [...g.teamA, ...g.teamB].forEach(pid => participants.add(pid)));

    // mDPR SEEDING: first time a player enters the mixed pool, initialize their
    // mixed rating from their current DPR-pool rating (entering this event) →
    // else roster ranking clamped to the rating range → else base 3.0.
    // Seeded BEFORE this tournament's games are applied to either pool.
    if (targets.includes("mixed")) {
      participants.forEach(pid => {
        if (pools.mixed.ratings[pid] !== undefined) return;
        let seed, source;
        if (pools.dpr.ratings[pid] !== undefined) {
          seed = pools.dpr.ratings[pid]; source = "dpr";
        } else if (rankingById[pid] != null && !isNaN(Number(rankingById[pid]))) {
          seed = Math.max(MIN_DPR, Math.min(MAX_DPR, Number(rankingById[pid]))); source = "ranking";
        } else {
          seed = BASE_DPR; source = "base";
        }
        pools.mixed.ratings[pid] = seed;
        mixedSeed[pid] = seed;
        mixedSeedSource[pid] = source;
      });
    }

    targets.forEach(key => {
      const pool = pools[key];
      // Tournament participation counts per pool (grandfathered mixed counts in both)
      participants.forEach(pid => {
        if (!pool.tourneySets[pid]) pool.tourneySets[pid] = new Set();
        pool.tourneySets[pid].add(t.id);
        if (dateKey) pool.lastPlayed[pid] = dateKey; // most recent counts last (chronological walk)
      });
      games.forEach(g => processGame(pool, g));
    });
  });

  // ── Rating confidence (0–100) — how much to trust a shown rating ──
  // Driven mostly by games played (a saturating curve), lightly trimmed when the
  // rating is still swinging or the player has been inactive a long time.
  // Tuning knobs: HALF_GAMES (games ≈ 50%), and the recency/volatility factors.
  const CONFIDENCE_HALF_GAMES = 16;   // ~1 tourney≈32%, 3≈59%, 6≈74%, 10≈83%
  const now = Date.now();
  const confidenceFor = (pool, pid) => {
    const games = pool.gameCounts[pid] || 0;
    if (games === 0) return null;
    let conf = games / (games + CONFIDENCE_HALF_GAMES);
    // Recency: a rating built only from old games is less certain about today.
    const last = pool.lastPlayed[pid];
    if (last) {
      const days = (now - new Date(last).getTime()) / 86400000;
      if (days > 120) conf *= Math.max(0.6, 1 - (days - 120) / 900); // ease down after ~4 months
    }
    // Volatility: still swinging tournament-to-tournament → less settled.
    const prev = pool.ratingBeforeLastTourney[pid];
    if (prev !== undefined && Math.abs(getRating(pool, pid) - prev) > 0.25) conf *= 0.9;
    return Math.max(1, Math.min(99, Math.round(conf * 100)));
  };

  // Build final map — union of players seen in either pool
  const dprMap = {};
  const allIds = new Set([...Object.keys(pools.dpr.tourneySets), ...Object.keys(pools.mixed.tourneySets)]);
  allIds.forEach(pid => {
    // ── Single-gender DPR (unchanged rule: null until MIN_TOURNEYS) ──
    const tourneyCount = pools.dpr.tourneySets[pid] ? pools.dpr.tourneySets[pid].size : 0;
    let dpr = null, trend = null;
    if (tourneyCount >= MIN_TOURNEYS) {
      const finalRating = getRating(pools.dpr, pid);
      const prevRating = pools.dpr.ratingBeforeLastTourney[pid] !== undefined ? pools.dpr.ratingBeforeLastTourney[pid] : BASE_DPR;
      trend = finalRating > prevRating + 0.05 ? "up" : finalRating < prevRating - 0.05 ? "down" : "flat";
      dpr = Math.round(finalRating * 100) / 100;
    }
    // ── Mixed mDPR (shown immediately from the first event; provisional UI until MIN_TOURNEYS) ──
    const mixedTourneyCount = pools.mixed.tourneySets[pid] ? pools.mixed.tourneySets[pid].size : 0;
    let mdpr = null, mixedTrend = null;
    if (mixedTourneyCount >= 1) {
      const finalRating = getRating(pools.mixed, pid);
      // For a single event the snapshot equals the seed, so this is trend-vs-seed
      const prevRating = pools.mixed.ratingBeforeLastTourney[pid] !== undefined
        ? pools.mixed.ratingBeforeLastTourney[pid]
        : (mixedSeed[pid] !== undefined ? mixedSeed[pid] : BASE_DPR);
      mixedTrend = finalRating > prevRating + 0.05 ? "up" : finalRating < prevRating - 0.05 ? "down" : "flat";
      mdpr = Math.round(finalRating * 100) / 100;
    }
    dprMap[pid] = {
      dpr, tourneyCount, trend,
      confidence: dpr != null ? confidenceFor(pools.dpr, pid) : null,
      mdpr, mixedTourneyCount, mixedTrend,
      mixedConfidence: mdpr != null ? confidenceFor(pools.mixed, pid) : null,
      mdprSeededFrom: mixedSeed[pid] !== undefined ? Math.round(mixedSeed[pid] * 100) / 100 : null,
      mdprSeedSource: mixedSeedSource[pid] || null,
    };
  });

  return dprMap;
}

// ════════════════════════════════════════════════════════════════════
// THEME
// ════════════════════════════════════════════════════════════════════
const DARK = {
  bg: "#0b0f14", surface: "#131920", surfaceHover: "#1a222c",
  card: "#161e28", cardHover: "#1c2636", border: "#243040", borderLight: "#1e2a38",
  inputBg: "#0f151c", accent: "#4a9eff", accentDim: "rgba(74,158,255,0.12)",
  accentDark: "#3580d4", blue: "#5ac8fa", blueDim: "rgba(90,200,250,0.12)",
  red: "#ff5757", redDim: "rgba(255,87,87,0.1)", orange: "#ff9f43",
  orangeDim: "rgba(255,159,67,0.1)", gold: "#ffd700", goldDim: "rgba(255,215,0,0.12)",
  silver: "#a8b8cc", bronze: "#cd7f32", text: "#e8edf4", textSec: "#8a9bb5",
  textMuted: "#5a6a80", teamName: "#e8edf4", teamNameBg: "rgba(74,158,255,0.10)", teamNameBorder: "rgba(74,158,255,0.25)",
};
const LIGHT = {
  bg: "#f4f6f9", surface: "#ffffff", surfaceHover: "#eef1f5",
  card: "#ffffff", cardHover: "#f8f9fb", border: "#d8dfe8", borderLight: "#e4e9f0",
  inputBg: "#f0f2f5", accent: "#2e7bd6", accentDim: "rgba(46,123,214,0.10)",
  accentDark: "#1f5fa8", blue: "#1a8fb5", blueDim: "rgba(26,143,181,0.10)",
  red: "#e04040", redDim: "rgba(224,64,64,0.08)", orange: "#e08830",
  orangeDim: "rgba(224,136,48,0.08)", gold: "#c8a000", goldDim: "rgba(200,160,0,0.10)",
  silver: "#6a7a8e", bronze: "#a06020", text: "#1a2233", textSec: "#556070",
  textMuted: "#8895a5", teamName: "#1a2233", teamNameBg: "rgba(46,123,214,0.08)", teamNameBorder: "rgba(46,123,214,0.30)",
};
let C = DARK;
const font = "'Outfit', sans-serif";
const fontMono = "'JetBrains Mono', monospace";

// ════════════════════════════════════════════════════════════════════
// DIVISIONS (Men's / Women's / Mixed Doubles)
// ════════════════════════════════════════════════════════════════════
const DIVISIONS = {
  mens:   { label: "Men's Doubles",   short: "Men's",   sym: "♂♂" },
  womens: { label: "Women's Doubles", short: "Women's", sym: "♀♀" },
  mixed:  { label: "Mixed Doubles",   short: "Mixed",   sym: "♂♀" },
};
function divColor(div) {
  if (div === "womens") return { color: C.orange, bg: C.orangeDim };
  if (div === "mixed") return { color: C.gold, bg: C.goldDim };
  return { color: C.accent, bg: C.accentDim };
}
function DivisionBadge({ division }) {
  const div = DIVISIONS[division] ? division : "mens";
  const dc = divColor(div);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", fontSize: 9.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.07em", padding: "3px 8px", borderRadius: 10, color: dc.color, background: dc.bg, lineHeight: 1.2, flexShrink: 0, whiteSpace: "nowrap" }}>
      {DIVISIONS[div].short}
    </span>
  );
}
// Confidence percentage shown next to a rating — how settled that rating is.
// Muted color that warms from low (coral) to high (green) so it reads at a glance.
function ConfidencePct({ pct, size = 10 }) {
  if (pct == null) return null;
  const color = pct >= 75 ? "#46b17b" : pct >= 50 ? C.orange : C.textMuted;
  return (
    <span style={{ fontSize: size, fontWeight: 700, color, letterSpacing: "0.02em" }}
      title={`Rating confidence: ${pct}% — grows with games played`}>
      {pct}%
    </span>
  );
}
// Small round gender chip (M = accent, F = orange)
function GenderChip({ gender, size = 22 }) {
  if (gender !== "M" && gender !== "F") return null;
  const isM = gender === "M";
  return (
    <span style={{ width: size, height: size, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: Math.round(size * 0.5), fontWeight: 800, color: isM ? C.accent : C.orange, background: isM ? C.accentDim : C.orangeDim, flexShrink: 0 }}>{gender}</span>
  );
}
// Resolve a player's gender from the tournament's embedded player list.
// (Players inside match teamA/teamB do NOT carry gender — always look up here.)
function genderOf(tournament, playerId) {
  const p = ((tournament && tournament.players) || []).find(pl => pl.id === playerId);
  const g = p ? p.gender : null;
  return g === "M" || g === "F" ? g : null;
}

// ════════════════════════════════════════════════════════════════════
// ICONS
// ════════════════════════════════════════════════════════════════════
const I = {
  Users: (p) => <svg {...p} width={p?.size||18} height={p?.size||18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  Trophy: (p) => <svg {...p} width={p?.size||18} height={p?.size||18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/></svg>,
  Grid: (p) => <svg {...p} width={p?.size||18} height={p?.size||18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>,
  Play: (p) => <svg {...p} width={p?.size||18} height={p?.size||18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>,
  Pause: (p) => <svg {...p} width={p?.size||18} height={p?.size||18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>,
  Plus: (p) => <svg {...p} width={p?.size||16} height={p?.size||16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>,
  X: (p) => <svg {...p} width={p?.size||16} height={p?.size||16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>,
  Check: (p) => <svg {...p} width={p?.size||16} height={p?.size||16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
  ChevRight: (p) => <svg {...p} width={p?.size||16} height={p?.size||16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>,
  ChevLeft: (p) => <svg {...p} width={p?.size||16} height={p?.size||16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>,
  Stop: (p) => <svg {...p} width={p?.size||16} height={p?.size||16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="6" y="6" width="12" height="12" rx="1"/></svg>,
  Settings: (p) => <svg {...p} width={p?.size||18} height={p?.size||18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>,
  Lock: (p) => <svg {...p} width={p?.size||18} height={p?.size||18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
  Logout: (p) => <svg {...p} width={p?.size||18} height={p?.size||18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
  Edit: (p) => <svg {...p} width={p?.size||14} height={p?.size||14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  Trash: (p) => <svg {...p} width={p?.size||14} height={p?.size||14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  Clock: (p) => <svg {...p} width={p?.size||14} height={p?.size||14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  Eye: (p) => <svg {...p} width={p?.size||16} height={p?.size||16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  Hammer: (p) => <svg {...p} width={p?.size||18} height={p?.size||18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 12l-8.5 8.5c-.83.83-2.17.83-3 0 0 0 0 0 0 0a2.12 2.12 0 0 1 0-3L12 9"/><path d="M17.64 15L22 10.64"/><path d="m20.91 11.7-1.25-1.25c-.6-.6-.93-1.4-.93-2.25v-.86L16.01 4.6a5.56 5.56 0 0 0-3.94-1.64H9l.92.82A6.18 6.18 0 0 1 12 8.4v1.56l2 2h2.47l2.26 1.91"/></svg>,
  Swap: (p) => <svg {...p} width={p?.size||16} height={p?.size||16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>,
  Star: (p) => <svg {...p} width={p?.size||14} height={p?.size||14} viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
  Refresh: (p) => <svg {...p} width={p?.size||14} height={p?.size||14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>,
  Sun: (p) => <svg {...p} width={p?.size||18} height={p?.size||18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>,
  Moon: (p) => <svg {...p} width={p?.size||18} height={p?.size||18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>,
  Help: (p) => <svg {...p} width={p?.size||18} height={p?.size||18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  PauseCircle: (p) => <svg {...p} width={p?.size||18} height={p?.size||18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="10" y1="15" x2="10" y2="9"/><line x1="14" y1="15" x2="14" y2="9"/></svg>,
  Undo: (p) => <svg {...p} width={p?.size||16} height={p?.size||16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>,
  Search: (p) => <svg {...p} width={p?.size||16} height={p?.size||16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
  Share: (p) => <svg {...p} width={p?.size||16} height={p?.size||16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>,
  ChevDown: (p) => <svg {...p} width={p?.size||16} height={p?.size||16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>,
  Download: (p) => <svg {...p} width={p?.size||16} height={p?.size||16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>,
  Medal: (p) => <svg {...p} width={p?.size||18} height={p?.size||18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7.21 15 2.66 7.14a2 2 0 0 1 .13-2.2L4.4 2.8A2 2 0 0 1 6 2h12a2 2 0 0 1 1.6.8l1.6 2.14a2 2 0 0 1 .14 2.2L16.79 15"/><path d="M11 12 5.12 2.2"/><path d="m13 12 5.88-9.8"/><path d="M8 7h8"/><circle cx="12" cy="17" r="5"/><path d="M12 18v-2h-.5"/></svg>,
};

// ════════════════════════════════════════════════════════════════════
// GLOBAL CSS
// ════════════════════════════════════════════════════════════════════
function buildCSS() { return `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500;600;700&display=swap');
* { box-sizing: border-box; margin: 0; padding: 0; }
body { background: ${C.bg}; }
input::-webkit-outer-spin-button, input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
input[type=number] { -moz-appearance: textfield; }
::-webkit-scrollbar { width: 6px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: ${C.border}; border-radius: 3px; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
@keyframes slideUp { from { opacity: 0; transform: translateY(100%); } to { opacity: 1; transform: translateY(0); } }
@keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.6; } }
/* fill-mode backwards (not both): a lingering translateY(0) would make every
   panel a containing block and cage position:fixed overlays inside it */
.fade-in { animation: fadeIn 0.25s ease-out backwards; }
.slide-up { animation: slideUp 0.3s cubic-bezier(0.16,1,0.3,1) backwards; }
.btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px; padding: 10px 18px; border-radius: 10px; border: none; font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.15s; font-family: ${font}; letter-spacing: -0.01em; white-space: nowrap; }
.btn:active { transform: scale(0.97); }
.btn-primary { background: ${C.accent}; color: ${C.bg}; }
.btn-primary:hover { background: ${C.accentDark}; }
.btn-blue { background: ${C.blue}; color: #fff; }
.btn-danger { background: ${C.redDim}; color: ${C.red}; }
.btn-danger:hover { background: rgba(255,87,87,0.18); }
.btn-ghost { background: transparent; color: ${C.textSec}; padding: 8px 12px; }
.btn-ghost:hover { color: ${C.text}; background: ${C.surfaceHover}; }
.btn-outline { background: transparent; color: ${C.text}; border: 1px solid ${C.border}; }
.btn-outline:hover { background: ${C.surfaceHover}; }
.btn-sm { padding: 6px 12px; font-size: 12px; border-radius: 8px; }
.btn-full { width: 100%; }
.btn:disabled { opacity: 0.35; pointer-events: none; }
.input { width: 100%; background: ${C.inputBg}; border: 1.5px solid ${C.border}; border-radius: 10px; padding: 11px 14px; color: ${C.text}; font-size: 15px; outline: none; font-family: ${font}; transition: border-color 0.15s, box-shadow 0.15s; }
.input:focus { border-color: ${C.accent}; box-shadow: 0 0 0 3px ${C.accentDim}; }
.input::placeholder { color: ${C.textMuted}; }
.input-score { width: 68px; background: ${C.inputBg}; border: 1.5px solid ${C.border}; border-radius: 10px; padding: 10px 8px; color: ${C.text}; font-size: 20px; font-weight: 700; text-align: center; outline: none; font-family: ${fontMono}; transition: border-color 0.15s, box-shadow 0.15s; }
.input-score:focus { border-color: ${C.accent}; box-shadow: 0 0 0 3px ${C.accentDim}; }
.card { background: ${C.card}; border-radius: 14px; border: 1px solid ${C.border}; padding: 16px; transition: border-color 0.15s, background 0.15s; }
.badge { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 700; padding: 3px 10px; border-radius: 20px; letter-spacing: 0.03em; text-transform: uppercase; }
.modal-backdrop { position: fixed; top: 0; left: 0; right: 0; bottom: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.75); z-index: 9999; backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); isolation: isolate; overflow-y: auto; -webkit-overflow-scrolling: touch; padding: 0 16px; }
.modal-content { background: ${C.surface}; border-radius: 20px; padding: 24px 20px; width: 100%; max-width: 520px; min-height: 0; max-height: calc(100vh - 48px); max-height: calc(100dvh - 48px); overflow-y: auto; border: 1px solid ${C.border}; position: relative; z-index: 10000; box-shadow: 0 24px 48px rgba(0,0,0,0.5); margin: 24px auto 24px; }
.tab-bar { display: flex; background: ${C.surface}; border-bottom: 1px solid ${C.border}; position: sticky; top: 0; z-index: 90; padding: 0 4px; }
.tab { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 3px; padding: 10px 8px 8px; font-size: 10px; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; color: ${C.textMuted}; cursor: pointer; border: none; background: none; font-family: ${font}; position: relative; transition: color 0.15s; }
.tab.active { color: ${C.accent}; }
.tab.active::after { content: ''; position: absolute; bottom: 0; left: 20%; right: 20%; height: 2px; background: ${C.accent}; border-radius: 2px; }
.section-title { font-size: 11px; font-weight: 700; color: ${C.textMuted}; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 12px; }
.tag { display: inline-flex; align-items: center; gap: 5px; background: ${C.surface}; border: 1px solid ${C.border}; border-radius: 20px; padding: 6px 8px 6px 12px; font-size: 13px; font-weight: 500; color: ${C.text}; }
.tag-remove { display: flex; align-items: center; justify-content: center; background: none; border: none; color: ${C.textMuted}; cursor: pointer; padding: 2px; border-radius: 50%; transition: all 0.15s; }
.tag-remove:hover { color: ${C.red}; background: ${C.redDim}; }
.empty-state { text-align: center; padding: 40px 20px; color: ${C.textMuted}; font-size: 14px; }
.court-badge { background: ${C.blueDim}; color: ${C.blue}; }
.live-badge { background: ${C.accentDim}; color: ${C.accent}; }
.done-badge { background: ${C.orangeDim}; color: ${C.orange}; }
.admin-badge { background: ${C.goldDim}; color: ${C.gold}; }
.viewer-badge { background: ${C.blueDim}; color: ${C.blue}; }
.timer-bar { height: 6px; border-radius: 3px; transition: width 0.5s linear, background 0.3s; }
.timer-pulse { animation: pulse 1s infinite; }
.toggle-btn { padding: 5px 14px; font-size: 11px; font-weight: 700; border-radius: 8px; border: 1px solid ${C.border}; background: transparent; color: ${C.textMuted}; cursor: pointer; font-family: ${font}; transition: all 0.15s; letter-spacing: 0.04em; }
.toggle-btn.active { background: ${C.accentDim}; color: ${C.accent}; border-color: ${C.accent}; }
.paused-badge { background: ${C.orangeDim}; color: ${C.orange}; }
@keyframes scoreFlash { 0% { box-shadow: 0 0 0 0 ${C.accent}44; } 50% { box-shadow: 0 0 0 6px ${C.accent}00; } 100% { box-shadow: none; } }
.score-confirmed { animation: scoreFlash 0.4s ease-out; }
.tiebreaker-info { font-size: 11px; color: ${C.textMuted}; line-height: 1.5; margin-top: 10px; padding: 10px 12px; background: ${C.card}; border-radius: 10px; border: 1px solid ${C.border}; }
`; }

// ════════════════════════════════════════════════════════════════════
// MODAL
// ════════════════════════════════════════════════════════════════════

// Haptic feedback utility — vibrates + optional sound on score/round actions
function haptic(type = "light") {
  try {
    if (navigator.vibrate) {
      if (type === "light") navigator.vibrate(15);
      else if (type === "medium") navigator.vibrate(30);
      else if (type === "success") navigator.vibrate([20, 40, 20]);
    }
  } catch (e) { /* not supported */ }
}

// Tiebreaker rules text (shared across all leaderboards)
const TIEBREAKER_RULES = "Tiebreaker order: 1) Wins  2) Head-to-head record  3) Point differential  4) Total points scored";

function Modal({ children, onClose }) {
  const ref = React.useRef();
  React.useEffect(() => { if (ref.current) ref.current.scrollTop = 0; }, []);
  // Portaled to <body> so no animated/transformed ancestor can reposition or clip it
  return createPortal(
    <div className="modal-backdrop" ref={ref} onClick={e => e.target === e.currentTarget && onClose?.()}>
      <div className="modal-content slide-up">{children}</div>
    </div>,
    document.body
  );
}
function ModalHeader({ title, onClose, children }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
      <div style={{ fontSize: 18, fontWeight: 700, color: C.text, display: "flex", alignItems: "center", gap: 8 }}>{title}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {children}
        {onClose && <button className="btn btn-ghost btn-sm" onClick={onClose}><I.X size={18} /></button>}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// PLAYER NAME WITH RANK BADGE (used everywhere)
// ════════════════════════════════════════════════════════════════════
function PlayerName({ player, dpr, style = {} }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, ...style }}>
      {player.name}
      {player.ranking != null && (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 2, fontSize: 10, fontWeight: 700, color: C.gold, background: C.goldDim, padding: "1px 5px", borderRadius: 8, lineHeight: 1.2, flexShrink: 0 }}>
          <I.Star size={8} />{player.ranking}
        </span>
      )}
      {dpr && dpr.dpr != null && (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 2, fontSize: 10, fontWeight: 700, color: C.blue, background: C.blueDim, padding: "1px 5px", borderRadius: 8, lineHeight: 1.2, flexShrink: 0 }}>
          {dpr.dpr}{dpr.trend === "up" ? "\u25B2" : dpr.trend === "down" ? "\u25BC" : ""}
        </span>
      )}
    </span>
  );
}

// ════════════════════════════════════════════════════════════════════
// PLAYER SORT (shared across all player lists)
// ════════════════════════════════════════════════════════════════════
function sortPlayers(players, mode, dprMap = {}) {
  return [...players].sort((a, b) => {
    if (mode === "rank") {
      if (a.ranking != null && b.ranking != null) return b.ranking - a.ranking;
      if (a.ranking != null) return -1;
      if (b.ranking != null) return 1;
      return a.name.localeCompare(b.name);
    }
    if (mode === "dpr") {
      // Regular DPR first, then players with only a mixed rating, then the rest A-Z
      const da = dprMap[a.id]?.dpr ?? -1, db = dprMap[b.id]?.dpr ?? -1;
      if (da !== db) return db - da;
      const ma = dprMap[a.id]?.mdpr ?? -1, mb = dprMap[b.id]?.mdpr ?? -1;
      if (ma !== mb) return mb - ma;
      return a.name.localeCompare(b.name);
    }
    if (mode === "mdpr") {
      const ma = dprMap[a.id]?.mdpr ?? -1, mb = dprMap[b.id]?.mdpr ?? -1;
      if (ma !== mb) return mb - ma; // mdpr desc, nulls last
      const da = dprMap[a.id]?.dpr ?? -1, db = dprMap[b.id]?.dpr ?? -1;
      if (da !== db) return db - da; // tiebreak by dpr
      return a.name.localeCompare(b.name);
    }
    return a.name.localeCompare(b.name);
  });
}

function SortToggle({ value, onChange, compact = false, showMdpr = false }) {
  const s = { fontSize: compact ? 10 : 11, fontWeight: 700, padding: compact ? "2px 8px" : "3px 10px", borderRadius: 6, border: `1px solid ${C.border}`, background: "transparent", cursor: "pointer", fontFamily: font, transition: "all 0.15s" };
  const active = (v) => {
    if (v !== value) return { ...s, color: C.textMuted };
    const c = v === "rank" || v === "mdpr" ? C.gold : v === "dpr" ? C.blue : C.accent;
    const bg = v === "rank" || v === "mdpr" ? C.goldDim : v === "dpr" ? C.blueDim : C.accentDim;
    return { ...s, background: bg, color: c, border: `1px solid ${c}` };
  };
  return (
    <div style={{ display: "inline-flex", gap: 4 }}>
      <button style={active("alpha")} onClick={() => onChange("alpha")}>A-Z</button>
      <button style={active("dpr")} onClick={() => onChange("dpr")}>DPR</button>
      {showMdpr && <button style={active("mdpr")} onClick={() => onChange("mdpr")}>mDPR</button>}
      <button style={active("rank")} onClick={() => onChange("rank")}>Rank</button>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// CONFIRM MODAL (shared delete confirmation)
// ════════════════════════════════════════════════════════════════════
function ConfirmModal({ title, message, warning, onConfirm, onCancel, confirmLabel = "Delete", confirmColor = C.red }) {
  return (
    <Modal onClose={onCancel}>
      <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: C.text }}>{title}</div>
      <p style={{ color: C.textSec, fontSize: 14, marginBottom: 6 }}>{message}</p>
      {warning && <p style={{ color: C.red, fontSize: 13, marginBottom: 20 }}>{warning}</p>}
      <div style={{ display: "flex", gap: 10 }}>
        <button className="btn btn-outline" onClick={onCancel} style={{ flex: 1 }}>Cancel</button>
        <button className="btn" style={{ flex: 1, background: confirmColor, color: "#fff" }} onClick={onConfirm}>{confirmLabel}</button>
      </div>
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════════
// GAME TIMER
// ════════════════════════════════════════════════════════════════════
function GameTimer({ minutes, timerState, onTimerUpdate, isAdmin }) {
  const totalSec = minutes * 60;
  // timerState = { startedAt, pausedRemaining, status: "ready"|"running"|"paused"|"finished" }
  const ts = timerState || { status: "ready" };
  const playedWarning = useRef(false);
  const playedFinish = useRef(false);
  const [, forceUpdate] = useState(0);

  // Web Audio beep generator
  const playBeep = useCallback((freq, duration, count = 1, gap = 200) => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      for (let i = 0; i < count; i++) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        osc.type = "sine";
        const start = ctx.currentTime + (i * (duration + gap)) / 1000;
        const end = start + duration / 1000;
        gain.gain.setValueAtTime(0.4, start);
        gain.gain.exponentialRampToValueAtTime(0.01, end);
        osc.start(start);
        osc.stop(end);
      }
    } catch (e) { /* Audio not available */ }
  }, []);

  // Calculate remaining seconds from synced state
  let remaining = totalSec;
  if (ts.status === "running" && ts.startedAt) {
    const elapsed = Math.floor((Date.now() - ts.startedAt) / 1000);
    remaining = Math.max(0, (ts.pausedRemaining || totalSec) - elapsed);
  } else if (ts.status === "paused" && ts.pausedRemaining != null) {
    remaining = ts.pausedRemaining;
  } else if (ts.status === "finished") {
    remaining = 0;
  }

  const running = ts.status === "running";
  const finished = ts.status === "finished" || (running && remaining <= 0);

  // Tick every second when running
  useEffect(() => {
    if (ts.status === "running") {
      const iv = setInterval(() => forceUpdate(n => n + 1), 500);
      return () => clearInterval(iv);
    }
  }, [ts.status, ts.startedAt]);

  // Auto-finish when time runs out
  useEffect(() => {
    if (running && remaining <= 0 && ts.status !== "finished" && onTimerUpdate) {
      onTimerUpdate({ status: "finished", startedAt: null, pausedRemaining: 0 });
    }
  }, [remaining, running, ts.status]);

  // Sound triggers
  useEffect(() => {
    if (remaining === 120 && running && !playedWarning.current) {
      playedWarning.current = true;
      playBeep(880, 300, 3, 250);
    }
    if (remaining <= 0 && (finished || ts.status === "finished") && !playedFinish.current) {
      playedFinish.current = true;
      playBeep(440, 500, 1);
      setTimeout(() => playBeep(660, 500, 1), 700);
      setTimeout(() => playBeep(880, 800, 1), 1400);
    }
  }, [remaining, running, finished, playBeep, ts.status]);

  const handleStart = () => {
    if (!onTimerUpdate) return;
    playedWarning.current = false;
    playedFinish.current = false;
    onTimerUpdate({ status: "running", startedAt: Date.now(), pausedRemaining: totalSec });
  };
  const handlePause = () => {
    if (!onTimerUpdate) return;
    onTimerUpdate({ status: "paused", startedAt: null, pausedRemaining: remaining });
  };
  const handleResume = () => {
    if (!onTimerUpdate) return;
    onTimerUpdate({ status: "running", startedAt: Date.now(), pausedRemaining: remaining });
  };
  const handleReset = () => {
    if (!onTimerUpdate) return;
    playedWarning.current = false;
    playedFinish.current = false;
    onTimerUpdate({ status: "ready", startedAt: null, pausedRemaining: null });
  };

  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;
  const pct = totalSec > 0 ? (remaining / totalSec) * 100 : 0;
  const warning = remaining > 0 && remaining <= 120 && !finished;

  return (
    <div className="card" style={{ marginBottom: 12, padding: "12px 16px", borderColor: finished ? C.accent : warning ? C.orange : C.border }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <I.Clock size={16} style={{ color: finished ? C.accent : warning ? C.orange : C.textMuted }} />
          <span style={{ fontSize: 28, fontWeight: 900, fontFamily: fontMono, color: finished ? C.accent : warning ? C.orange : C.text, letterSpacing: "-0.02em" }}>
            {mins}:{secs.toString().padStart(2, "0")}
          </span>
        </div>
        {isAdmin && onTimerUpdate ? (
          <div style={{ display: "flex", gap: 4 }}>
            {ts.status === "ready" && (
              <button className="btn btn-primary btn-sm" onClick={handleStart}><I.Play size={12} /> Start</button>
            )}
            {ts.status === "running" && (
              <button className="btn btn-outline btn-sm" onClick={handlePause}><I.Pause size={12} /> Pause</button>
            )}
            {ts.status === "paused" && (
              <button className="btn btn-primary btn-sm" onClick={handleResume}><I.Play size={12} /> Resume</button>
            )}
            {ts.status !== "ready" && (
              <button className="btn btn-ghost btn-sm" onClick={handleReset}><I.Refresh size={12} /> Reset</button>
            )}
          </div>
        ) : (
          <div style={{ fontSize: 11, fontWeight: 600, color: running ? C.accent : finished ? C.accent : C.textMuted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            {running ? "Running" : finished ? "Time!" : ts.status === "paused" ? "Paused" : "Ready"}
          </div>
        )}
      </div>
      <div style={{ background: C.border, borderRadius: 3, overflow: "hidden" }}>
        <div className={`timer-bar ${warning ? "timer-pulse" : ""}`} style={{ width: `${pct}%`, background: finished ? C.accent : warning ? C.orange : C.blue }} />
      </div>
      {warning && <div style={{ fontSize: 12, fontWeight: 700, color: C.orange, marginTop: 6, textTransform: "uppercase", letterSpacing: "0.08em" }}>⚠ 2-Minute Warning</div>}
      {finished && <div style={{ fontSize: 13, fontWeight: 700, color: C.accent, marginTop: 6 }}>⏰ Time! Enter scores below.</div>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// LOGIN
// ════════════════════════════════════════════════════════════════════
function LoginScreen({ onLogin, onBootstrap, onProvideEmail, usersLoaded, bootstrapNeeded }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const bootstrapping = usersLoaded && bootstrapNeeded;
  const [newUser, setNewUser] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPass, setNewPass] = useState("");
  const [needsEmail, setNeedsEmail] = useState(false);
  const [emailValue, setEmailValue] = useState("");

  const handleLogin = async () => {
    const res = await onLogin(username, password);
    if (res === "needs-email") { setError(""); setNeedsEmail(true); }
    else if (typeof res === "string" && res.startsWith("locked:")) {
      const mins = res.split(":")[1];
      setError(`Too many sign-in attempts. This account is locked for about ${mins} more minute${mins === "1" ? "" : "s"} — wait, then try once. An admin can unlock it from the Users tab.`);
    }
    else if (!res) setError("Invalid username or password");
    else setError("");
  };

  const handleEmailContinue = async () => {
    if (!/^\S+@\S+\.\S+$/.test(emailValue.trim())) { setError("A valid email address is required"); return; }
    const ok = await onProvideEmail(emailValue.trim());
    if (!ok) setError("That email is invalid or already in use");
    else setError("");
  };
  const handleBootstrap = () => {
    if (newUser.trim().length < 2) { setError("Username must be at least 2 characters"); return; }
    if (!/^\S+@\S+\.\S+$/.test(newEmail.trim())) { setError("A valid email address is required"); return; }
    if (newPass.trim().length < 6) { setError("Password must be at least 6 characters"); return; }
    onBootstrap(newUser.trim(), newEmail.trim(), newPass.trim());
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: C.bg, fontFamily: font }}>
      <div className="fade-in" style={{ width: "100%", maxWidth: 380 }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <img src="/dip-logo.png" alt="Dinks in Paradise — Watersound" style={{ maxWidth: 230, width: "70%", height: "auto" }} />
          <div style={{ fontSize: 15, color: C.textSec, marginTop: 14 }}>Pickleball Tournament Manager</div>
        </div>
        {!usersLoaded ? (
          <div className="card" style={{ padding: 24, textAlign: "center" }}>
            <div style={{ fontSize: 14, color: C.textSec }}>Connecting...</div>
          </div>
        ) : bootstrapping ? (
          <div className="card" style={{ padding: 24 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 4 }}>Create Admin Account</div>
            <div style={{ fontSize: 13, color: C.textSec, marginBottom: 20 }}>First-time setup</div>
            <input className="input" placeholder="Username" value={newUser} onChange={e => setNewUser(e.target.value)} style={{ marginBottom: 10 }} onKeyDown={e => e.key === "Enter" && handleBootstrap()} />
            <input className="input" type="email" placeholder="Email" value={newEmail} onChange={e => setNewEmail(e.target.value)} style={{ marginBottom: 10 }} onKeyDown={e => e.key === "Enter" && handleBootstrap()} />
            <input className="input" type="password" placeholder="Password" value={newPass} onChange={e => setNewPass(e.target.value)} style={{ marginBottom: 16 }} onKeyDown={e => e.key === "Enter" && handleBootstrap()} />
            {error && <div style={{ color: C.red, fontSize: 13, marginBottom: 12 }}>{error}</div>}
            <button className="btn btn-primary btn-full" onClick={handleBootstrap} style={{ padding: 14, fontSize: 16 }}>Create Account</button>
          </div>
        ) : needsEmail ? (
          <div className="card" style={{ padding: 24 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 4 }}>Add Your Email</div>
            <div style={{ fontSize: 13, color: C.textSec, marginBottom: 20 }}>An email address is now required for your account. Enter yours to continue signing in.</div>
            <input className="input" type="email" placeholder="Email" value={emailValue} onChange={e => setEmailValue(e.target.value)} style={{ marginBottom: 16 }} onKeyDown={e => e.key === "Enter" && handleEmailContinue()} autoFocus />
            {error && <div style={{ color: C.red, fontSize: 13, marginBottom: 12 }}>{error}</div>}
            <button className="btn btn-primary btn-full" onClick={handleEmailContinue} style={{ padding: 14, fontSize: 16 }}>Continue</button>
          </div>
        ) : (
          <div className="card" style={{ padding: 24 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 16 }}>Sign In</div>
            <input className="input" type="email" placeholder="Email" autoComplete="username" value={username} onChange={e => setUsername(e.target.value)} style={{ marginBottom: 10 }} onKeyDown={e => e.key === "Enter" && handleLogin()} />
            <input className="input" type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} style={{ marginBottom: 16 }} onKeyDown={e => e.key === "Enter" && handleLogin()} />
            {error && <div style={{ color: C.red, fontSize: 13, marginBottom: 12 }}>{error}</div>}
            <button className="btn btn-primary btn-full" onClick={handleLogin} style={{ padding: 14, fontSize: 16 }}>Sign In</button>
          </div>
        )}
        <div style={{ textAlign: "center", marginTop: 26 }}>
          <a href={GIP_URL} style={{ color: C.accent, textDecoration: "none", fontSize: 13 }}>Part of the Games in Paradise family →</a>
          <div style={{ fontSize: 11, color: C.textMuted, marginTop: 22, lineHeight: 1.7 }}>
            Dinks in Paradise · Watersound, FL · dinksinparadise.com · v{APP_VERSION}<br />
            © {new Date().getFullYear()} Games in Paradise · All rights reserved
          </div>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// PLAYERS TAB (with ranking support)
// ════════════════════════════════════════════════════════════════════
function PlayersTab({ players, onAddPlayer, onUpdatePlayer, onDeletePlayer, tournaments, isAdmin, isSuperAdmin, dprMap = {} }) {
  const [name, setName] = useState("");
  const [newRanking, setNewRanking] = useState("");
  const [newGender, setNewGender] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [editRanking, setEditRanking] = useState("");
  const [editGender, setEditGender] = useState(null);
  const [editBirthdate, setEditBirthdate] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [confirmDel, setConfirmDel] = useState(null);
  const [confirmDelAll, setConfirmDelAll] = useState(false);
  const [sortMode, setSortMode] = useState("dpr");
  const [genderFilter, setGenderFilter] = useState("all");
  const [viewingPlayer, setViewingPlayer] = useState(null);

  const handleAdd = () => {
    if (name.trim().length < 2) return;
    const r = newRanking.trim() === "" ? null : parseFloat(newRanking);
    onAddPlayer({ id: uid(), name: name.trim(), ranking: (r !== null && !isNaN(r)) ? Math.round(r * 100) / 100 : null, gender: newGender, birthdate: null, phone: null, email: null });
    setName(""); setNewRanking(""); setNewGender(null);
  };
  const startEdit = (p) => {
    setEditing(p.id); setEditName(p.name); setEditRanking(p.ranking != null ? String(p.ranking) : "");
    setEditGender(p.gender === "M" || p.gender === "F" ? p.gender : null);
    setEditBirthdate(p.birthdate || ""); setEditPhone(p.phone || ""); setEditEmail(p.email || "");
  };
  const saveEdit = () => {
    if (editName.trim().length < 2) return;
    const r = editRanking.trim() === "" ? null : parseFloat(editRanking);
    onUpdatePlayer({
      ...players.find(p => p.id === editing),
      name: editName.trim(),
      ranking: (r !== null && !isNaN(r)) ? Math.round(r * 100) / 100 : null,
      gender: editGender,
      birthdate: editBirthdate.trim() === "" ? null : editBirthdate,
      phone: editPhone.trim() === "" ? null : editPhone.trim(),
      email: editEmail.trim() === "" ? null : editEmail.trim(),
    });
    setEditing(null);
  };

  const stats = useMemo(() => {
    const s = {};
    tournaments.filter(t => t.status === "finished").forEach(t => {
      try {
      (t.rounds || []).forEach(r => {
        (r.courts || []).forEach(c => {
          const proc = (team, score, opp) => {
            team.forEach(p => {
              if (!s[p.id]) s[p.id] = { pts: 0, wins: 0, losses: 0, gp: 0, tourneys: {} };
              if (score !== null && opp !== null) {
                s[p.id].pts += score || 0; s[p.id].gp += 1;
                if (score > opp) s[p.id].wins += 1;
                else if (score < opp) s[p.id].losses += 1;
              }
              s[p.id].tourneys[t.id] = true;
            });
          };
          proc(c.teamA || [], c.scoreA, c.scoreB);
          proc(c.teamB || [], c.scoreB, c.scoreA);
        });
      });
      } catch(e) {}
    });
    return s;
  }, [tournaments]);

  const sorted = sortPlayers(players, sortMode, dprMap)
    .filter(p => genderFilter === "all" || p.gender === genderFilter);
  const mdprPlayersExist = players.some(p => (dprMap[p.id]?.mixedTourneyCount || 0) > 0);

  return (
    <div style={{ padding: 16 }} className="fade-in">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, gap: 8, flexWrap: "wrap" }}>
        <div className="section-title" style={{ margin: 0 }}>Player Roster ({players.length})</div>
        <div style={{ display: "flex", gap: 4 }}>
          {[["all", "All"], ["M", "Men"], ["F", "Women"]].map(([key, label]) => (
            <button key={key} className={`btn btn-sm ${genderFilter === key ? "btn-primary" : "btn-outline"}`} onClick={() => setGenderFilter(key)} style={{ fontSize: 11, padding: "4px 10px" }}>{label}</button>
          ))}
        </div>
        <SortToggle value={sortMode} onChange={setSortMode} showMdpr={mdprPlayersExist} />
      </div>
      {isAdmin && (
        <div style={{ display: "flex", gap: 6, marginBottom: 16, alignItems: "center" }}>
          <input className="input" placeholder="Player name..." value={name} onChange={e => setName(e.target.value)} onKeyDown={e => e.key === "Enter" && handleAdd()} style={{ flex: 1, minWidth: 0 }} />
          <input className="input" type="number" step="0.01" placeholder="Rank" value={newRanking} onChange={e => setNewRanking(e.target.value)} onKeyDown={e => e.key === "Enter" && handleAdd()} style={{ width: 64, textAlign: "center" }} />
          <div style={{ display: "flex", gap: 2, flexShrink: 0 }}>
            <button className={`toggle-btn ${newGender === "M" ? "active" : ""}`} onClick={() => setNewGender(newGender === "M" ? null : "M")} style={{ padding: "8px 9px" }}>M</button>
            <button className="toggle-btn" onClick={() => setNewGender(newGender === "F" ? null : "F")} style={newGender === "F" ? { padding: "8px 9px", background: C.orangeDim, color: C.orange, borderColor: C.orange } : { padding: "8px 9px" }}>F</button>
          </div>
          <button className="btn btn-primary" onClick={handleAdd} style={{ flexShrink: 0 }}><I.Plus size={18} /></button>
        </div>
      )}
      {players.length === 0 && <div className="empty-state">No players added yet</div>}
      {sorted.map((p, i) => {
        const st = stats[p.id];
        return (
          <div key={p.id} className="card fade-in" style={{ padding: "12px 14px", marginBottom: 8, animationDelay: `${i*30}ms`, cursor: editing === p.id ? "default" : "pointer" }} onClick={() => editing !== p.id && setViewingPlayer(p)}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              {editing === p.id ? (
                <div style={{ flex: 1 }} onClick={e => e.stopPropagation()}>
                  <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
                    <input className="input" value={editName} onChange={e => setEditName(e.target.value)} onKeyDown={e => e.key === "Enter" && saveEdit()} style={{ padding: "7px 10px", fontSize: 14, flex: 1, minWidth: 100 }} autoFocus placeholder="Name" />
                    <input className="input" value={editRanking} onChange={e => setEditRanking(e.target.value)} onKeyDown={e => e.key === "Enter" && saveEdit()} style={{ padding: "7px 10px", fontSize: 14, width: 80 }} placeholder="Rank" type="number" step="0.01" />
                  </div>
                  <div style={{ display: "flex", gap: 6, marginBottom: 8, alignItems: "center" }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", width: 58, flexShrink: 0 }}>Gender</span>
                    <button className={`toggle-btn ${editGender === "M" ? "active" : ""}`} onClick={() => setEditGender(editGender === "M" ? null : "M")} style={{ flex: 1 }}>Male</button>
                    <button className="toggle-btn" onClick={() => setEditGender(editGender === "F" ? null : "F")} style={editGender === "F" ? { flex: 1, background: C.orangeDim, color: C.orange, borderColor: C.orange } : { flex: 1 }}>Female</button>
                  </div>
                  <div style={{ display: "flex", gap: 6, marginBottom: 6, alignItems: "center" }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", width: 58, flexShrink: 0 }}>Born</span>
                    <input className="input" type="date" value={editBirthdate} onChange={e => setEditBirthdate(e.target.value)} style={{ padding: "7px 10px", fontSize: 14, flex: 1 }} />
                  </div>
                  <input className="input" type="tel" placeholder="Phone (optional)" value={editPhone} onChange={e => setEditPhone(e.target.value)} onKeyDown={e => e.key === "Enter" && saveEdit()} style={{ padding: "7px 10px", fontSize: 14, marginBottom: 6 }} />
                  <input className="input" type="email" placeholder="Email (optional)" value={editEmail} onChange={e => setEditEmail(e.target.value)} onKeyDown={e => e.key === "Enter" && saveEdit()} style={{ padding: "7px 10px", fontSize: 14, marginBottom: 8 }} />
                  <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setEditing(null)}><I.X /> Cancel</button>
                    <button className="btn btn-primary btn-sm" onClick={saveEdit}><I.Check /> Save</button>
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      {(p.gender === "M" || p.gender === "F") && <GenderChip gender={p.gender} size={18} />}
                      <span style={{ fontWeight: 600, fontSize: 15, color: C.text }}>{p.name}</span>
                      {p.ranking != null && (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 11, fontWeight: 700, color: C.gold, background: C.goldDim, padding: "2px 7px", borderRadius: 10 }}>
                          <I.Star size={10} /> {p.ranking}
                        </span>
                      )}
                      {dprMap[p.id] && dprMap[p.id].dpr != null && (() => {
                        const d = dprMap[p.id];
                        const prov = d.tourneyCount < 3; // 2 tourneys = shown but not yet settled
                        return (
                          <span style={prov
                            ? { display: "inline-flex", alignItems: "center", gap: 3, fontSize: 11, fontWeight: 600, color: C.blue, opacity: 0.6, background: "transparent", border: `1px dashed ${C.blue}`, padding: "1px 7px", borderRadius: 10 }
                            : { display: "inline-flex", alignItems: "center", gap: 3, fontSize: 11, fontWeight: 700, color: C.blue, background: C.blueDim, padding: "2px 7px", borderRadius: 10 }}
                            title={`DPR: ${d.dpr} (${d.tourneyCount} tournament${d.tourneyCount === 1 ? "" : "s"}${prov ? ", provisional" : ""})`}>
                            DPR {d.dpr}{d.trend === "up" ? " \u25B2" : d.trend === "down" ? " \u25BC" : ""}
                          </span>
                        );
                      })()}
                      {dprMap[p.id] && dprMap[p.id].dpr != null && <ConfidencePct pct={dprMap[p.id].confidence} />}
                      {dprMap[p.id] && dprMap[p.id].dpr == null && dprMap[p.id].tourneyCount > 0 && (
                        <span style={{ display: "inline-flex", alignItems: "center", fontSize: 10, color: C.textMuted, padding: "2px 6px" }}>
                          DPR: {dprMap[p.id].tourneyCount}/2 tourneys
                        </span>
                      )}
                      {dprMap[p.id] && dprMap[p.id].mixedTourneyCount > 0 && (() => {
                        const m = dprMap[p.id];
                        const provisional = m.mixedTourneyCount < 3;
                        return (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 10.5, fontWeight: 600, color: C.gold, opacity: 0.6, background: "transparent", border: `1px ${provisional ? "dashed" : "solid"} ${C.gold}`, padding: "1px 7px", borderRadius: 10 }} title={`mDPR: ${m.mdpr} (${m.mixedTourneyCount} mixed tournament${m.mixedTourneyCount === 1 ? "" : "s"}${provisional ? ", provisional" : ""})`}>
                            mDPR {m.mdpr}{m.mixedTrend === "up" ? " ▲" : m.mixedTrend === "down" ? " ▼" : ""}{provisional ? ` ·${m.mixedTourneyCount}/3·` : ""}
                          </span>
                        );
                      })()}
                      {dprMap[p.id] && dprMap[p.id].mdpr != null && <ConfidencePct pct={dprMap[p.id].mixedConfidence} />}
                    </div>
                    {st ? (
                      <div style={{ fontSize: 12, color: C.textSec, marginTop: 2, fontFamily: fontMono }}>
                        {Object.keys(st.tourneys).length}T · {st.gp}G · {st.wins}W-{st.losses}L · {st.pts}pts
                      </div>
                    ) : (
                      <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>No tournament history</div>
                    )}
                  </div>
                  {isAdmin && (
                    <div style={{ display: "flex", gap: 4 }} onClick={e => e.stopPropagation()}>
                      <button className="btn btn-ghost btn-sm" onClick={() => startEdit(p)}><I.Edit /></button>
                      {isSuperAdmin && <button className="btn btn-ghost btn-sm" style={{ color: C.red }} onClick={() => setConfirmDel(p)}><I.Trash /></button>}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}
      {confirmDel && (
        <ConfirmModal
          title="Delete Player?"
          message={<>Delete <strong style={{ color: C.text }}>{confirmDel.name}</strong> from the roster?</>}
          warning="This cannot be undone. Past tournament records are preserved."
          onConfirm={() => { onDeletePlayer(confirmDel.id); setConfirmDel(null); }}
          onCancel={() => setConfirmDel(null)}
        />
      )}
      {viewingPlayer && <PlayerProfile player={viewingPlayer} tournaments={tournaments} dprMap={dprMap} onClose={() => setViewingPlayer(null)} />}
      {isSuperAdmin && players.length > 0 && (
        <div style={{ marginTop: 24, padding: "14px 0", borderTop: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Danger Zone</div>
          <button className="btn btn-outline btn-sm" style={{ color: C.red, borderColor: C.red, fontSize: 12 }} onClick={() => setConfirmDelAll(true)}>
            <I.Trash size={12} /> Delete All Players
          </button>
        </div>
      )}
      {confirmDelAll && (
        <ConfirmModal
          title="Delete ALL Players?"
          message={<>Permanently delete <strong style={{ color: C.text }}>all {players.length} players</strong> from the roster?</>}
          warning="This cannot be undone. Tournament records are preserved."
          onConfirm={() => { players.forEach(p => onDeletePlayer(p.id)); setConfirmDelAll(false); }}
          onCancel={() => setConfirmDelAll(false)}
        />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// COURTS TAB (with delete confirmation)
// ════════════════════════════════════════════════════════════════════
function CourtsTab({ courts, onAddCourt, onDeleteCourt, isAdmin, isSuperAdmin }) {
  const [num, setNum] = useState("");
  const [confirmDel, setConfirmDel] = useState(null);
  const handleAdd = () => {
    const n = parseInt(num, 10);
    if (isNaN(n) || n < 1 || courts.find(c => c.number === n)) return;
    onAddCourt({ id: uid(), number: n });
    setNum("");
  };
  return (
    <div style={{ padding: 16 }} className="fade-in">
      <div className="section-title">Available Courts ({courts.length})</div>
      {isAdmin && (
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <input className="input" type="number" inputMode="numeric" placeholder="Court number..." value={num} onChange={e => setNum(e.target.value)} onKeyDown={e => e.key === "Enter" && handleAdd()} />
          <button className="btn btn-primary" onClick={handleAdd} style={{ flexShrink: 0 }}><I.Plus size={18} /></button>
        </div>
      )}
      {courts.length === 0 && <div className="empty-state">No courts added yet</div>}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {courts.map((c, i) => (
          <div key={c.id} className="card fade-in" style={{ padding: "14px 20px", display: "flex", alignItems: "center", gap: 12, animationDelay: `${i*40}ms` }}>
            <div style={{ fontSize: 28, fontWeight: 900, fontFamily: fontMono, color: C.blue }}>{c.number}</div>
            {isSuperAdmin && <button className="tag-remove" onClick={() => setConfirmDel(c)}><I.X size={14} /></button>}
          </div>
        ))}
      </div>
      {confirmDel && (
        <ConfirmModal
          title="Remove Court?"
          message={<>Remove Court <strong style={{ color: C.text }}>{confirmDel.number}</strong>?</>}
          onConfirm={() => { onDeleteCourt(confirmDel.id); setConfirmDel(null); }}
          onCancel={() => setConfirmDel(null)}
          confirmLabel="Remove"
        />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// BUILD TOURNAMENT (all 3 formats with team-based flow)
// ════════════════════════════════════════════════════════════════════
function generateRoundRobinSchedule(teams, courtsList) {
  // Circle method: generates rounds where every team plays every other, max concurrency on courts
  const t = [...teams];
  if (t.length % 2 !== 0) t.push(null); // bye placeholder
  const n = t.length;
  const rounds = [];
  for (let r = 0; r < n - 1; r++) {
    const matches = [];
    for (let i = 0; i < n / 2; i++) {
      const home = t[i], away = t[n - 1 - i];
      if (home && away) {
        matches.push({
          teamA: home.players, teamB: away.players,
          teamAName: home.name, teamBName: away.name,
          teamAId: home.id, teamBId: away.id,
          courtNumber: courtsList.length > 0 ? courtsList[matches.length % courtsList.length].number : null,
          scoreA: null, scoreB: null,
        });
      }
    }
    if (matches.length > 0) rounds.push(matches);
    // Rotate: fix first element, rotate rest
    t.splice(1, 0, t.pop());
  }
  return rounds;
}

function buildBracketRounds(teams) {
  const n = teams.length;
  if (n < 2) return [];
  const size = Math.pow(2, Math.ceil(Math.log2(n)));

  // Standard bracket seeding: #1 meets #2 only in final
  function seedOrder(sz) {
    if (sz === 1) return [0];
    const half = seedOrder(sz / 2);
    return half.flatMap(i => [i, sz - 1 - i]);
  }
  const positions = seedOrder(size);

  // Map seeds into bracket slots - byes (null) fill where seed index >= team count
  const slots = new Array(size).fill(null);
  for (let i = 0; i < size; i++) {
    const seedIdx = positions[i];
    slots[i] = seedIdx < n ? teams[seedIdx] : null;
  }

  const firstRound = [];
  for (let i = 0; i < size; i += 2) {
    const a = slots[i], b = slots[i + 1];
    firstRound.push({
      teamA: a ? a.players : null, teamB: b ? b.players : null,
      teamAName: a?.name || null, teamBName: b?.name || null,
      teamAId: a?.id || null, teamBId: b?.id || null,
      scoreA: null, scoreB: null, winner: null,
    });
  }
  firstRound.forEach(m => {
    if (!m.teamA && m.teamB) { m.winner = "B"; m.scoreA = 0; m.scoreB = 0; }
    else if (m.teamA && !m.teamB) { m.winner = "A"; m.scoreA = 0; m.scoreB = 0; }
  });
  const bracketRounds = [firstRound];
  let prevCount = firstRound.length;
  while (prevCount > 1) {
    const next = [];
    for (let i = 0; i < prevCount / 2; i++) {
      next.push({ teamA: null, teamB: null, teamAName: null, teamBName: null, teamAId: null, teamBId: null, scoreA: null, scoreB: null, winner: null });
    }
    bracketRounds.push(next);
    prevCount = next.length;
  }
  // Propagate byes forward - only auto-advance when both feeder matches are decided
  for (let ri = 0; ri < bracketRounds.length - 1; ri++) {
    const curr = bracketRounds[ri];
    const next = bracketRounds[ri + 1];
    for (let mi = 0; mi < curr.length; mi += 2) {
      const m1 = curr[mi], m2 = curr[mi + 1];
      const nextMatch = next[Math.floor(mi / 2)];
      if (m1 && m1.winner) {
        const w = m1.winner === "A" ? { t: m1.teamA, n: m1.teamAName, id: m1.teamAId } : { t: m1.teamB, n: m1.teamBName, id: m1.teamBId };
        nextMatch.teamA = w.t; nextMatch.teamAName = w.n; nextMatch.teamAId = w.id;
      }
      if (m2 && m2.winner) {
        const w = m2.winner === "A" ? { t: m2.teamA, n: m2.teamAName, id: m2.teamAId } : { t: m2.teamB, n: m2.teamBName, id: m2.teamBId };
        nextMatch.teamB = w.t; nextMatch.teamBName = w.n; nextMatch.teamBId = w.id;
      }
      // Only auto-advance if BOTH feeder matches are resolved (both are byes)
      const bothResolved = (m1 && m1.winner) && (m2 && m2.winner);
      if (bothResolved && nextMatch.teamA && !nextMatch.teamB && !nextMatch.winner) {
        nextMatch.winner = "A"; nextMatch.scoreA = 0; nextMatch.scoreB = 0;
      } else if (bothResolved && !nextMatch.teamA && nextMatch.teamB && !nextMatch.winner) {
        nextMatch.winner = "B"; nextMatch.scoreA = 0; nextMatch.scoreB = 0;
      }
    }
  }
  return bracketRounds;
}

function BuildTournament({ players, courts, onStart, onCancel, onCreateRegistration, onUpdatePlayer, preSelectedPlayers, preRegTournament, dprMap = {} }) {
  const fromReg = !!preSelectedPlayers;
  const [step, setStep] = useState(fromReg ? 2 : 1); // skip step 1 when coming from registration
  const [tName, setTName] = useState(preRegTournament?.name || "");
  const [gameMinutes, setGameMinutes] = useState(preRegTournament?.gameMinutes || 10);
  const [timerOn, setTimerOn] = useState(preRegTournament?.gameMinutes ? true : true);
  const [format, setFormat] = useState(preRegTournament?.format || "kingofhill");
  const [division, setDivision] = useState(preRegTournament?.division || (fromReg ? "mens" : null));
  const [preRegOn, setPreRegOn] = useState(false);
  const [maxPlayers, setMaxPlayers] = useState(24);
  const [mixedTeamError, setMixedTeamError] = useState(null); // teamId with a mixed-pairing error

  // King of the Hill state
  const [selCourts, setSelCourts] = useState([]);
  const [courtPlayers, setCourtPlayers] = useState({});
  const effectivePlayers = fromReg ? preSelectedPlayers : players;
  const [availPlayers, setAvailPlayers] = useState([...effectivePlayers]);
  const [autoAssign, setAutoAssign] = useState(false);

  // Team-based state (RR & Bracket)
  const [numTeams, setNumTeams] = useState(fromReg ? Math.floor(preSelectedPlayers.length / 2) : 4);
  const [numTeamsInput, setNumTeamsInput] = useState(fromReg ? String(Math.floor(preSelectedPlayers.length / 2)) : "4");
  const [teams, setTeams] = useState([]); // [{id, name, players: [p1, p2]}]
  const [teamAvail, setTeamAvail] = useState([...effectivePlayers]);
  const [teamBuildMode, setTeamBuildMode] = useState("manual"); // "manual" | "random"
  const [randomPool, setRandomPool] = useState(fromReg ? preSelectedPlayers.map(p => p.id) : []); // pre-select all checked-in for random pairing

  // Sync available player pools if effectivePlayers changes (e.g. from reg flow)
  const effectiveIds = effectivePlayers.map(p => p.id).join(",");
  useEffect(() => {
    const assigned = Object.values(courtPlayers).flat().map(p => p.id);
    const teamAssigned = teams.flatMap(t => t.players).map(p => p.id);
    setAvailPlayers(effectivePlayers.filter(p => !assigned.includes(p.id)));
    setTeamAvail(effectivePlayers.filter(p => !teamAssigned.includes(p.id)));
  }, [effectiveIds]);
  const [bracketOrder, setBracketOrder] = useState([]); // ordered team ids for bracket seeding
  const [playerSort, setPlayerSort] = useState("alpha");
  const [playerSearch, setPlayerSearch] = useState("");

  // PBR state
  const [pbrPlayers, setPbrPlayers] = useState([]);
  const [pbrSearch, setPbrSearch] = useState("");

  // ── Division / gender helpers ──
  // Always resolve gender from the live roster so inline [M]/[F] updates show immediately.
  const liveGender = (p) => {
    const g = ((players.find(x => x.id === p.id)) || p).gender;
    return g === "M" || g === "F" ? g : null;
  };
  const divisionEligible = (p) => {
    const g = liveGender(p);
    if (division === "mens") return g === "M";
    if (division === "womens") return g === "F";
    if (division === "mixed") return g !== null;
    return true;
  };
  const eligiblePlayers = effectivePlayers.filter(divisionEligible);
  const setPlayerGender = (p, g) => {
    const full = players.find(x => x.id === p.id) || p;
    onUpdatePlayer && onUpdatePlayer({ ...full, gender: g });
  };
  // Refresh gender from the live roster before saving into the tournament object
  const withLiveGender = (p) => ({ ...p, gender: liveGender(p) });
  const mixedChip = (p, size = 16) => division === "mixed" ? <GenderChip gender={liveGender(p)} size={size} /> : null;
  // Dimmed rows for players whose gender isn't set yet, with inline [M]/[F] setters
  const renderUnsetGender = (pool) => {
    if (!division) return null;
    const unset = pool.filter(p => liveGender(p) === null);
    if (unset.length === 0) return null;
    return (
      <div style={{ marginBottom: 12 }}>
        {unset.map(p => (
          <div key={p.id} className="card" style={{ padding: "8px 12px", marginBottom: 6, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ opacity: 0.38 }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: C.text }}>{p.name}</div>
              <div style={{ fontSize: 11, color: C.textMuted }}>Gender not set</div>
            </div>
            <div style={{ display: "flex", gap: 4 }}>
              <button className="btn btn-outline btn-sm" style={{ color: C.accent, borderColor: C.accent, padding: "4px 10px" }} onClick={() => setPlayerGender(p, "M")}>M</button>
              <button className="btn btn-outline btn-sm" style={{ color: C.orange, borderColor: C.orange, padding: "4px 10px" }} onClick={() => setPlayerGender(p, "F")}>F</button>
            </div>
          </div>
        ))}
      </div>
    );
  };
  // Live "Selected: X men · Y women" counter card (mixed division only)
  const renderMixedCounter = (selectedList) => {
    if (division !== "mixed") return null;
    const men = selectedList.filter(p => liveGender(p) === "M").length;
    const women = selectedList.filter(p => liveGender(p) === "F").length;
    const balanced = men === women && men > 0;
    return (
      <div className="card" style={{ padding: "10px 14px", marginBottom: 12, borderColor: balanced ? C.gold : C.border }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Selected: {men} men · {women} women</span>
        {balanced
          ? <span style={{ fontSize: 12, fontWeight: 700, color: C.gold, marginLeft: 6 }}>— balanced ✓</span>
          : <span style={{ fontSize: 12, color: C.textMuted, marginLeft: 6 }}>needs equal men and women</span>}
      </div>
    );
  };

  const toggleCourt = (c) => setSelCourts(prev => prev.find(sc => sc.id === c.id) ? prev.filter(sc => sc.id !== c.id) : [...prev, c].sort((a, b) => a.number - b.number));
  const assignPlayer = (courtId, player) => {
    const cp = courtPlayers[courtId] || [];
    if (cp.length >= 4) return;
    if (division === "mixed") {
      const g = liveGender(player);
      if (g && cp.filter(x => liveGender(x) === g).length >= 2) return; // keep courts at 2M + 2F
    }
    setCourtPlayers({ ...courtPlayers, [courtId]: [...cp, player] });
    setAvailPlayers(prev => prev.filter(p => p.id !== player.id));
  };
  const removeFromCourt = (courtId, playerId) => {
    const player = (courtPlayers[courtId] || []).find(p => p.id === playerId);
    if (!player) return;
    setCourtPlayers({ ...courtPlayers, [courtId]: (courtPlayers[courtId] || []).filter(p => p.id !== playerId) });
    setAvailPlayers(prev => [...prev, player].sort((a, b) => a.name.localeCompare(b.name)));
  };
  const goToKothStep3 = () => { setCourtPlayers({}); setAvailPlayers([...players]); setAutoAssign(false); setStep(3); };
  const autoAssignCourts = (cmp) => {
    const pool = eligiblePlayers;
    const cp = {};
    let leftover;
    if (division === "mixed") {
      // Take the next 2 men + next 2 women (per the sort) for each court so teams stay M+F
      const men = pool.filter(p => liveGender(p) === "M").sort(cmp);
      const women = pool.filter(p => liveGender(p) === "F").sort(cmp);
      selCourts.forEach((c, ci) => { cp[c.id] = [...men.slice(ci * 2, ci * 2 + 2), ...women.slice(ci * 2, ci * 2 + 2)]; });
      const usedIds = new Set(Object.values(cp).flat().map(p => p.id));
      leftover = pool.filter(p => !usedIds.has(p.id)).sort(cmp);
    } else {
      const ranked = [...pool].sort(cmp);
      const needed = selCourts.length * 4;
      selCourts.forEach((c, ci) => { cp[c.id] = ranked.slice(ci * 4, ci * 4 + 4); });
      leftover = ranked.slice(needed);
    }
    setCourtPlayers(cp); setAvailPlayers(leftover); setAutoAssign(true);
  };
  const handleAutoAssignKoth = () => {
    autoAssignCourts((a, b) => { if (a.ranking != null && b.ranking != null) return b.ranking - a.ranking; if (a.ranking != null) return -1; if (b.ranking != null) return 1; return a.name.localeCompare(b.name); });
  };
  // Effective MIXED rating for seeding mixed-division events:
  // mdpr → dpr → roster ranking (clamped to rating range) → base 3.0
  const effectiveMixedRating = (p) => {
    const e = dprMap[p.id];
    if (e?.mdpr != null) return e.mdpr;
    if (e?.dpr != null) return e.dpr;
    if (p.ranking != null && !isNaN(Number(p.ranking))) return Math.max(0.5, Math.min(5.0, Number(p.ranking)));
    return 3.0;
  };
  const handleAutoAssignKothDPR = () => {
    const val = division === "mixed"
      ? (p) => effectiveMixedRating(p)
      : (p) => dprMap[p.id]?.dpr ?? 0;
    autoAssignCourts((a, b) => {
      const da = val(a), db = val(b);
      if (da !== db) return db - da;
      return a.name.localeCompare(b.name);
    });
  };
  const allFull = selCourts.every(c => (courtPlayers[c.id] || []).length === 4);
  const courtsGenderOk = division !== "mixed" || selCourts.every(c => {
    const cp = courtPlayers[c.id] || [];
    return cp.filter(p => liveGender(p) === "M").length === 2 && cp.filter(p => liveGender(p) === "F").length === 2;
  });
  const canStartKoth = tName.trim() && selCourts.length > 0 && allFull && courtsGenderOk;
  const rankedPlayersExist = players.some(p => p.ranking != null);

  // Team building helpers
  const initTeams = () => {
    const n = parseInt(numTeams, 10) || 4;
    const t = [];
    for (let i = 0; i < n; i++) t.push({ id: uid(), name: `Team ${i + 1}`, players: [] });
    setTeams(t); setTeamAvail([...effectivePlayers]); setBracketOrder([]); setMixedTeamError(null);
  };
  const addPlayerToTeam = (teamId, player) => {
    if (division === "mixed") {
      const team = teams.find(t => t.id === teamId);
      const g0 = team && team.players.length === 1 ? liveGender(team.players[0]) : null;
      const g1 = liveGender(player);
      if (g0 && g1 && g0 === g1) { setMixedTeamError(teamId); return; } // block same-gender mixed teams
    }
    setMixedTeamError(null);
    setTeams(prev => prev.map(t => t.id === teamId && t.players.length < 2 ? { ...t, players: [...t.players, player] } : t));
    setTeamAvail(prev => prev.filter(p => p.id !== player.id));
  };
  const removePlayerFromTeam = (teamId, playerId) => {
    let removed = null;
    setTeams(prev => {
      const updated = prev.map(t => {
        if (t.id !== teamId) return t;
        removed = t.players.find(p => p.id === playerId);
        return { ...t, players: t.players.filter(p => p.id !== playerId) };
      });
      return updated;
    });
    // Find the player directly from teams state since setter is async
    const team = teams.find(t => t.id === teamId);
    const player = team?.players.find(p => p.id === playerId);
    if (player) setTeamAvail(prev => [...prev, player].sort((a, b) => a.name.localeCompare(b.name)));
  };
  const renameTeam = (teamId, name) => setTeams(prev => prev.map(t => t.id === teamId ? { ...t, name } : t));
  const randomPairTeams = () => {
    const poolPlayers = players.filter(p => randomPool.includes(p.id));
    // Fisher-Yates shuffle
    const shuffleArr = (arr) => {
      const a = [...arr];
      for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; }
      return a;
    };
    const shuffled = shuffleArr(poolPlayers);
    const newTeams = [];
    if (division === "mixed") {
      // Mixed: every team is one man + one woman
      const men = shuffled.filter(p => liveGender(p) === "M");
      const women = shuffled.filter(p => liveGender(p) === "F");
      const n = Math.min(men.length, women.length);
      for (let i = 0; i < n; i++) newTeams.push({ id: uid(), name: `Team ${i + 1}`, players: [men[i], women[i]] });
    } else {
      const nTeams = Math.floor(shuffled.length / 2);
      for (let i = 0; i < nTeams; i++) {
        newTeams.push({ id: uid(), name: `Team ${i + 1}`, players: [shuffled[i * 2], shuffled[i * 2 + 1]] });
      }
    }
    const usedIds = new Set(newTeams.flatMap(t => t.players.map(p => p.id)));
    const leftover = shuffled.filter(p => !usedIds.has(p.id));
    setTeams(newTeams);
    setTeamAvail(leftover);
    setNumTeams(newTeams.length);
    setNumTeamsInput(String(newTeams.length));
  };
  const allTeamsFull = teams.length > 0 && teams.every(t => t.players.length === 2);
  const teamRank = (t) => {
    const r1 = t.players[0]?.ranking ?? 0;
    const r2 = t.players[1]?.ranking ?? 0;
    return r1 + r2;
  };
  const autoSeedByRank = () => {
    const sorted = [...teams].sort((a, b) => teamRank(b) - teamRank(a));
    setBracketOrder(sorted.map(t => t.id));
  };
  const teamDPR = (t) => {
    if (division === "mixed") {
      // Mixed events seed by effective MIXED rating (mdpr → dpr → ranking → base)
      return t.players.reduce((sum, p) => sum + (p ? effectiveMixedRating(p) : 0), 0);
    }
    const d1 = dprMap[t.players[0]?.id]?.dpr ?? 0;
    const d2 = dprMap[t.players[1]?.id]?.dpr ?? 0;
    return d1 + d2;
  };
  const autoSeedByDPR = () => {
    const sorted = [...teams].sort((a, b) => teamDPR(b) - teamDPR(a));
    setBracketOrder(sorted.map(t => t.id));
  };
  const dprPlayersExist = players.some(p => dprMap[p.id]?.dpr != null);
  const mdprPlayersExist = players.some(p => (dprMap[p.id]?.mixedTourneyCount || 0) > 0);
  // In a mixed build, the DPR assign/seed actions run off the effective mixed rating
  const seedByMixed = division === "mixed";
  const dprSeedAvailable = dprPlayersExist || (seedByMixed && mdprPlayersExist);

  const formatLabels = { kingofhill: "King of the Hill", roundrobin: "Round Robin by Team", rrplayer: "PBR", bracket: "Bracket" };
  const formatDescs = {
    kingofhill: "Winners move up, losers move down. Partners split each round.",
    roundrobin: "Every team plays every other team. Best for fair skill assessment.",
    rrplayer: "Players re-pair every round. Win % decides the champion. Great for large groups.",
    bracket: "Single elimination. Lose and you're out. Fast, high-stakes.",
  };

  const preRegData = preRegOn ? { enabled: true, maxPlayers, registrants: [], waitlist: [] } : null;

  const handleStart = () => {
    const tDivision = division || "mens";
    if (format === "kingofhill") {
      const tournCourts = selCourts.map(c => {
        const p = courtPlayers[c.id];
        let teamA = null, teamB = null;
        if (tDivision === "mixed") {
          // Arrange each court so both teams are one man + one woman
          const men = p.filter(x => liveGender(x) === "M");
          const women = p.filter(x => liveGender(x) === "F");
          if (men.length === 2 && women.length === 2) { teamA = [men[0], women[0]]; teamB = [men[1], women[1]]; }
        }
        if (!teamA) { teamA = [p[0], p[1]]; teamB = [p[2], p[3]]; }
        return { courtId: c.id, courtNumber: c.number, teamA, teamB, scoreA: null, scoreB: null };
      });
      onStart({ id: uid(), name: tName.trim(), format, division: tDivision, gameMinutes: timerOn ? gameMinutes : null, preRegistration: preRegData, courts: tournCourts, players: selCourts.flatMap(c => courtPlayers[c.id]).map(withLiveGender), teams: null, createdAt: new Date().toISOString() });
    } else if (format === "roundrobin") {
      const schedule = generateRoundRobinSchedule(teams, selCourts);
      const allPlayers = teams.flatMap(t => t.players).map(withLiveGender);
      onStart({ id: uid(), name: tName.trim(), format, division: tDivision, gameMinutes: timerOn ? gameMinutes : null, preRegistration: preRegData, schedule, teams, players: allPlayers, courts: selCourts.map(c => ({ courtId: c.id, courtNumber: c.number })), currentMatchRound: 0, completedRounds: [], createdAt: new Date().toISOString() });
    } else if (format === "bracket") {
      const ordered = bracketOrder.length === teams.length ? bracketOrder.map(id => teams.find(t => t.id === id)) : teams;
      const bracketRounds = buildBracketRounds(ordered);
      const allPlayers = teams.flatMap(t => t.players).map(withLiveGender);
      onStart({ id: uid(), name: tName.trim(), format, division: tDivision, gameMinutes: timerOn ? gameMinutes : null, preRegistration: preRegData, bracketRounds, teams, players: allPlayers, createdAt: new Date().toISOString() });
    } else if (format === "rrplayer") {
      onStart({ id: uid(), name: tName.trim(), format, division: tDivision, gameMinutes: timerOn ? gameMinutes : null, courts: selCourts.map(c => ({ courtId: c.id, courtNumber: c.number })), players: pbrPlayers.map(withLiveGender), rounds: [], currentRound: 1, status: "playing", createdAt: new Date().toISOString() });
    }
  };

  // Step count depends on format
  const totalSteps = format === "kingofhill" ? 3 : format === "rrplayer" ? 3 : format === "roundrobin" ? 4 : 4;

  return (
    <div style={{ padding: 16 }} className="fade-in">
      <div style={{ display: "flex", gap: 4, marginBottom: 20 }}>
        {Array.from({ length: totalSteps }, (_, i) => i + 1).map(s => <div key={s} style={{ flex: 1, height: 3, borderRadius: 2, background: s <= step ? C.accent : C.border, transition: "background 0.3s" }} />)}
      </div>

      {/* STEP 1: Name, format, timer */}
      {step === 1 && (
        <div className="fade-in">
          <div style={{ fontSize: 20, fontWeight: 800, color: C.text, marginBottom: 4 }}>Name Your Tournament</div>
          <div style={{ fontSize: 14, color: C.textSec, marginBottom: 20 }}>Give it a name to find it later</div>
          <input className="input" placeholder="e.g. Friday Night Showdown" value={tName} onChange={e => setTName(e.target.value)} autoFocus style={{ marginBottom: 16, fontSize: 17, padding: 14 }} />
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.textSec, marginBottom: 8 }}>Tournament Format</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {["kingofhill", "roundrobin", "rrplayer", "bracket"].map(f => (
                <button key={f} className={`toggle-btn ${format === f ? "active" : ""}`} onClick={() => setFormat(f)} style={{ padding: "8px 14px", fontSize: 12 }}>{formatLabels[f]}</button>
              ))}
            </div>
            <div style={{ fontSize: 12, color: C.textMuted, marginTop: 6 }}>{formatDescs[format]}</div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.textSec, marginBottom: 8 }}>Division</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
              {Object.entries(DIVISIONS).map(([key, d]) => {
                const sel = division === key;
                const dc = divColor(key);
                return (
                  <div key={key} onClick={() => setDivision(key)} className="card" style={{ padding: "12px 6px", cursor: "pointer", textAlign: "center", border: sel ? `2px solid ${dc.color}` : `1px solid ${C.border}`, background: sel ? dc.bg : C.card }}>
                    <div style={{ fontSize: 20, color: dc.color, marginBottom: 4, lineHeight: 1 }}>{d.sym}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: sel ? dc.color : C.textSec }}>{d.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.textSec, display: "flex", alignItems: "center", gap: 6 }}><I.Clock size={14} /> Game Timer</div>
              <button className={`toggle-btn ${timerOn ? "active" : ""}`} onClick={() => setTimerOn(!timerOn)} style={{ fontSize: 11 }}>{timerOn ? "ON" : "OFF"}</button>
            </div>
            {timerOn && (
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {[5, 8, 10, 12, 15].map(m => (
                  <button key={m} className={`toggle-btn ${gameMinutes === m ? "active" : ""}`} onClick={() => setGameMinutes(m)}>{m}</button>
                ))}
                <input className="input" type="number" inputMode="numeric" value={gameMinutes} onChange={e => { const v = parseInt(e.target.value, 10); if (!isNaN(v) && v > 0 && v <= 60) setGameMinutes(v); }} style={{ width: 70, padding: "7px 10px", fontSize: 14, textAlign: "center" }} />
                <span style={{ fontSize: 12, color: C.textMuted }}>min</span>
              </div>
            )}
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.textSec, display: "flex", alignItems: "center", gap: 6 }}><I.Users size={14} /> Pre-Registration</div>
              <button className={`toggle-btn ${preRegOn ? "active" : ""}`} onClick={() => setPreRegOn(!preRegOn)} style={{ fontSize: 11 }}>{preRegOn ? "ON" : "OFF"}</button>
            </div>
            {preRegOn && (
              <div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
                  <span style={{ fontSize: 12, color: C.textMuted }}>Max players:</span>
                  {[12, 16, 20, 24, 32].map(n => (
                    <button key={n} className={`toggle-btn ${maxPlayers === n ? "active" : ""}`} onClick={() => setMaxPlayers(n)}>{n}</button>
                  ))}
                  <input className="input" type="number" inputMode="numeric" value={maxPlayers} onChange={e => { const v = parseInt(e.target.value, 10); if (!isNaN(v) && v > 0 && v <= 100) setMaxPlayers(v); }} style={{ width: 60, padding: "7px 10px", fontSize: 14, textAlign: "center" }} />
                </div>
                <div style={{ fontSize: 11, color: C.textMuted }}>Creates a registration link. Players pick their name from the roster or add a new one. Waitlist activates when full.</div>
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-outline" onClick={onCancel}>Cancel</button>
            {preRegOn ? (
              <button className="btn btn-primary" disabled={!tName.trim() || !division} onClick={() => {
                onCreateRegistration && onCreateRegistration({
                  id: uid(), name: tName.trim(), format, division,
                  gameMinutes: timerOn ? gameMinutes : null,
                  preRegistration: { enabled: true, maxPlayers, registrants: [], waitlist: [] },
                  status: "registration", createdAt: new Date().toISOString(),
                });
              }} style={{ flex: 1 }}><I.Users size={16} /> Open Registration</button>
            ) : (
              <button className="btn btn-primary" disabled={!tName.trim() || !division} onClick={() => {
                if (format === "kingofhill") setStep(2);
                else { initTeams(); setStep(2); }
              }} style={{ flex: 1 }}>Next <I.ChevRight /></button>
            )}
          </div>
        </div>
      )}

      {/* STEP 2 KotH: Select Courts */}
      {step === 2 && format === "kingofhill" && (
        <div className="fade-in">
          <div style={{ fontSize: 20, fontWeight: 800, color: C.text, marginBottom: 4 }}>Select Courts</div>
          <div style={{ fontSize: 14, color: C.textSec, marginBottom: 20 }}>{selCourts.length} selected · {selCourts.length * 4} players needed</div>
          {courts.length === 0 && <div className="empty-state">No courts available. Add courts first.</div>}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
            {courts.map(c => {
              const sel = selCourts.find(sc => sc.id === c.id);
              return (
                <div key={c.id} onClick={() => toggleCourt(c)} className="card" style={{ padding: "16px 24px", cursor: "pointer", textAlign: "center", borderColor: sel ? C.accent : C.border, background: sel ? C.accentDim : C.card }}>
                  <div style={{ fontSize: 28, fontWeight: 900, fontFamily: fontMono, color: sel ? C.accent : C.textSec }}>{c.number}</div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: sel ? C.accent : C.textMuted, marginTop: 2, textTransform: "uppercase", letterSpacing: "0.08em" }}>Court</div>
                </div>
              );
            })}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-outline" onClick={() => setStep(1)}><I.ChevLeft /> Back</button>
            <button className="btn btn-primary" disabled={selCourts.length === 0} onClick={goToKothStep3} style={{ flex: 1 }}>Next — Assign Players <I.ChevRight /></button>
          </div>
        </div>
      )}

      {/* STEP 2 RR/Bracket: How many teams + courts */}
      {step === 2 && (format === "roundrobin" || format === "bracket") && (
        <div className="fade-in">
          <div style={{ fontSize: 20, fontWeight: 800, color: C.text, marginBottom: 4 }}>Set Up Teams</div>
          <div style={{ fontSize: 14, color: C.textSec, marginBottom: 16 }}>2 players per team</div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.textSec, marginBottom: 8 }}>How many teams?</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {[3, 4, 5, 6, 8].map(n => (
                <button key={n} className={`toggle-btn ${numTeams === n ? "active" : ""}`} onClick={() => { setNumTeams(n); setNumTeamsInput(String(n)); }}>{n}</button>
              ))}
              <input className="input" type="number" inputMode="numeric" value={numTeamsInput} onChange={e => { setNumTeamsInput(e.target.value); const v = parseInt(e.target.value, 10); if (!isNaN(v) && v >= 2 && v <= 32) setNumTeams(v); }} onBlur={() => { if (!numTeamsInput || isNaN(parseInt(numTeamsInput, 10))) setNumTeamsInput(String(numTeams)); }} style={{ width: 64, padding: "7px 10px", fontSize: 14, textAlign: "center" }} />
            </div>
            <div style={{ fontSize: 12, color: C.textMuted, marginTop: 6 }}>{numTeams} teams = {numTeams * 2} players needed{numTeams % 2 !== 0 ? " · odd teams: one bye per round" : ""}</div>
          </div>
          {format === "roundrobin" && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.textSec, marginBottom: 8 }}>Courts to use</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {courts.map(c => {
                  const sel = selCourts.find(sc => sc.id === c.id);
                  return <button key={c.id} className={`toggle-btn ${sel ? "active" : ""}`} onClick={() => toggleCourt(c)}>Court {c.number}</button>;
                })}
              </div>
              {courts.length === 0 && <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>No courts added. Matches will play sequentially.</div>}
            </div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-outline" onClick={() => setStep(1)}><I.ChevLeft /> Back</button>
            <button className="btn btn-primary" disabled={numTeams < 2} onClick={() => { initTeams(); setStep(3); }} style={{ flex: 1 }}>Next — Build Teams <I.ChevRight /></button>
          </div>
        </div>
      )}

      {/* STEP 3 KotH: Assign Players to Courts */}
      {step === 3 && format === "kingofhill" && (() => {
        const effectiveIds = new Set(effectivePlayers.map(p => p.id));
        const sortedAvailKoth = sortPlayers(availPlayers.filter(p => effectiveIds.has(p.id) && divisionEligible(p)), playerSort, dprMap).filter(p => !playerSearch || p.name.toLowerCase().includes(playerSearch.toLowerCase()));
        const assignedPlayers = selCourts.flatMap(c => courtPlayers[c.id] || []);
        return (
        <div className="fade-in">
          <div style={{ fontSize: 20, fontWeight: 800, color: C.text, marginBottom: 4 }}>Assign Players to Courts</div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <div style={{ fontSize: 14, color: C.textSec }}>4 per court{division === "mixed" ? " (2 men + 2 women)" : ""} · {sortedAvailKoth.length} unassigned{fromReg && <span style={{ color: C.blue }}> (checked-in)</span>}</div>
            <SortToggle value={playerSort} onChange={setPlayerSort} compact showMdpr={mdprPlayersExist} />
          </div>
          {renderMixedCounter(assignedPlayers)}
          {availPlayers.length > 8 && <PlayerSearch value={playerSearch} onChange={setPlayerSearch} placeholder="Search players..." />}
          {renderUnsetGender(availPlayers.filter(p => effectiveIds.has(p.id)))}
          {(rankedPlayersExist || dprSeedAvailable) && !autoAssign && (
            <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
              {rankedPlayersExist && <button className="btn btn-outline btn-sm" onClick={handleAutoAssignKoth} style={{ borderColor: C.gold, color: C.gold }}><I.Star size={12} /> Assign by Rank</button>}
              {dprSeedAvailable && <button className="btn btn-outline btn-sm" onClick={handleAutoAssignKothDPR} style={seedByMixed ? { borderColor: C.gold, color: C.gold } : { borderColor: C.blue, color: C.blue }}>{seedByMixed ? "Assign by mDPR" : "Assign by DPR"}</button>}
            </div>
          )}
          {selCourts.map(c => {
            const cp = courtPlayers[c.id] || [];
            const isFull = cp.length === 4;
            return (
              <div key={c.id} className="card" style={{ marginBottom: 12, borderColor: isFull ? C.accent : C.border }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <span className="badge court-badge">Court {c.number}</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: isFull ? C.accent : C.textMuted }}>{cp.length}/4</span>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: cp.length < 4 ? 10 : 0 }}>
                  {cp.map(p => <span key={p.id} className="tag">{mixedChip(p)}{p.name}{p.ranking != null && <span style={{ fontSize: 10, color: C.gold, marginLeft: 2 }}>★{p.ranking}</span>}{dprMap[p.id]?.dpr != null && <span style={{ fontSize: 10, color: C.blue, marginLeft: 2 }}>{dprMap[p.id].dpr}</span>}<button className="tag-remove" onClick={() => removeFromCourt(c.id, p.id)}><I.X size={12} /></button></span>)}
                  {cp.length === 0 && <div style={{ fontSize: 13, color: C.textMuted, padding: "4px 0" }}>No players assigned</div>}
                </div>
                {!isFull && sortedAvailKoth.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4, borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
                    {sortedAvailKoth.map(p => <button key={p.id} className="btn btn-outline btn-sm" onClick={() => assignPlayer(c.id, p)} style={{ fontSize: 12 }}><I.Plus size={12} /> {mixedChip(p)}{p.name}{p.ranking != null && <span style={{ fontSize: 9, color: C.gold, marginLeft: 2 }}>★{p.ranking}</span>}{dprMap[p.id]?.dpr != null && <span style={{ fontSize: 9, color: C.blue, marginLeft: 2 }}>{dprMap[p.id].dpr}</span>}</button>)}
                  </div>
                )}
              </div>
            );
          })}
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button className="btn btn-outline" onClick={() => setStep(2)}><I.ChevLeft /> Back</button>
            <button className="btn btn-primary" disabled={!canStartKoth} onClick={handleStart} style={{ flex: 1, padding: 14, fontSize: 16 }}><I.Play size={18} /> Start Tournament</button>
          </div>
        </div>
        );
      })()}

      {/* STEP 3 RR/Bracket: Build Teams — manual or random */}
      {step === 3 && (format === "roundrobin" || format === "bracket") && (() => {
        const effectiveIds = new Set(effectivePlayers.map(p => p.id));
        const sortedAvail = sortPlayers(teamAvail.filter(p => effectiveIds.has(p.id) && divisionEligible(p)), playerSort, dprMap).filter(p => !playerSearch || p.name.toLowerCase().includes(playerSearch.toLowerCase()));
        const sortedAllPlayers = sortPlayers(eligiblePlayers, playerSort, dprMap).filter(p => !playerSearch || p.name.toLowerCase().includes(playerSearch.toLowerCase()));
        const poolPlayers = effectivePlayers.filter(p => randomPool.includes(p.id));
        const poolCount = randomPool.length;
        const poolMen = poolPlayers.filter(p => liveGender(p) === "M").length;
        const poolWomen = poolPlayers.filter(p => liveGender(p) === "F").length;
        const canPair = poolCount >= 4 && (division === "mixed" ? poolMen === poolWomen : poolCount % 2 === 0);
        const allPaired = teams.length > 0 && teams.every(t => t.players.length === 2);
        return (
        <div className="fade-in">
          <div style={{ fontSize: 20, fontWeight: 800, color: C.text, marginBottom: 4 }}>Build Your Teams</div>
          {/* Mode toggle */}
          <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
            <button className={`toggle-btn ${teamBuildMode === "manual" ? "active" : ""}`} onClick={() => setTeamBuildMode("manual")} style={{ fontSize: 12, flex: 1 }}>Manual Build</button>
            <button className={`toggle-btn ${teamBuildMode === "random" ? "active" : ""}`} onClick={() => setTeamBuildMode("random")} style={{ fontSize: 12, flex: 1 }}>Random Pairs</button>
          </div>
          {players.length > 8 && <PlayerSearch value={playerSearch} onChange={setPlayerSearch} placeholder="Search players..." />}

          {/* ── RANDOM MODE ── */}
          {teamBuildMode === "random" && !allPaired && (
            <>
              <div style={{ fontSize: 14, color: C.textSec, marginBottom: 8 }}>Select players who are here today, then tap Pair.</div>
              {renderMixedCounter(poolPlayers)}
              <div style={{ display: "flex", gap: 6, marginBottom: 10, alignItems: "center" }}>
                <button className="btn btn-outline btn-sm" onClick={() => setRandomPool(randomPool.length === eligiblePlayers.length ? [] : eligiblePlayers.map(p => p.id))} style={{ fontSize: 12 }}>
                  {randomPool.length === eligiblePlayers.length ? "Deselect All" : "Select All"}
                </button>
                <span style={{ fontSize: 13, fontWeight: 600, color: canPair ? C.accent : poolCount > 0 ? C.orange : C.textMuted }}>
                  {poolCount} selected{poolCount >= 2 ? ` = ${Math.floor(poolCount / 2)} teams` : ""}{division !== "mixed" && poolCount % 2 !== 0 && poolCount > 0 ? " (need even #)" : ""}
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", marginBottom: 6 }}>
                <SortToggle value={playerSort} onChange={setPlayerSort} compact showMdpr={mdprPlayersExist} />
              </div>
              {renderUnsetGender(effectivePlayers)}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
                {sortedAllPlayers.map(p => {
                  const sel = randomPool.includes(p.id);
                  return (
                    <button key={p.id} className={`toggle-btn ${sel ? "active" : ""}`} onClick={() => setRandomPool(prev => sel ? prev.filter(id => id !== p.id) : [...prev, p.id])}
                      style={{ fontSize: 12, padding: "6px 10px", display: "flex", alignItems: "center", gap: 4 }}>
                      {sel ? <I.Check size={12} /> : <I.Plus size={12} />}
                      {mixedChip(p)}
                      {p.name}
                      {p.ranking != null && <span style={{ fontSize: 9, color: C.gold }}>★{p.ranking}</span>}
                    </button>
                  );
                })}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn btn-outline" onClick={() => setStep(2)}><I.ChevLeft /> Back</button>
                <button className="btn btn-primary" disabled={!canPair} onClick={randomPairTeams} style={{ flex: 1 }}>
                  <I.Refresh size={14} /> Pair {Math.floor(poolCount / 2)} Teams Randomly
                </button>
              </div>
            </>
          )}

          {/* ── RANDOM MODE: show results after pairing ── */}
          {teamBuildMode === "random" && allPaired && (
            <>
              <div style={{ fontSize: 14, color: C.accent, fontWeight: 600, marginBottom: 10 }}>{teams.length} teams paired</div>
              {teams.map(t => (
                <div key={t.id} className="card" style={{ marginBottom: 8, borderColor: C.accent, padding: "10px 14px" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <input className="input" value={t.name} onChange={e => renameTeam(t.id, e.target.value)} style={{ padding: "4px 8px", fontSize: 13, fontWeight: 700, border: "none", background: "transparent", color: C.text, width: 120 }} />
                    <span style={{ fontSize: 13, color: C.text }}>{t.players.map(p => p.name).join(" & ")}</span>
                  </div>
                </div>
              ))}
              <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                <button className="btn btn-outline" onClick={() => { setTeams([]); setTeamAvail([...players]); }} style={{ fontSize: 12 }}><I.Refresh size={12} /> Re-Shuffle</button>
                <button className="btn btn-outline btn-sm" onClick={() => setTeamBuildMode("manual")} style={{ fontSize: 12 }}>Edit Manually</button>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <button className="btn btn-outline" onClick={() => setStep(2)}><I.ChevLeft /> Back</button>
                {format === "roundrobin" ? (
                  <button className="btn btn-primary" onClick={handleStart} style={{ flex: 1, padding: 14, fontSize: 16 }}><I.Play size={18} /> Start Round Robin</button>
                ) : (
                  <button className="btn btn-primary" onClick={() => { autoSeedByRank(); setStep(4); }} style={{ flex: 1 }}>Next — Seed Bracket <I.ChevRight /></button>
                )}
              </div>
            </>
          )}

          {/* ── MANUAL MODE ── */}
          {teamBuildMode === "manual" && (
            <>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <div style={{ fontSize: 14, color: C.textSec }}>{teams.filter(t => t.players.length === 2).length}/{teams.length} teams ready · {sortedAvail.length} available</div>
                <SortToggle value={playerSort} onChange={setPlayerSort} compact showMdpr={mdprPlayersExist} />
              </div>
              {renderUnsetGender(teamAvail.filter(p => effectiveIds.has(p.id)))}
              {teams.some(t => t.players.length > 0) && (
                <div style={{ marginBottom: 10 }}>
                  <button className="btn btn-outline btn-sm" onClick={() => { setTeams(prev => prev.map(t => ({ ...t, players: [] }))); setTeamAvail([...players]); }} style={{ fontSize: 12, color: C.textMuted }}>Clear All</button>
                </div>
              )}
              {teams.map((t, ti) => (
                <div key={t.id} className="card" style={{ marginBottom: 10, borderColor: t.players.length === 2 ? C.accent : C.border }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                    <input className="input" value={t.name} onChange={e => renameTeam(t.id, e.target.value)} style={{ padding: "5px 10px", fontSize: 13, fontWeight: 700, border: "none", background: "transparent", color: C.text, width: 140 }} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: t.players.length === 2 ? C.accent : C.textMuted }}>
                      {t.players.length}/2
                      {t.players.length === 2 && (t.players[0].ranking != null || t.players[1].ranking != null) && (
                        <span style={{ color: C.gold, marginLeft: 6 }}>★{teamRank(t).toFixed(1)}</span>
                      )}
                    </span>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: t.players.length < 2 ? 8 : 0 }}>
                    {t.players.map(p => (
                      <span key={p.id} className="tag">
                        {mixedChip(p)}{p.name}{p.ranking != null && <span style={{ fontSize: 10, color: C.gold, marginLeft: 2 }}>★{p.ranking}</span>}{dprMap[p.id]?.dpr != null && <span style={{ fontSize: 10, color: C.blue, marginLeft: 2 }}>DPR {dprMap[p.id].dpr}</span>}
                        <button className="tag-remove" onClick={() => removePlayerFromTeam(t.id, p.id)}><I.X size={12} /></button>
                      </span>
                    ))}
                    {t.players.length === 0 && <div style={{ fontSize: 13, color: C.textMuted }}>Tap players below to add</div>}
                  </div>
                  {mixedTeamError === t.id && (
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.red, marginBottom: 6 }}>Mixed teams need one man and one woman</div>
                  )}
                  {t.players.length < 2 && sortedAvail.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>
                      {sortedAvail.map(p => (
                        <button key={p.id} className="btn btn-outline btn-sm" onClick={() => addPlayerToTeam(t.id, p)} style={{ fontSize: 12 }}>
                          <I.Plus size={12} /> {mixedChip(p)}{p.name}{p.ranking != null && <span style={{ fontSize: 9, color: C.gold, marginLeft: 2 }}>★{p.ranking}</span>}{dprMap[p.id]?.dpr != null && <span style={{ fontSize: 9, color: C.blue, marginLeft: 2 }}>{dprMap[p.id].dpr}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                <button className="btn btn-outline" onClick={() => setStep(2)}><I.ChevLeft /> Back</button>
                {format === "roundrobin" ? (
                  <button className="btn btn-primary" disabled={!allTeamsFull} onClick={handleStart} style={{ flex: 1, padding: 14, fontSize: 16 }}><I.Play size={18} /> Start Round Robin</button>
                ) : (
                  <button className="btn btn-primary" disabled={!allTeamsFull} onClick={() => { autoSeedByRank(); setStep(4); }} style={{ flex: 1 }}>Next — Seed Bracket <I.ChevRight /></button>
                )}
              </div>
            </>
          )}
        </div>
        );
      })()}

      {/* STEP 4 Bracket: Seed/Order teams */}
      {step === 4 && format === "bracket" && (
        <div className="fade-in">
          <div style={{ fontSize: 20, fontWeight: 800, color: C.text, marginBottom: 4 }}>Seed the Bracket</div>
          <div style={{ fontSize: 14, color: C.textSec, marginBottom: 12 }}>Reorder manually or auto-seed. #1 seed plays lowest, byes go to top seeds.</div>
          <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
            {rankedPlayersExist && (
              <button className="btn btn-outline btn-sm" onClick={autoSeedByRank} style={{ borderColor: C.gold, color: C.gold }}><I.Star size={12} /> Seed by Rank</button>
            )}
            {dprSeedAvailable && (
              <button className="btn btn-outline btn-sm" onClick={autoSeedByDPR} style={seedByMixed ? { borderColor: C.gold, color: C.gold } : { borderColor: C.blue, color: C.blue }}>{seedByMixed ? "Seed by mDPR" : "DPR Seed by DPR"}</button>
            )}
          </div>
          <div style={{ marginBottom: 16 }}>
            {(bracketOrder.length > 0 ? bracketOrder.map(id => teams.find(t => t.id === id)).filter(Boolean) : teams).map((t, i) => (
              <div key={t.id} className="card" style={{ padding: "10px 14px", marginBottom: 6, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 16, fontWeight: 900, fontFamily: fontMono, color: C.accent, width: 24 }}>#{i + 1}</span>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: C.text }}>{t.name}</div>
                    <div style={{ fontSize: 11, color: C.textSec }}>{t.players.map(p => p.name).join(" & ")}{teamRank(t) > 0 && <span style={{ color: C.gold, marginLeft: 4 }}>★{teamRank(t).toFixed(1)}</span>}{teamDPR(t) > 0 && <span style={{ color: seedByMixed ? C.gold : C.blue, marginLeft: 4 }}>{seedByMixed ? "mDPR" : "DPR"} {teamDPR(t).toFixed(2)}</span>}</div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 2 }}>
                  {i > 0 && <button className="btn btn-ghost btn-sm" onClick={() => {
                    const order = bracketOrder.length > 0 ? [...bracketOrder] : teams.map(t => t.id);
                    [order[i - 1], order[i]] = [order[i], order[i - 1]];
                    setBracketOrder(order);
                  }} style={{ padding: 4 }}>▲</button>}
                  {i < teams.length - 1 && <button className="btn btn-ghost btn-sm" onClick={() => {
                    const order = bracketOrder.length > 0 ? [...bracketOrder] : teams.map(t => t.id);
                    [order[i], order[i + 1]] = [order[i + 1], order[i]];
                    setBracketOrder(order);
                  }} style={{ padding: 4 }}>▼</button>}
                </div>
              </div>
            ))}
          </div>
          {teams.length % 2 !== 0 && (
            <div style={{ fontSize: 12, color: C.orange, marginBottom: 12 }}>Odd number of teams — top seed(s) will get a bye in Round 1.</div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-outline" onClick={() => setStep(3)}><I.ChevLeft /> Back</button>
            <button className="btn btn-primary" onClick={handleStart} style={{ flex: 1, padding: 14, fontSize: 16 }}><I.Play size={18} /> Start Bracket</button>
          </div>
        </div>
      )}

      {/* PBR STEP 2: Select Courts */}
      {format === "rrplayer" && step === 2 && (
        <div className="fade-in">
          <div style={{ fontSize: 20, fontWeight: 800, color: C.text, marginBottom: 4 }}>Select Courts</div>
          <div style={{ fontSize: 14, color: C.textSec, marginBottom: 20 }}>Choose which courts are in play</div>
          {courts.length === 0 && <div className="empty-state">No courts available. Add courts first.</div>}
          {courts.map(c => (
            <div key={c.id} className="card" style={{ padding: "12px 14px", marginBottom: 8, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", border: selCourts.find(sc => sc.id === c.id) ? `2px solid ${C.accent}` : `1px solid ${C.border}` }} onClick={() => toggleCourt(c)}>
              <span style={{ fontWeight: 700, color: C.text }}>Court {c.number}</span>
              {selCourts.find(sc => sc.id === c.id) ? <I.Check size={18} color={C.accent} /> : <span style={{ width: 18 }} />}
            </div>
          ))}
          <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 16 }}>
            {selCourts.length > 0 ? `${selCourts.length} court${selCourts.length > 1 ? "s" : ""} · ${selCourts.length * 4} player slots per round` : "No courts selected"}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-outline" onClick={() => setStep(1)}><I.ChevLeft /> Back</button>
            <button className="btn btn-primary" style={{ flex: 1, padding: 14, fontSize: 16 }} disabled={selCourts.length === 0} onClick={() => setStep(3)}>Next <I.ChevRight /></button>
          </div>
        </div>
      )}

      {/* PBR STEP 3: Add Players */}
      {format === "rrplayer" && step === 3 && (() => {
        const searchFiltered = effectivePlayers.filter(p => !pbrSearch || p.name.toLowerCase().includes(pbrSearch.toLowerCase()));
        const pbrMen = pbrPlayers.filter(p => liveGender(p) === "M").length;
        const pbrWomen = pbrPlayers.filter(p => liveGender(p) === "F").length;
        const canStartPbr = pbrPlayers.length >= 4 && (division !== "mixed" || (pbrMen === pbrWomen && pbrMen + pbrWomen >= 4));
        return (
        <div className="fade-in">
          <div style={{ fontSize: 20, fontWeight: 800, color: C.text, marginBottom: 4 }}>Add Players</div>
          <div style={{ fontSize: 14, color: C.textSec, marginBottom: 4 }}>Select everyone who will play — minimum 4</div>
          <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 16 }}>{pbrPlayers.length} player{pbrPlayers.length !== 1 ? "s" : ""} added · {selCourts.length * 4} slots per round{pbrPlayers.length > selCourts.length * 4 ? ` · ${pbrPlayers.length - selCourts.length * 4} will sit out each round` : ""}</div>
          {renderMixedCounter(pbrPlayers)}
          <input className="input" placeholder="Search players..." value={pbrSearch} onChange={e => setPbrSearch(e.target.value)} style={{ marginBottom: 12, fontSize: 15, padding: 10 }} />
          {renderUnsetGender(searchFiltered)}
          <div style={{ marginBottom: 16 }}>
            {searchFiltered.filter(divisionEligible).map(p => {
              const selected = pbrPlayers.find(x => x.id === p.id);
              return (
                <div key={p.id} className="card" style={{ padding: "10px 14px", marginBottom: 6, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", border: selected ? `2px solid ${C.accent}` : `1px solid ${C.border}` }}
                  onClick={() => setPbrPlayers(prev => selected ? prev.filter(x => x.id !== p.id) : [...prev, p])}>
                  <span style={{ fontWeight: 600, color: C.text, display: "inline-flex", alignItems: "center", gap: 8 }}>{mixedChip(p, 22)}{p.name}</span>
                  {selected ? <I.Check size={18} color={C.accent} /> : <span style={{ width: 18 }} />}
                </div>
              );
            })}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-outline" onClick={() => setStep(2)}><I.ChevLeft /> Back</button>
            <button className="btn btn-primary" style={{ flex: 1, padding: 14, fontSize: 16 }} disabled={!canStartPbr} onClick={handleStart}><I.Play size={18} /> Start PBR</button>
          </div>
        </div>
        );
      })()}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// LEADERBOARD + ROUND HISTORY
// ════════════════════════════════════════════════════════════════════
function LeaderboardTable({ rounds }) {
  const [mode, setMode] = useState("pts");
  const board = calcLeaderboard(rounds, mode);
  if (board.length === 0) return <div className="empty-state">No scores yet</div>;
  const rc = i => i === 0 ? "rank-1" : i === 1 ? "rank-2" : i === 2 ? "rank-3" : "rank-n";
  const scoreKey = mode === "dtr" ? "dtr" : "pts";
  return (
    <div>
      <div style={{ display: "flex", gap: 4, marginBottom: 14 }}>
        <button className={`toggle-btn ${mode === "pts" ? "active" : ""}`} onClick={() => setMode("pts")}>PTS</button>
        <button className={`toggle-btn ${mode === "dtr" ? "active" : ""}`} onClick={() => setMode("dtr")}>DTR</button>
      </div>
      <div style={{ display: "flex", padding: "0 12px 10px", fontSize: 10, fontWeight: 700, color: C.textMuted, letterSpacing: "0.08em" }}>
        <span style={{ width: 36 }}>#</span><span style={{ flex: 1 }}>PLAYER</span><span style={{ width: 52, textAlign: "center" }}>{mode === "dtr" ? "DTR" : "PTS"}</span><span style={{ width: 36, textAlign: "center" }}>W</span><span style={{ width: 36, textAlign: "center" }}>L</span><span style={{ width: 36, textAlign: "center" }}>GP</span>
      </div>
      {board.map((p, i) => (
        <div key={p.id} className="fade-in" style={{ display: "flex", alignItems: "center", padding: "10px 12px", borderRadius: 10, marginBottom: 4, background: i < 3 ? (i === 0 ? C.goldDim : i === 1 ? "rgba(168,184,204,0.08)" : "rgba(205,127,50,0.08)") : "transparent", animationDelay: `${i*30}ms` }}>
          <div className={`badge ${rc(i)}`} style={{ width: 26, height: 26, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", marginRight: 10, padding: 0, fontSize: i < 3 ? 15 : 12 }}>{i < 3 ? ["🥇", "🥈", "🥉"][i] : i + 1}</div>
          <span style={{ flex: 1, fontWeight: 600, fontSize: 14, display: "flex", alignItems: "center", gap: 4 }}>
            {p.name}
            {p.ranking != null && <span style={{ display: "inline-flex", alignItems: "center", gap: 2, fontSize: 9, fontWeight: 700, color: C.gold, background: C.goldDim, padding: "1px 5px", borderRadius: 8 }}><I.Star size={7} />{p.ranking}</span>}
          </span>
          <span style={{ width: 52, textAlign: "center", fontWeight: 800, fontSize: 17, fontFamily: fontMono, color: C.accent }}>{p[scoreKey]}</span>
          <span style={{ width: 36, textAlign: "center", fontSize: 13, fontFamily: fontMono, color: C.textSec }}>{p.wins}</span>
          <span style={{ width: 36, textAlign: "center", fontSize: 13, fontFamily: fontMono, color: C.textMuted }}>{p.losses}</span>
          <span style={{ width: 36, textAlign: "center", fontSize: 13, fontFamily: fontMono, color: C.textMuted }}>{p.gp}</span>
        </div>
      ))}
      {mode === "dtr" && (
        <div style={{ marginTop: 12, padding: "12px 14px", fontSize: 12, color: C.textSec, background: C.card, borderRadius: 10, border: `1px solid ${C.border}` }}>
          <div style={{ fontWeight: 700, color: C.accent, marginBottom: 4, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em" }}>How DTR is Calculated</div>
          <div style={{ fontFamily: fontMono, fontWeight: 600, color: C.text, marginBottom: 6 }}>DTR = (10 × Wins) + Game Points + Point Diff</div>
          <div style={{ fontSize: 11, color: C.textMuted, lineHeight: 1.5 }}>
            Wins are worth 10 pts each. Every point you score adds up. Your score minus opponent score (+ or −) is added. Players who win big rank higher than close wins.
          </div>
        </div>
      )}
      <div className="tiebreaker-info">
        <span style={{ fontWeight: 700, color: C.textSec, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>Tiebreakers</span>
        <div style={{ marginTop: 3 }}>{mode === "dtr" ? "DTR score → Wins → Head-to-head → Point diff → Total pts" : "Total pts → Wins → Head-to-head → Point diff"}</div>
      </div>
    </div>
  );
}

function RoundHistoryList({ rounds, editable = false, onEditScore }) {
  const [editingRound, setEditingRound] = useState(null);
  const [editVal, setEditVal] = useState("");
  if (!rounds || rounds.length === 0) return <div className="empty-state">No completed rounds</div>;
  const startEdit = (ri, ci, team, currentVal) => { setEditingRound({ ri, ci, team }); setEditVal(currentVal === null ? "" : String(currentVal)); };
  const saveEdit = () => { if (!editingRound || !onEditScore) return; const val = editVal === "" ? null : parseInt(editVal, 10); onEditScore(editingRound.ri, editingRound.ci, editingRound.team, isNaN(val) ? null : val); setEditingRound(null); };
  return (
    <div>
      {rounds.map((r, ri) => (
        <div key={ri} style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Round {r.roundNumber || ri + 1}{editable && <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, marginLeft: 6, color: C.accent, fontSize: 11 }}>tap score to edit</span>}</div>
          {(r.courts || []).map((c, ci) => {
            const aW = (c.scoreA||0) > (c.scoreB||0), bW = (c.scoreB||0) > (c.scoreA||0);
            const eA = editingRound && editingRound.ri === ri && editingRound.ci === ci && editingRound.team === "A";
            const eB = editingRound && editingRound.ri === ri && editingRound.ci === ci && editingRound.team === "B";
            return (
              <div key={ci} style={{ padding: "8px 12px", background: C.card, borderRadius: 10, marginBottom: 4, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontWeight: aW?700:400, color: aW?C.accent:C.text }}>{(c.teamA||[]).map(p=>p.name).join(" & ")}</span>
                  <span style={{ color: C.textMuted, margin: "0 6px" }}>vs</span>
                  <span style={{ fontWeight: bW?700:400, color: bW?C.accent:C.text }}>{(c.teamB||[]).map(p=>p.name).join(" & ")}</span>
                </div>
                <span style={{ fontFamily: fontMono, fontWeight: 700, fontSize: 14, display: "flex", alignItems: "center", gap: 3, flexShrink: 0 }}>
                  {eA ? <input className="input" type="number" inputMode="numeric" value={editVal} onChange={e => setEditVal(e.target.value)} onBlur={saveEdit} onKeyDown={e => e.key === "Enter" && saveEdit()} autoFocus style={{ width: 48, padding: "3px 4px", fontSize: 14, textAlign: "center", fontFamily: fontMono }} />
                    : <span style={{ color: aW?C.accent:C.textMuted, cursor: editable?"pointer":"default", padding: editable?"3px 8px":"0", borderRadius: 6, background: editable?C.surfaceHover:"transparent" }} onClick={() => editable && startEdit(ri, ci, "A", c.scoreA)}>{c.scoreA ?? "–"}</span>}
                  <span style={{ color: C.textMuted }}>-</span>
                  {eB ? <input className="input" type="number" inputMode="numeric" value={editVal} onChange={e => setEditVal(e.target.value)} onBlur={saveEdit} onKeyDown={e => e.key === "Enter" && saveEdit()} autoFocus style={{ width: 48, padding: "3px 4px", fontSize: 14, textAlign: "center", fontFamily: fontMono }} />
                    : <span style={{ color: bW?C.accent:C.textMuted, cursor: editable?"pointer":"default", padding: editable?"3px 8px":"0", borderRadius: 6, background: editable?C.surfaceHover:"transparent" }} onClick={() => editable && startEdit(ri, ci, "B", c.scoreB)}>{c.scoreB ?? "–"}</span>}
                </span>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// ACTIVE TOURNAMENT (with timer, substitute, quick end)
// ════════════════════════════════════════════════════════════════════
function ActiveTournament({ tournament, allPlayers, onUpdateTournament, onEnd, onPause, isAdmin }) {
  const [showLB, setShowLB] = useState(false);
  const [showHist, setShowHist] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [showSub, setShowSub] = useState(null); // { courtIdx, playerIdx, team }
  const t = tournament;
  const courts = t.currentCourts || [];
  const allScored = courts.every(c => c.scoreA !== null && c.scoreB !== null);
  const anyScored = courts.some(c => c.scoreA !== null || c.scoreB !== null);
  const canSub = !anyScored; // Can substitute at start of any round before scores entered

  const updateScore = (idx, team, val) => {
    if (!isAdmin) return;
    const nc = [...courts];
    nc[idx] = { ...nc[idx], [team === "A" ? "scoreA" : "scoreB"]: val };
    onUpdateTournament({ ...t, currentCourts: nc });
    if (val !== null) haptic("light");
  };
  const nextRound = () => {
    if (!allScored || !isAdmin) return;
    haptic("success");
    const done = { roundNumber: t.currentRound, courts: t.currentCourts };
    const nr = [...(t.rounds || []), done];
    // Build partner history from all rounds including current
    const ph = [...(t.partnerHistory || [])];
    t.currentCourts.forEach(c => {
      c.teamA.forEach((p, i) => { if (c.teamA[1-i]) ph.push([p.id, c.teamA[1-i].id]); });
      c.teamB.forEach((p, i) => { if (c.teamB[1-i]) ph.push([p.id, c.teamB[1-i].id]); });
    });
    // Mixed division: constrain re-pairing so every team stays one man + one woman
    const genderLookup = (t.division || "mens") === "mixed" ? (pid) => genderOf(t, pid) : null;
    const nc = buildNextRoundCourts(t.currentCourts, ph, genderLookup);
    onUpdateTournament({ ...t, rounds: nr, currentCourts: nc, currentRound: t.currentRound + 1, partnerHistory: ph });
  };
  const endTournament = (forceEnd = false) => {
    if (!isAdmin) return;
    if (!forceEnd && !allScored) return;
    let finalRounds = [...(t.rounds || [])];
    if (allScored) {
      finalRounds.push({ roundNumber: t.currentRound, courts: t.currentCourts });
    } else {
      // Quick end: zero out unscored courts
      const zeroed = courts.map(c => ({
        ...c,
        scoreA: c.scoreA ?? 0,
        scoreB: c.scoreB ?? 0,
      }));
      finalRounds.push({ roundNumber: t.currentRound, courts: zeroed });
    }
    onEnd({ ...t, rounds: finalRounds, status: "finished", finishedAt: new Date().toISOString() });
    setConfirmEnd(false);
  };

  // Substitute logic — waitlist players shown first (from pre-registration), then all other available
  const tournamentPlayerIds = new Set((t.players || []).map(p => p.id));
  courts.forEach(c => { c.teamA.forEach(p => tournamentPlayerIds.add(p.id)); c.teamB.forEach(p => tournamentPlayerIds.add(p.id)); });
  const waitlistPlayers = (t.preRegistration?.waitlist || [])
    .map(w => allPlayers.find(p => p.id === w.playerId))
    .filter(p => p && !tournamentPlayerIds.has(p.id));
  const waitlistIds = new Set(waitlistPlayers.map(p => p.id));
  const otherSubs = allPlayers.filter(p => !tournamentPlayerIds.has(p.id) && !waitlistIds.has(p.id));
  const availSubs = [...waitlistPlayers, ...otherSubs];

  const handleSub = (sub) => {
    if (!showSub) return;
    const { courtIdx, playerIdx, team } = showSub;
    const nc = [...courts];
    const c = { ...nc[courtIdx] };
    const teamKey = team === "A" ? "teamA" : "teamB";
    const newTeam = [...c[teamKey]];
    const oldPlayer = newTeam[playerIdx];
    newTeam[playerIdx] = sub;
    c[teamKey] = newTeam;
    nc[courtIdx] = c;
    // Update players list
    const newPlayers = (t.players || []).map(p => p.id === oldPlayer.id ? sub : p);
    onUpdateTournament({ ...t, currentCourts: nc, players: newPlayers });
    setShowSub(null);
  };

  const [copiedLink, setCopiedLink] = useState(false);
  const copySpectatorLink = () => {
    const url = `${window.location.origin}${window.location.pathname}?spectate=${t.id}`;
    navigator.clipboard.writeText(url).then(() => { setCopiedLink(true); setTimeout(() => setCopiedLink(false), 2000); }).catch(() => {});
  };

  const lbRounds = [...(t.rounds || [])];
  if (courts.some(c => c.scoreA !== null || c.scoreB !== null)) lbRounds.push({ courts });

  return (
    <div className="fade-in">
      <div style={{ padding: "16px 16px 8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>{t.name}</div>
            <DivisionBadge division={t.division} />
          </div>
          <div style={{ fontSize: 12, color: C.textSec, marginTop: 2 }}>Round {t.currentRound} · {courts.length} court{courts.length !== 1 ? "s" : ""}</div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button className="btn btn-outline btn-sm" onClick={copySpectatorLink} style={{ color: copiedLink ? C.accent : C.blue, borderColor: copiedLink ? C.accent : C.blue, fontSize: 12 }}>{copiedLink ? <><I.Check size={14} /> Copied!</> : <><I.Share size={14} /> Share Live</>}</button>
          {(t.rounds || []).length > 0 && <button className="btn btn-ghost btn-sm" onClick={() => setShowHist(true)}><I.Clock /> History</button>}
          <button className="btn btn-ghost btn-sm" style={{ color: C.gold }} onClick={() => setShowLB(true)}><I.Trophy size={14} /> Board</button>
        </div>
      </div>
      <div style={{ padding: "8px 16px 120px" }}>
        {t.gameMinutes && <GameTimer minutes={t.gameMinutes} timerState={t.timerState} onTimerUpdate={isAdmin ? (ts) => onUpdateTournament({ ...t, timerState: ts }) : null} isAdmin={isAdmin} />}
        {courts.map((c, i) => {
          const has = c.scoreA !== null && c.scoreB !== null;
          const aW = has && (c.scoreA||0) > (c.scoreB||0), bW = has && (c.scoreB||0) > (c.scoreA||0);
          return (
            <div key={`${t.currentRound}-${c.courtNumber}`} className="card fade-in" style={{ marginBottom: 10, animationDelay: `${i*50}ms` }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <span className="badge court-badge">Court {c.courtNumber}</span>
                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                  {canSub && isAdmin && availSubs.length > 0 && <button className="btn btn-ghost btn-sm" onClick={() => setShowSub({ courtIdx: i, playerIdx: null, team: null })} title="Substitute"><I.Swap size={14} /></button>}
                  {has && <span className="badge live-badge">Scored</span>}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 0" }}>
                <div style={{ flex: 1 }}><span style={{ fontWeight: aW?700:500, fontSize: 15, color: aW?C.accent:C.text }}>{c.teamA.map(p => <PlayerName key={p.id} player={p} />).reduce((a, b) => [a, " & ", b])}</span></div>
                <input className="input-score" type="number" inputMode="numeric" value={c.scoreA === null ? "" : c.scoreA} onChange={e => updateScore(i, "A", e.target.value === "" ? null : parseInt(e.target.value, 10))} disabled={!isAdmin} placeholder="—" style={{ borderColor: aW?C.accent:C.border }} />
              </div>
              <div style={{ fontSize: 10, fontWeight: 800, color: C.textMuted, letterSpacing: "0.15em", padding: "2px 0 2px 2px" }}>VS</div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 0" }}>
                <div style={{ flex: 1 }}><span style={{ fontWeight: bW?700:500, fontSize: 15, color: bW?C.accent:C.text }}>{c.teamB.map(p => <PlayerName key={p.id} player={p} />).reduce((a, b) => [a, " & ", b])}</span></div>
                <input className="input-score" type="number" inputMode="numeric" value={c.scoreB === null ? "" : c.scoreB} onChange={e => updateScore(i, "B", e.target.value === "" ? null : parseInt(e.target.value, 10))} disabled={!isAdmin} placeholder="—" style={{ borderColor: bW?C.accent:C.border }} />
              </div>
            </div>
          );
        })}
      </div>
      {isAdmin && (
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: C.surface, borderTop: `1px solid ${C.border}`, display: "flex", gap: 10, padding: "12px 16px", justifyContent: "center", zIndex: 200 }}>
          <button className="btn btn-danger" onClick={() => setConfirmEnd(true)} style={{ flex: 0.5 }}><I.Stop size={14} /> End</button>
          <button className="btn btn-outline" onClick={() => onPause && onPause(t)} style={{ flex: 0.5, color: C.orange, borderColor: C.orange }}><I.PauseCircle size={14} /> Pause</button>
          <button className="btn btn-primary" disabled={!allScored} onClick={nextRound} style={{ flex: 1.2, padding: 14 }}>Next Round <I.ChevRight /></button>
        </div>
      )}
      {showLB && <Modal onClose={() => setShowLB(false)}><ModalHeader title={<><I.Trophy size={20} /> Leaderboard</>} onClose={() => setShowLB(false)} /><LeaderboardTable rounds={lbRounds} /></Modal>}
      {showHist && <Modal onClose={() => setShowHist(false)}><ModalHeader title="Round History" onClose={() => setShowHist(false)} /><RoundHistoryList rounds={t.rounds} editable={isAdmin} onEditScore={(ri, ci, team, val) => {
        const nr = (t.rounds || []).map((r, i) => i !== ri ? r : { ...r, courts: r.courts.map((c, j) => j !== ci ? c : { ...c, [team === "A" ? "scoreA" : "scoreB"]: val }) });
        onUpdateTournament({ ...t, rounds: nr });
      }} /></Modal>}
      {confirmEnd && (
        <Modal onClose={() => setConfirmEnd(false)}>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: C.text }}>End Tournament?</div>
          {allScored ? (
            <p style={{ color: C.textSec, fontSize: 14, marginBottom: 20 }}>Current round scores will be saved. Final standings will be calculated.</p>
          ) : (
            <div>
              <p style={{ color: C.textSec, fontSize: 14, marginBottom: 8 }}>Not all scores are entered for this round.</p>
              <p style={{ color: C.orange, fontSize: 13, marginBottom: 20 }}>All unscored teams will receive 0 points. The tournament will close with final standings from all rounds.</p>
            </div>
          )}
          <div style={{ display: "flex", gap: 10 }}>
            <button className="btn btn-outline" onClick={() => setConfirmEnd(false)} style={{ flex: 1 }}>Cancel</button>
            <button className="btn" onClick={() => endTournament(!allScored)} style={{ flex: 1, background: C.red, color: "#fff" }}>End Tournament</button>
          </div>
        </Modal>
      )}
      {showSub && (
        <Modal onClose={() => setShowSub(null)}>
          <ModalHeader title="Substitute Player" onClose={() => setShowSub(null)} />
          {showSub.playerIdx === null ? (
            <div>
              <div style={{ fontSize: 13, color: C.textSec, marginBottom: 14 }}>Select the player to replace on Court {courts[showSub.courtIdx].courtNumber}:</div>
              {["teamA", "teamB"].map((tk, ti) => (
                courts[showSub.courtIdx][tk].map((p, pi) => (
                  <button key={p.id} className="btn btn-outline btn-full" style={{ marginBottom: 6 }} onClick={() => setShowSub({ ...showSub, playerIdx: pi, team: ti === 0 ? "A" : "B" })}>
                    {p.name}
                  </button>
                ))
              ))}
            </div>
          ) : (() => {
            // Mixed division: only offer substitutes of the same gender as the player being replaced
            let shownWaitlist = waitlistPlayers, shownOthers = otherSubs;
            if ((t.division || "mens") === "mixed") {
              const replaced = courts[showSub.courtIdx][showSub.team === "A" ? "teamA" : "teamB"][showSub.playerIdx];
              const g = genderOf(t, replaced?.id);
              if (g) {
                const sameGender = (p) => p.gender === g;
                shownWaitlist = waitlistPlayers.filter(sameGender);
                shownOthers = otherSubs.filter(sameGender);
              }
            }
            const shownSubs = [...shownWaitlist, ...shownOthers];
            return (
            <div>
              <div style={{ fontSize: 13, color: C.textSec, marginBottom: 14 }}>Choose substitute:</div>
              {shownWaitlist.length > 0 && <div style={{ fontSize: 11, fontWeight: 700, color: C.orange, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Waitlist ({shownWaitlist.length})</div>}
              {shownSubs.map((p, i) => (
                <React.Fragment key={p.id}>
                  {i === shownWaitlist.length && shownWaitlist.length > 0 && shownOthers.length > 0 && <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 10, marginBottom: 6 }}>Other Players</div>}
                  <button className="btn btn-outline btn-full" style={{ marginBottom: 6, borderColor: i < shownWaitlist.length ? C.orange : C.border }} onClick={() => handleSub(p)}>
                    {i < shownWaitlist.length && <span style={{ fontSize: 10, fontWeight: 700, color: C.orange, marginRight: 6 }}>W{i + 1}</span>}
                    {p.name}{p.ranking != null && <span style={{ fontSize: 11, color: C.gold, marginLeft: 6 }}>★{p.ranking}</span>}
                  </button>
                </React.Fragment>
              ))}
              {shownSubs.length === 0 && <div className="empty-state">No available substitutes</div>}
            </div>
            );
          })()}
        </Modal>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// ACTIVE ROUND ROBIN TOURNAMENT
// ════════════════════════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════════════
// ACTIVE PBR (stub - full component coming in next build)
// ════════════════════════════════════════════════════════════════════
function ActivePBR({ tournament, onUpdateTournament, onEnd, isAdmin }) {
  const t = tournament;
  const allPlayers = t.players || [];
  const courts = t.courts || [];
  const slots = courts.length * 4;
  const isMixed = (t.division || "mens") === "mixed";
  const pGender = (p) => genderOf(t, p?.id);

  // ── local state ──────────────────────────────────────────────────
  const [rounds, setRounds] = useState(t.rounds || []);
  const [phase, setPhase] = useState(() => {
    // If we have a round with no scores yet → "scoring"; else "pregame"
    const last = (t.rounds || []).slice(-1)[0];
    if (last && !last.locked) return "scoring";
    return "pregame";
  });
  const [currentRound, setCurrentRound] = useState(t.currentRound || 1);
  const [pendingCourts, setPendingCourts] = useState([]); // courts for the round being previewed
  const [scores, setScores] = useState({});             // { courtIndex: { a: "", b: "" } }
  const [swapModal, setSwapModal] = useState(null);     // { courtIdx, slot: "teamA"|"teamB", playerIdx }
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [showStandings, setShowStandings] = useState(false);

  // ── helpers ──────────────────────────────────────────────────────
  const save = async (updatedT) => {
    try { await onUpdateTournament(updatedT); } catch (e) { console.error("PBR save error", e); }
  };

  // Build per-player stats from completed rounds
  const calcStandings = (roundList) => {
    const stats = {};
    allPlayers.forEach(p => { stats[p.id] = { id: p.id, name: p.name, wins: 0, losses: 0, pf: 0, pa: 0, gp: 0 }; });
    roundList.forEach(r => {
      if (!r.locked) return;
      (r.courts || []).forEach(c => {
        const scoreA = Number(c.scoreA), scoreB = Number(c.scoreB);
        if (isNaN(scoreA) || isNaN(scoreB)) return;
        const aWon = scoreA > scoreB;
        [...(c.teamA || [])].forEach(p => {
          if (!p || !stats[p.id]) return;
          stats[p.id].gp++;
          stats[p.id].pf += scoreA;
          stats[p.id].pa += scoreB;
          if (aWon) stats[p.id].wins++; else stats[p.id].losses++;
        });
        [...(c.teamB || [])].forEach(p => {
          if (!p || !stats[p.id]) return;
          stats[p.id].gp++;
          stats[p.id].pf += scoreB;
          stats[p.id].pa += scoreA;
          if (!aWon) stats[p.id].wins++; else stats[p.id].losses++;
        });
      });
    });
    return Object.values(stats).sort((a, b) => {
      const wpA = a.gp ? a.wins / a.gp : 0;
      const wpB = b.gp ? b.wins / b.gp : 0;
      if (wpB !== wpA) return wpB - wpA;
      const diffA = a.pf - a.pa, diffB = b.pf - b.pa;
      return diffB - diffA;
    });
  };

  // Build partner history: map playerId → Set of partner ids
  const buildPartnerHistory = (roundList) => {
    const hist = {};
    allPlayers.forEach(p => { hist[p.id] = new Set(); });
    roundList.forEach(r => {
      (r.courts || []).forEach(c => {
        const [a0, a1] = c.teamA || [];
        const [b0, b1] = c.teamB || [];
        if (a0 && a1) { hist[a0.id]?.add(a1.id); hist[a1.id]?.add(a0.id); }
        if (b0 && b1) { hist[b0.id]?.add(b1.id); hist[b1.id]?.add(b0.id); }
      });
    });
    return hist;
  };

  // Generate court assignments for a new round
  const generateRound = (roundList) => {
    const standings = calcStandings(roundList);
    const partnerHist = buildPartnerHistory(roundList);

    // Determine sit-outs: prioritize fewest games played
    const sorted = [...standings].sort((a, b) => a.gp - b.gp || Math.random() - 0.5);
    let sitOutIds;
    if (isMixed) {
      // Mixed: sit out men and women independently so active players stay balanced
      const menIds = new Set(allPlayers.filter(p => pGender(p) === "M").map(p => p.id));
      const womenIds = new Set(allPlayers.filter(p => pGender(p) === "F").map(p => p.id));
      const perGender = slots / 2;
      const sitMen = Math.max(0, menIds.size - perGender);
      const sitWomen = Math.max(0, womenIds.size - perGender);
      sitOutIds = new Set([
        ...sorted.filter(s => menIds.has(s.id)).slice(0, sitMen).map(s => s.id),
        ...sorted.filter(s => womenIds.has(s.id)).slice(0, sitWomen).map(s => s.id),
      ]);
    } else {
      const sitOutCount = Math.max(0, allPlayers.length - slots);
      sitOutIds = new Set(sorted.slice(0, sitOutCount).map(p => p.id));
    }
    let activePlayers = allPlayers.filter(p => !sitOutIds.has(p.id));

    // Shuffle active players, then try to pair winners with losers
    // Split into winners (win% >= 0.5) and losers (win% < 0.5), interleave
    const wp = (p) => { const s = standings.find(x => x.id === p.id); return s && s.gp ? s.wins / s.gp : 0.5; };
    const winners = activePlayers.filter(p => wp(p) >= 0.5);
    const losers = activePlayers.filter(p => wp(p) < 0.5);
    // Shuffle each group
    const shuffle = arr => { const a = [...arr]; for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a; };
    const shuffledW = shuffle(winners);
    const shuffledL = shuffle(losers);
    // Interleave: W L W L ... so teams get mixed
    const ordered = [];
    const maxLen = Math.max(shuffledW.length, shuffledL.length);
    for (let i = 0; i < maxLen; i++) {
      if (i < shuffledW.length) ordered.push(shuffledW[i]);
      if (i < shuffledL.length) ordered.push(shuffledL[i]);
    }

    // Pair into teams of 2, avoiding repeat partners where possible
    const tryPair = (pool) => {
      const used = new Set();
      const pairs = [];
      for (let i = 0; i < pool.length; i++) {
        if (used.has(pool[i].id)) continue;
        // Find best partner: prefer no prior partnership
        let partner = null;
        for (let j = i + 1; j < pool.length; j++) {
          if (used.has(pool[j].id)) continue;
          if (!partnerHist[pool[i].id]?.has(pool[j].id)) { partner = pool[j]; break; }
        }
        // Fall back to any available
        if (!partner) {
          for (let j = i + 1; j < pool.length; j++) {
            if (!used.has(pool[j].id)) { partner = pool[j]; break; }
          }
        }
        if (partner) {
          pairs.push([pool[i], partner]);
          used.add(pool[i].id);
          used.add(partner.id);
        }
      }
      return pairs;
    };

    // Mixed: pair each man with a woman (avoiding repeat partners where possible)
    let pairs = null;
    if (isMixed) {
      const menPool = ordered.filter(p => pGender(p) === "M");
      const womenPool = ordered.filter(p => pGender(p) === "F");
      if (menPool.length > 0 && menPool.length === womenPool.length) {
        const usedW = new Set();
        pairs = [];
        menPool.forEach(m => {
          let partner = womenPool.find(w => !usedW.has(w.id) && !partnerHist[m.id]?.has(w.id));
          if (!partner) partner = womenPool.find(w => !usedW.has(w.id));
          if (partner) { usedW.add(partner.id); pairs.push([m, partner]); }
        });
      }
      // If the pool is unbalanced (shouldn't happen), fall back to unconstrained pairing below
    }
    if (!pairs) pairs = tryPair(ordered);
    // Assign pairs to courts: pair[0] vs pair[1] on court 0, pair[2] vs pair[3] on court 1, etc.
    const courtAssignments = courts.map((c, i) => {
      const teamA = pairs[i * 2] || [];
      const teamB = pairs[i * 2 + 1] || [];
      return { courtId: c.courtId, courtNumber: c.courtNumber, teamA, teamB, scoreA: null, scoreB: null };
    });

    const sitOutPlayers = allPlayers.filter(p => sitOutIds.has(p.id));
    return { courtAssignments, sitOutPlayers };
  };

  // ── actions ──────────────────────────────────────────────────────
  const handleGenerateRound = () => {
    const { courtAssignments, sitOutPlayers } = generateRound(rounds);
    setPendingCourts(courtAssignments);
    setScores({});
    // Store sit-outs so swap modal can use them
    setPendingCourts(courtAssignments.map((c, i) => ({ ...c, _sitOuts: i === 0 ? sitOutPlayers : [] })));
    setPhase("preview");
  };

  // Swap a player in preview phase
  const handleSwap = (courtIdx, team, playerIdx, newPlayer) => {
    // Find which player is being replaced
    const replaced = pendingCourts[courtIdx][team][playerIdx];
    // Get current sit-outs from first court's _sitOuts
    const sitOuts = pendingCourts[0]?._sitOuts || [];
    const newSitOuts = sitOuts.filter(p => p.id !== newPlayer.id);
    if (replaced) newSitOuts.push(replaced);
    setPendingCourts(prev => prev.map((c, i) => {
      const updated = { ...c };
      if (i === courtIdx) {
        const newTeam = [...c[team]];
        newTeam[playerIdx] = newPlayer;
        updated[team] = newTeam;
      }
      if (i === 0) updated._sitOuts = newSitOuts;
      return updated;
    }));
    setSwapModal(null);
  };

  const handleLockRound = () => {
    const newRound = { roundNumber: currentRound, courts: pendingCourts.map(c => ({ ...c, _sitOuts: undefined })), locked: false };
    const newRounds = [...rounds, newRound];
    setRounds(newRounds);
    setScores({});
    setPhase("scoring");
    const updated = { ...t, rounds: newRounds, currentRound };
    save(updated);
  };

  const handleScoreChange = (courtIdx, team, val) => {
    setScores(prev => ({ ...prev, [courtIdx]: { ...(prev[courtIdx] || {}), [team]: val } }));
  };

  const allScored = pendingCourts.length > 0 && rounds.length > 0 && (() => {
    const lastRound = rounds[rounds.length - 1];
    return (lastRound?.courts || []).every((_, i) => {
      const s = scores[i];
      return s && s.a !== "" && s.a !== undefined && s.b !== "" && s.b !== undefined && !isNaN(Number(s.a)) && !isNaN(Number(s.b));
    });
  })();

  const handleSaveScores = async () => {
    const lastRound = rounds[rounds.length - 1];
    if (!lastRound) return;
    const updatedCourts = lastRound.courts.map((c, i) => ({
      ...c,
      scoreA: Number(scores[i]?.a ?? c.scoreA),
      scoreB: Number(scores[i]?.b ?? c.scoreB),
    }));
    const updatedRound = { ...lastRound, courts: updatedCourts, locked: true };
    const newRounds = [...rounds.slice(0, -1), updatedRound];
    setRounds(newRounds);
    setPhase("pregame");
    setCurrentRound(prev => prev + 1);
    const updated = { ...t, rounds: newRounds, currentRound: currentRound + 1 };
    save(updated);
  };

  const handleEndTournament = async () => {
    const standings = calcStandings(rounds);
    const winner = standings[0];
    const updated = { ...t, rounds, status: "finished", finishedAt: new Date().toISOString(), winner: winner?.name || null };
    await onEnd(updated);
  };

  // ── render helpers ────────────────────────────────────────────────
  const standings = calcStandings(rounds);
  const lastRound = rounds[rounds.length - 1];
  const scoringCourts = lastRound?.courts || [];

  // ── STANDINGS TABLE ───────────────────────────────────────────────
  const StandingsTable = () => (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", padding: "0 12px 8px", fontSize: 10, fontWeight: 700, color: C.textMuted, letterSpacing: "0.08em" }}>
        <span style={{ width: 28 }}>#</span>
        <span style={{ flex: 1 }}>PLAYER</span>
        <span style={{ width: 44, textAlign: "center" }}>W%</span>
        <span style={{ width: 36, textAlign: "center" }}>W</span>
        <span style={{ width: 36, textAlign: "center" }}>L</span>
        <span style={{ width: 44, textAlign: "center" }}>+/-</span>
      </div>
      {standings.map((p, i) => {
        const wp = p.gp ? ((p.wins / p.gp) * 100).toFixed(0) : "-";
        const diff = p.pf - p.pa;
        const rankClass = i === 0 ? "rank-1" : i === 1 ? "rank-2" : i === 2 ? "rank-3" : "rank-n";
        return (
          <div key={p.id} className={`card ${rankClass}`} style={{ padding: "10px 12px", marginBottom: 4, display: "flex", alignItems: "center" }}>
            <span style={{ width: 28, fontWeight: 900, fontFamily: fontMono, fontSize: i < 3 ? 15 : 13 }}>{i < 3 ? ["🥇", "🥈", "🥉"][i] : i + 1}</span>
            <span style={{ flex: 1, fontWeight: 600, fontSize: 14 }}>{p.name}</span>
            <span style={{ width: 44, textAlign: "center", fontWeight: 700, fontSize: 13, color: C.accent }}>{wp}{p.gp ? "%" : ""}</span>
            <span style={{ width: 36, textAlign: "center", fontSize: 13, color: C.green }}>{p.wins}</span>
            <span style={{ width: 36, textAlign: "center", fontSize: 13, color: C.red }}>{p.losses}</span>
            <span style={{ width: 44, textAlign: "center", fontSize: 13, color: diff >= 0 ? C.green : C.red }}>{diff > 0 ? "+" : ""}{diff}</span>
          </div>
        );
      })}
    </div>
  );

  // ── PREGAME (between rounds) ──────────────────────────────────────
  if (phase === "pregame") {
    return (
      <div style={{ padding: 16 }} className="fade-in">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>{t.name}</div>
            <DivisionBadge division={t.division} />
          </div>
          <span style={{ fontSize: 12, color: C.textMuted }}>Round {currentRound}</span>
        </div>
        <div style={{ fontSize: 13, color: C.textSec, marginBottom: 16 }}>{allPlayers.length} players · {courts.length} courts · {Math.max(0, allPlayers.length - slots)} sit out per round</div>

        {rounds.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.textSec, marginBottom: 10 }}>STANDINGS</div>
            <StandingsTable />
          </div>
        )}

        {isAdmin && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <button className="btn btn-primary" style={{ padding: 14, fontSize: 16 }} onClick={handleGenerateRound}>
              <I.Play size={18} /> {rounds.length === 0 ? "Start Round 1" : `Start Round ${currentRound}`}
            </button>
            {rounds.length > 0 && (
              <button className="btn btn-outline" style={{ padding: 12, fontSize: 14, borderColor: C.red, color: C.red }} onClick={() => setConfirmEnd(true)}>
                End Tournament
              </button>
            )}
          </div>
        )}

        {confirmEnd && (
          <Modal onClose={() => setConfirmEnd(false)}>
            <ModalHeader title="End Tournament?" onClose={() => setConfirmEnd(false)} />
            <div style={{ padding: "0 20px 20px" }}>
              <div style={{ fontSize: 14, color: C.textSec, marginBottom: 20 }}>
                {standings[0] ? <><strong>{standings[0].name}</strong> wins with {standings[0].gp ? ((standings[0].wins / standings[0].gp) * 100).toFixed(0) : 0}% win rate.</> : "No rounds completed."}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => setConfirmEnd(false)}>Cancel</button>
                <button className="btn btn-primary" style={{ flex: 1, background: C.red, borderColor: C.red }} onClick={handleEndTournament}>End Tournament</button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    );
  }

  // ── PREVIEW (admin reviews teams before locking) ──────────────────
  if (phase === "preview") {
    const sitOuts = pendingCourts[0]?._sitOuts || [];
    return (
      <div style={{ padding: 16 }} className="fade-in">
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>{t.name}</div>
          <DivisionBadge division={t.division} />
        </div>
        <div style={{ fontSize: 13, color: C.textSec, marginBottom: 16 }}>Round {currentRound} · Review matchups before locking</div>

        {pendingCourts.map((c, ci) => (
          <div key={ci} className="card" style={{ marginBottom: 12, padding: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.textMuted, marginBottom: 10 }}>COURT {c.courtNumber}</div>
            {["teamA", "teamB"].map((team, ti) => (
              <div key={team}>
                {ti === 1 && <div style={{ textAlign: "center", fontSize: 11, color: C.textMuted, margin: "6px 0" }}>vs</div>}
                <div style={{ display: "flex", gap: 6 }}>
                  {(c[team] || []).map((p, pi) => (
                    <div key={pi} style={{ flex: 1, background: C.surface, borderRadius: 8, padding: "8px 10px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <span style={{ fontWeight: 600, fontSize: 13, color: C.text }}>{p?.name || "—"}</span>
                      {isAdmin && (
                        <button className="btn btn-ghost btn-sm" style={{ padding: 2, fontSize: 11, color: C.textMuted }}
                          onClick={() => setSwapModal({ courtIdx: ci, team, playerIdx: pi })}>⇄</button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}

        {sitOuts.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.textMuted, marginBottom: 6 }}>SITTING OUT THIS ROUND</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {sitOuts.map(p => (
                <span key={p.id} style={{ background: C.surface, borderRadius: 6, padding: "4px 10px", fontSize: 13, color: C.textSec }}>{p.name}</span>
              ))}
            </div>
          </div>
        )}

        {isAdmin && (
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => setPhase("pregame")}>Back</button>
            <button className="btn btn-primary" style={{ flex: 2, padding: 14, fontSize: 16 }} onClick={handleLockRound}>
              <I.Lock size={16} /> Lock &amp; Start Round
            </button>
          </div>
        )}

        {swapModal && (() => {
          // Mixed division: only offer sit-outs of the same gender as the player being replaced
          let candidates = pendingCourts[0]?._sitOuts || [];
          if (isMixed) {
            const replaced = pendingCourts[swapModal.courtIdx]?.[swapModal.team]?.[swapModal.playerIdx];
            const g = pGender(replaced);
            if (g) candidates = candidates.filter(p => pGender(p) === g);
          }
          return (
          <Modal onClose={() => setSwapModal(null)}>
            <ModalHeader title="Swap Player" onClose={() => setSwapModal(null)} />
            <div style={{ padding: "0 20px 20px" }}>
              <div style={{ fontSize: 13, color: C.textSec, marginBottom: 12 }}>Choose a sitting-out player to swap in:</div>
              {candidates.length === 0 && (
                <div style={{ fontSize: 13, color: C.textMuted }}>No players sitting out.</div>
              )}
              {candidates.map(p => (
                <div key={p.id} className="card" style={{ padding: "10px 14px", marginBottom: 6, cursor: "pointer" }}
                  onClick={() => handleSwap(swapModal.courtIdx, swapModal.team, swapModal.playerIdx, p)}>
                  <span style={{ fontWeight: 600, color: C.text }}>{p.name}</span>
                </div>
              ))}
            </div>
          </Modal>
          );
        })()}
      </div>
    );
  }

  // ── SCORING ───────────────────────────────────────────────────────
  if (phase === "scoring") {
    return (
      <div style={{ padding: 16 }} className="fade-in">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>{t.name}</div>
            <DivisionBadge division={t.division} />
          </div>
          <button className="btn btn-ghost btn-sm" style={{ fontSize: 12, color: C.textSec }} onClick={() => setShowStandings(s => !s)}>
            {showStandings ? "Hide" : "Standings"}
          </button>
        </div>
        <div style={{ fontSize: 13, color: C.textSec, marginBottom: 16 }}>Round {currentRound - 1} · Enter scores</div>

        {showStandings && <StandingsTable />}

        {scoringCourts.map((c, ci) => {
          const sa = scores[ci]?.a ?? (c.scoreA !== null ? String(c.scoreA) : "");
          const sb = scores[ci]?.b ?? (c.scoreB !== null ? String(c.scoreB) : "");
          const numA = Number(sa), numB = Number(sb);
          const aWon = sa !== "" && sb !== "" && !isNaN(numA) && !isNaN(numB) && numA > numB;
          const bWon = sa !== "" && sb !== "" && !isNaN(numA) && !isNaN(numB) && numB > numA;
          return (
            <div key={ci} className="card" style={{ marginBottom: 12, padding: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.textMuted, marginBottom: 10 }}>COURT {c.courtNumber}</div>
              {["teamA", "teamB"].map((team, ti) => {
                const won = ti === 0 ? aWon : bWon;
                return (
                  <div key={team}>
                    {ti === 1 && <div style={{ textAlign: "center", fontSize: 11, color: C.textMuted, margin: "4px 0" }}>vs</div>}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0" }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: won ? C.accent : C.text }}>
                          {(c[team] || []).map(p => p?.name).filter(Boolean).join(" & ")}
                          {won && <span style={{ marginLeft: 6, fontSize: 11, color: C.accent }}>✓ WIN</span>}
                        </div>
                      </div>
                      <input
                        type="number" inputMode="numeric" min="0" max="99"
                        value={ti === 0 ? sa : sb}
                        onChange={e => handleScoreChange(ci, ti === 0 ? "a" : "b", e.target.value)}
                        style={{ width: 56, textAlign: "center", fontSize: 18, fontWeight: 700, padding: "6px 4px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}

        {isAdmin && (
          <button className="btn btn-primary" style={{ width: "100%", padding: 14, fontSize: 16, marginTop: 8 }}
            disabled={!allScored} onClick={handleSaveScores}>
            Save Scores &amp; Continue
          </button>
        )}
      </div>
    );
  }

  return null;
}



// ════════════════════════════════════════════════════════════════════
// ACTIVE ROUND ROBIN
// ════════════════════════════════════════════════════════════════════
function ActiveRoundRobin({ tournament, onUpdateTournament, onEnd, onPause, onSeedToBracket, isAdmin }) {
  const [showLB, setShowLB] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [showSeedModal, setShowSeedModal] = useState(false);
  const [seedCount, setSeedCount] = useState(0);
  const [copiedLink, setCopiedLink] = useState(false);
  const t = tournament;
  const schedule = t.schedule || [];
  const currentIdx = t.currentMatchRound || 0;
  const currentMatches = schedule[currentIdx] || [];
  const completedRounds = (t.completedRounds || []);
  const allScored = currentMatches.every(m => m.scoreA !== null && m.scoreB !== null);
  const isLastRound = currentIdx >= schedule.length - 1;

  const updateScore = (mi, team, val) => {
    if (!isAdmin) return;
    const ns = schedule.map((r, ri) => ri === currentIdx ? r.map((m, i) => i === mi ? { ...m, [team === "A" ? "scoreA" : "scoreB"]: val } : m) : r);
    onUpdateTournament({ ...t, schedule: ns });
    if (val !== null) haptic("light");
  };

  const nextRound = () => {
    if (!allScored || !isAdmin) return;
    haptic("success");
    const done = [...completedRounds, { roundNumber: currentIdx + 1, courts: currentMatches }];
    onUpdateTournament({ ...t, completedRounds: done, currentMatchRound: currentIdx + 1 });
  };

  const endTournament = (force = false) => {
    if (!isAdmin) return;
    let finalRounds = [...completedRounds];
    const zeroed = currentMatches.map(m => ({ ...m, scoreA: m.scoreA ?? 0, scoreB: m.scoreB ?? 0 }));
    finalRounds.push({ roundNumber: currentIdx + 1, courts: zeroed });
    onEnd({ ...t, rounds: finalRounds, status: "finished", finishedAt: new Date().toISOString() });
  };

  const allRounds = [...completedRounds];
  if (currentMatches.some(m => m.scoreA !== null || m.scoreB !== null)) {
    allRounds.push({ courts: currentMatches });
  }

  const getTeamStandings = (rounds) => {
    const stats = {};
    (t.teams || []).forEach(tm => { stats[tm.id] = { ...tm, wins: 0, losses: 0, pts: 0, diff: 0 }; });
    rounds.forEach(r => {
      (r.courts || r).forEach(m => {
        if (m.scoreA === null || m.scoreB === null) return;
        const aId = m.teamAId, bId = m.teamBId;
        if (stats[aId]) { stats[aId].pts += (m.scoreA||0); stats[aId].diff += (m.scoreA||0) - (m.scoreB||0); }
        if (stats[bId]) { stats[bId].pts += (m.scoreB||0); stats[bId].diff += (m.scoreB||0) - (m.scoreA||0); }
        if ((m.scoreA||0) > (m.scoreB||0)) { if (stats[aId]) stats[aId].wins++; if (stats[bId]) stats[bId].losses++; }
        else if ((m.scoreB||0) > (m.scoreA||0)) { if (stats[bId]) stats[bId].wins++; if (stats[aId]) stats[aId].losses++; }
      });
    });
    return Object.values(stats).sort((a, b) => b.wins - a.wins || b.diff - a.diff || b.pts - a.pts);
  };

  const handleShowSeed = () => { setSeedCount((t.teams || []).length); setShowSeedModal(true); };
  const handleSeedToBracket = () => {
    const allDone = [...completedRounds, { courts: currentMatches }];
    const standings = getTeamStandings(allDone);
    onSeedToBracket(t, standings.slice(0, seedCount));
  };

  const teamLabel = (m, side) => {
    const name = side === "A" ? m.teamAName : m.teamBName;
    const players = side === "A" ? m.teamA : m.teamB;
    if (!players) return <span style={{ color: C.textMuted }}>BYE</span>;
    return (
      <span style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        {name && <span style={{ fontWeight: 700, fontSize: 13, color: C.textSec }}>{name}</span>}
        <span style={{ fontWeight: 700, fontSize: 14, color: C.teamName, background: C.teamNameBg, border: `1px solid ${C.teamNameBorder}`, padding: "2px 10px", borderRadius: 6 }}>{players.map(p => p.name).join(" & ")}</span>
      </span>
    );
  };

  const copySpectatorLink = () => {
    const url = `${window.location.origin}${window.location.pathname}?spectate=${t.id}`;
    navigator.clipboard.writeText(url).then(() => { setCopiedLink(true); setTimeout(() => setCopiedLink(false), 2000); }).catch(() => {});
  };

  return (
    <div className="fade-in">
      <div style={{ padding: "16px 16px 8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>{t.name}</div>
            <DivisionBadge division={t.division} />
          </div>
          <div style={{ fontSize: 12, color: C.textSec, marginTop: 2 }}>Round Robin · Round {currentIdx + 1} of {schedule.length}</div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button className="btn btn-outline btn-sm" onClick={copySpectatorLink} style={{ color: copiedLink ? C.accent : C.blue, borderColor: copiedLink ? C.accent : C.blue, fontSize: 12 }}>{copiedLink ? <><I.Check size={14} /> Copied!</> : <><I.Share size={14} /> Share Live</>}</button>
          <button className="btn btn-ghost btn-sm" style={{ color: C.gold }} onClick={() => setShowLB(true)}><I.Trophy size={14} /> Board</button>
        </div>
      </div>
      <div style={{ padding: "8px 16px 120px" }}>
        {t.gameMinutes && <GameTimer minutes={t.gameMinutes} timerState={t.timerState} onTimerUpdate={isAdmin ? (ts) => onUpdateTournament({ ...t, timerState: ts }) : null} isAdmin={isAdmin} />}
        {currentMatches.map((m, i) => {
          const has = m.scoreA !== null && m.scoreB !== null;
          const aW = has && (m.scoreA||0) > (m.scoreB||0), bW = has && (m.scoreB||0) > (m.scoreA||0);
          return (
            <div key={i} className="card fade-in" style={{ marginBottom: 10, animationDelay: `${i*50}ms` }}>
              {m.courtNumber && <div style={{ marginBottom: 8 }}><span className="badge court-badge">Court {m.courtNumber}</span></div>}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 0" }}>
                <div style={{ flex: 1, color: aW ? C.accent : C.text, fontWeight: aW ? 700 : 500, fontSize: 14 }}>{teamLabel(m, "A")}</div>
                <input className="input-score" type="number" inputMode="numeric" value={m.scoreA === null ? "" : m.scoreA} onChange={e => updateScore(i, "A", e.target.value === "" ? null : parseInt(e.target.value, 10))} disabled={!isAdmin} placeholder="—" style={{ borderColor: aW?C.accent:C.border }} />
              </div>
              <div style={{ fontSize: 10, fontWeight: 800, color: C.textMuted, letterSpacing: "0.15em", padding: "2px 0 2px 2px" }}>VS</div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 0" }}>
                <div style={{ flex: 1, color: bW ? C.accent : C.text, fontWeight: bW ? 700 : 500, fontSize: 14 }}>{teamLabel(m, "B")}</div>
                <input className="input-score" type="number" inputMode="numeric" value={m.scoreB === null ? "" : m.scoreB} onChange={e => updateScore(i, "B", e.target.value === "" ? null : parseInt(e.target.value, 10))} disabled={!isAdmin} placeholder="—" style={{ borderColor: bW?C.accent:C.border }} />
              </div>
            </div>
          );
        })}
      </div>
      {isAdmin && (
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: C.surface, borderTop: `1px solid ${C.border}`, display: "flex", gap: 8, padding: "12px 16px", justifyContent: "center", zIndex: 200 }}>
          <button className="btn btn-danger" onClick={() => setConfirmEnd(true)} style={{ flex: 0.4 }}><I.Stop size={14} /> End</button>
          <button className="btn btn-outline" onClick={() => onPause && onPause(t)} style={{ flex: 0.4, color: C.orange, borderColor: C.orange, fontSize: 12 }}><I.PauseCircle size={14} /> Pause</button>
          {isLastRound && allScored ? (<>
            <button className="btn btn-outline" onClick={() => endTournament(false)} style={{ flex: 0.8, fontSize: 13 }}>Finish</button>
            <button className="btn btn-primary" onClick={handleShowSeed} style={{ flex: 1.2, padding: 14, fontSize: 13 }}>Seed to Bracket <I.ChevRight /></button>
          </>) : (
            <button className="btn btn-primary" disabled={!allScored} onClick={nextRound} style={{ flex: 1.2, padding: 14 }}>Next Round <I.ChevRight /></button>
          )}
        </div>
      )}
      {showLB && <Modal onClose={() => setShowLB(false)}><ModalHeader title={<><I.Trophy size={20} /> Standings</>} onClose={() => setShowLB(false)} /><LeaderboardTable rounds={allRounds} /></Modal>}
      {confirmEnd && (<Modal onClose={() => setConfirmEnd(false)}>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: C.text }}>End Round Robin?</div>
        <p style={{ color: C.orange, fontSize: 13, marginBottom: 20 }}>Unscored matches get 0 points.</p>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="btn btn-outline" onClick={() => setConfirmEnd(false)} style={{ flex: 1 }}>Cancel</button>
          <button className="btn" onClick={() => endTournament(true)} style={{ flex: 1, background: C.red, color: "#fff" }}>End Tournament</button>
        </div>
      </Modal>)}
      {showSeedModal && (<Modal onClose={() => setShowSeedModal(false)}>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4, color: C.text }}>Seed to Bracket</div>
        <div style={{ fontSize: 13, color: C.textSec, marginBottom: 16 }}>Teams seeded by RR standings (wins → point diff → total pts).</div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.textSec, marginBottom: 8 }}>How many teams?</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {Array.from({ length: Math.max((t.teams||[]).length - 1, 1) }, (_, i) => i + 2).map(n => (
              <button key={n} className={`toggle-btn ${seedCount === n ? "active" : ""}`} onClick={() => setSeedCount(n)}>{n}</button>
            ))}
          </div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.textMuted, marginBottom: 6 }}>Seed preview:</div>
          {getTeamStandings([...completedRounds, { courts: currentMatches }]).slice(0, seedCount).map((tm, i) => (
            <div key={tm.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", fontSize: 13 }}>
              <span style={{ fontFamily: fontMono, fontWeight: 900, color: C.accent, width: 20 }}>#{i+1}</span>
              <span style={{ fontWeight: 600, color: C.text }}>{tm.name}</span>
              <span style={{ color: C.textSec, fontSize: 11 }}>{tm.wins}W-{tm.losses}L · {tm.diff > 0 ? "+" : ""}{tm.diff}</span>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="btn btn-outline" onClick={() => setShowSeedModal(false)} style={{ flex: 1 }}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSeedToBracket} style={{ flex: 1 }}>Start Bracket <I.Play size={14} /></button>
        </div>
      </Modal>)}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// ACTIVE BRACKET TOURNAMENT
// ════════════════════════════════════════════════════════════════════
function ActiveBracket({ tournament, onUpdateTournament, onEnd, onPause, isAdmin }) {
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [advanceHistory, setAdvanceHistory] = useState([]); // stack of { ri, mi } for multi-level undo
  const t = tournament;
  const bracketRounds = t.bracketRounds || [];

  const getRoundLabel = (ri, total) => {
    const fromEnd = total - 1 - ri;
    if (fromEnd === 0) return "Final";
    if (fromEnd === 1) return "Semifinals";
    if (fromEnd === 2) return "Quarterfinals";
    return `Round ${ri + 1}`;
  };

  const updateScore = (ri, mi, team, val) => {
    if (!isAdmin) return;
    const nb = bracketRounds.map((r, rri) => rri === ri ? r.map((m, mmi) => mmi === mi ? { ...m, [team === "A" ? "scoreA" : "scoreB"]: val } : m) : r.map(m => ({ ...m })));
    onUpdateTournament({ ...t, bracketRounds: nb });
    if (val !== null) haptic("light");
  };

  const advanceWinner = (ri, mi) => {
    if (!isAdmin) return;
    const match = bracketRounds[ri][mi];
    if (match.scoreA === null || match.scoreB === null) return;
    const winner = (match.scoreA||0) >= (match.scoreB||0) ? "A" : "B";
    const nb = bracketRounds.map(r => r.map(m => ({ ...m })));
    nb[ri][mi].winner = winner;
    if (ri + 1 < nb.length) {
      const nextMi = Math.floor(mi / 2);
      if (mi % 2 === 0) {
        nb[ri + 1][nextMi].teamA = winner === "A" ? match.teamA : match.teamB;
        nb[ri + 1][nextMi].teamAName = winner === "A" ? match.teamAName : match.teamBName;
        nb[ri + 1][nextMi].teamAId = winner === "A" ? match.teamAId : match.teamBId;
      } else {
        nb[ri + 1][nextMi].teamB = winner === "A" ? match.teamA : match.teamB;
        nb[ri + 1][nextMi].teamBName = winner === "A" ? match.teamAName : match.teamBName;
        nb[ri + 1][nextMi].teamBId = winner === "A" ? match.teamAId : match.teamBId;
      }
    }
    setAdvanceHistory(prev => [...prev, { ri, mi }]);
    haptic("success");
    onUpdateTournament({ ...t, bracketRounds: nb });
  };

  const undoAdvance = () => {
    if (advanceHistory.length === 0 || !isAdmin) return;
    const { ri, mi } = advanceHistory[advanceHistory.length - 1];
    const nb = bracketRounds.map(r => r.map(m => ({ ...m })));
    const match = nb[ri][mi];
    if (!match.winner) { setAdvanceHistory(prev => prev.slice(0, -1)); return; }
    // Check that the next-round match hasn't been played
    if (ri + 1 < nb.length) {
      const nextMi = Math.floor(mi / 2);
      const nextMatch = nb[ri + 1][nextMi];
      if (nextMatch.winner) return; // can't undo — downstream already played
      if (mi % 2 === 0) {
        nextMatch.teamA = null; nextMatch.teamAName = null; nextMatch.teamAId = null;
        nextMatch.scoreA = null;
      } else {
        nextMatch.teamB = null; nextMatch.teamBName = null; nextMatch.teamBId = null;
        nextMatch.scoreB = null;
      }
    }
    nb[ri][mi].winner = null;
    setAdvanceHistory(prev => prev.slice(0, -1));
    haptic("medium");
    onUpdateTournament({ ...t, bracketRounds: nb });
  };

  const isTournamentComplete = bracketRounds.length > 0 && bracketRounds[bracketRounds.length - 1].every(m => m.winner);
  const champion = isTournamentComplete && bracketRounds[bracketRounds.length - 1][0];
  const championName = champion ? (champion.winner === "A" ? champion.teamAName : champion.teamBName) : null;
  const championPlayers = champion ? (champion.winner === "A" ? champion.teamA : champion.teamB) : null;

  const endTournament = () => {
    const allMatches = bracketRounds.flat().filter(m => m.teamA && m.teamB && m.scoreA !== null);
    const rounds = [{ courts: allMatches }];
    onEnd({ ...t, rounds, status: "finished", finishedAt: new Date().toISOString() });
  };

  const copySpectatorLink = () => {
    const url = `${window.location.origin}${window.location.pathname}?spectate=${t.id}`;
    navigator.clipboard.writeText(url).then(() => { setCopiedLink(true); setTimeout(() => setCopiedLink(false), 2000); }).catch(() => {});
  };

  // ── Bracket layout constants ──
  const MATCH_H = 88;
  const ADVANCE_H = 32;
  const MATCH_W = 220;
  const MATCH_GAP = 48;
  const ROUND_GAP = 36;
  const CONNECTOR_W = 28;
  const COL_W = MATCH_W + CONNECTOR_W + ROUND_GAP;

  const numRounds = bracketRounds.length;
  const firstRoundMatches = bracketRounds[0]?.length || 0;
  const totalH = firstRoundMatches * (MATCH_H + MATCH_GAP) - MATCH_GAP + 60;

  // Calculate Y position for each match (centered relative to children)
  const getMatchY = (ri, mi) => {
    if (ri === 0) return mi * (MATCH_H + MATCH_GAP) + 30;
    const childY1 = getMatchY(ri - 1, mi * 2);
    const childY2 = getMatchY(ri - 1, mi * 2 + 1);
    return (childY1 + childY2) / 2;
  };

  // ── Match card render ──
  const renderMatch = (m, ri, mi) => {
    const x = ri * COL_W;
    const y = getMatchY(ri, mi);
    const isBye = (!m.teamA && !m.teamB) || (m.winner && m.scoreA === 0 && m.scoreB === 0 && (!m.teamA || !m.teamB));
    const canScore = m.teamA && m.teamB && !m.winner;
    const canAdvance = m.scoreA !== null && m.scoreB !== null && !m.winner && m.teamA && m.teamB;
    const aW = m.winner === "A", bW = m.winner === "B";
    const isComplete = !!m.winner;
    const isActive = canScore || canAdvance;

    if (isBye) {
      return (
        <g key={`${ri}-${mi}`}>
          <rect x={x} y={y} width={MATCH_W} height={MATCH_H} rx={8} fill={C.surface} stroke={C.border} strokeWidth={1} opacity={0.4} />
          <text x={x + MATCH_W/2} y={y + MATCH_H/2 + 4} textAnchor="middle" fill={C.textMuted} fontSize={11}>BYE</text>
        </g>
      );
    }

    const teamALabel = m.teamA ? m.teamA.map(p => p.name).join(" & ") : "TBD";
    const teamBLabel = m.teamB ? m.teamB.map(p => p.name).join(" & ") : "TBD";
    const advanceH = canAdvance && isAdmin ? 32 : 0;

    return (
      <g key={`${ri}-${mi}`}>
        <foreignObject x={x} y={y} width={MATCH_W} height={MATCH_H + advanceH}>
          <div xmlns="http://www.w3.org/1999/xhtml" style={{ fontFamily: font, width: MATCH_W, height: "100%" }}>
            <div style={{ background: isActive ? C.surface : C.card, border: `${isActive ? 2 : 1}px solid ${isActive ? C.accent : isComplete ? C.accent + "66" : C.border}`, borderRadius: 8, overflow: "hidden", height: MATCH_H + advanceH, display: "flex", flexDirection: "column" }}>
              {/* Team A row */}
              <div style={{ display: "flex", alignItems: "center", padding: "0 10px", flex: 1, borderBottom: `1px solid ${C.border}33`, background: aW ? C.accentDim : "transparent", gap: 6 }}>
                <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                  <div style={{ fontSize: 12, fontWeight: aW ? 800 : m.teamA ? 700 : 400, color: aW ? C.accent : m.teamA ? C.text : C.textMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.teamAName || (m.teamA ? "Team" : "TBD")}</div>
                  {m.teamA && <div style={{ fontSize: 10, color: aW ? C.accent + "cc" : C.textMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginTop: 1 }}>{teamALabel}</div>}
                </div>
                {canScore && isAdmin ? (
                  <input type="number" inputMode="numeric" value={m.scoreA === null ? "" : m.scoreA} onChange={e => updateScore(ri, mi, "A", e.target.value === "" ? null : parseInt(e.target.value, 10))} style={{ width: 40, height: 28, border: `1px solid ${C.border}`, borderRadius: 4, textAlign: "center", fontSize: 15, fontWeight: 700, fontFamily: fontMono, background: C.bg, color: C.text, outline: "none", flexShrink: 0 }} placeholder="-" />
                ) : (
                  <span style={{ fontFamily: fontMono, fontWeight: 700, fontSize: 16, color: aW ? C.accent : m.scoreA != null ? C.text : C.textMuted, minWidth: 24, textAlign: "center", flexShrink: 0 }}>{m.scoreA ?? ""}</span>
                )}
              </div>
              {/* Team B row */}
              <div style={{ display: "flex", alignItems: "center", padding: "0 10px", flex: 1, background: bW ? C.accentDim : "transparent", gap: 6 }}>
                <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                  <div style={{ fontSize: 12, fontWeight: bW ? 800 : m.teamB ? 700 : 400, color: bW ? C.accent : m.teamB ? C.text : C.textMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.teamBName || (m.teamB ? "Team" : "TBD")}</div>
                  {m.teamB && <div style={{ fontSize: 10, color: bW ? C.accent + "cc" : C.textMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginTop: 1 }}>{teamBLabel}</div>}
                </div>
                {canScore && isAdmin ? (
                  <input type="number" inputMode="numeric" value={m.scoreB === null ? "" : m.scoreB} onChange={e => updateScore(ri, mi, "B", e.target.value === "" ? null : parseInt(e.target.value, 10))} style={{ width: 40, height: 28, border: `1px solid ${C.border}`, borderRadius: 4, textAlign: "center", fontSize: 15, fontWeight: 700, fontFamily: fontMono, background: C.bg, color: C.text, outline: "none", flexShrink: 0 }} placeholder="-" />
                ) : (
                  <span style={{ fontFamily: fontMono, fontWeight: 700, fontSize: 16, color: bW ? C.accent : m.scoreB != null ? C.text : C.textMuted, minWidth: 24, textAlign: "center", flexShrink: 0 }}>{m.scoreB ?? ""}</span>
                )}
              </div>
              {/* Advance button */}
              {canAdvance && isAdmin && (
                <button onClick={() => advanceWinner(ri, mi)} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, padding: "7px 0", background: C.accent, color: C.bg, border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: font, borderRadius: "0 0 6px 6px" }}><I.ChevRight size={12} /> Advance Winner</button>
              )}
            </div>
          </div>
        </foreignObject>
      </g>
    );
  };

  // ── Connector lines ──
  const renderConnectors = () => {
    const lines = [];
    for (let ri = 0; ri < numRounds - 1; ri++) {
      const round = bracketRounds[ri];
      for (let mi = 0; mi < round.length; mi += 2) {
        const m1 = round[mi], m2 = round[mi + 1];
        if (!m2) continue;
        const y1 = getMatchY(ri, mi) + MATCH_H / 2;
        const y2 = getMatchY(ri, mi + 1) + MATCH_H / 2;
        const x1 = ri * COL_W + MATCH_W;
        const xMid = x1 + CONNECTOR_W / 2 + ROUND_GAP / 2;
        const x2 = (ri + 1) * COL_W;
        const yMid = (y1 + y2) / 2;
        const isResolved = m1.winner && m2.winner;
        const lineColor = isResolved ? C.accent + "88" : C.border;
        lines.push(
          <g key={`conn-${ri}-${mi}`}>
            <line x1={x1} y1={y1} x2={xMid} y2={y1} stroke={lineColor} strokeWidth={1.5} />
            <line x1={x1} y1={y2} x2={xMid} y2={y2} stroke={lineColor} strokeWidth={1.5} />
            <line x1={xMid} y1={y1} x2={xMid} y2={y2} stroke={lineColor} strokeWidth={1.5} />
            <line x1={xMid} y1={yMid} x2={x2} y2={yMid} stroke={lineColor} strokeWidth={1.5} />
          </g>
        );
      }
    }
    return lines;
  };

  const svgW = numRounds * COL_W - ROUND_GAP - CONNECTOR_W + (championName ? 100 : 0);

  return (
    <div className="fade-in">
      <div style={{ padding: "16px 16px 8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>{t.name}</div>
            <DivisionBadge division={t.division} />
          </div>
          <div style={{ fontSize: 12, color: C.textSec, marginTop: 2 }}>Bracket · {t.players?.length} players · {(t.teams||[]).length} teams</div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button className="btn btn-outline btn-sm" onClick={copySpectatorLink} style={{ color: copiedLink ? C.accent : C.blue, borderColor: copiedLink ? C.accent : C.blue, fontSize: 12 }}>{copiedLink ? <><I.Check size={14} /> Copied!</> : <><I.Share size={14} /> Share Live</>}</button>
        </div>
      </div>

      {championName && (
        <div style={{ textAlign: "center", padding: "20px 16px", margin: "0 16px 12px", background: `linear-gradient(135deg, ${C.goldDim}, ${C.accentDim})`, borderRadius: 16, border: `1px solid rgba(255,215,0,0.15)` }}>
          <div style={{ fontSize: 36, marginBottom: 4 }}>🏆</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: C.gold }}>{championName}</div>
          {championPlayers && <div style={{ fontSize: 13, color: C.textSec }}>{championPlayers.map(p => p.name).join(" & ")}</div>}
        </div>
      )}

      {t.gameMinutes && <div style={{ padding: "0 16px" }}><GameTimer minutes={t.gameMinutes} timerState={t.timerState} onTimerUpdate={isAdmin ? (ts) => onUpdateTournament({ ...t, timerState: ts }) : null} isAdmin={isAdmin} /></div>}

      {/* Round labels */}
      <div style={{ overflowX: "scroll", padding: "4px 16px 0", maxWidth: "100vw" }}>
        <div style={{ display: "flex", width: svgW, padding: "0 0 4px" }}>
          {bracketRounds.map((_, ri) => (
            <div key={ri} style={{ width: COL_W, flexShrink: 0, fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              {getRoundLabel(ri, numRounds)}
            </div>
          ))}
        </div>
      </div>

      {/* Bracket SVG */}
      <div style={{ overflowX: "scroll", padding: "0 16px 120px", maxWidth: "100vw" }}>
        <svg width={svgW} height={totalH} style={{ display: "block" }}>
          {renderConnectors()}
          {/* Render completed/inactive matches first, active matches on top */}
          {bracketRounds.map((round, ri) => round.map((m, mi) => {
            const canScore = m.teamA && m.teamB && !m.winner;
            const canAdvance = m.scoreA !== null && m.scoreB !== null && !m.winner && m.teamA && m.teamB;
            if (canScore || canAdvance) return null;
            return renderMatch(m, ri, mi);
          }))}
          {bracketRounds.map((round, ri) => round.map((m, mi) => {
            const canScore = m.teamA && m.teamB && !m.winner;
            const canAdvance = m.scoreA !== null && m.scoreB !== null && !m.winner && m.teamA && m.teamB;
            if (!canScore && !canAdvance) return null;
            return renderMatch(m, ri, mi);
          }))}
          {/* Champion connector */}
          {championName && (() => {
            const finalY = getMatchY(numRounds - 1, 0) + MATCH_H / 2;
            const finalX = (numRounds - 1) * COL_W + MATCH_W;
            return (
              <g>
                <line x1={finalX} y1={finalY} x2={finalX + 30} y2={finalY} stroke={C.gold} strokeWidth={2} />
                <text x={finalX + 38} y={finalY - 6} fill={C.gold} fontSize={20}>🏆</text>
                <text x={finalX + 38} y={finalY + 12} fill={C.gold} fontSize={11} fontWeight="800" fontFamily={font}>{championName}</text>
              </g>
            );
          })()}
        </svg>
      </div>

      {isAdmin && (
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: C.surface, borderTop: `1px solid ${C.border}`, display: "flex", gap: 10, padding: "12px 16px", justifyContent: "center", zIndex: 200 }}>
          {isTournamentComplete ? (
            <button className="btn btn-primary btn-full" onClick={endTournament} style={{ padding: 14 }}><I.Trophy size={16} /> Save & Close Tournament</button>
          ) : (
            <>
              <button className="btn btn-danger" onClick={() => setConfirmEnd(true)} style={{ flex: 0.5 }}><I.Stop size={14} /> End</button>
              <button className="btn btn-outline" onClick={() => onPause && onPause(t)} style={{ flex: 0.5, color: C.orange, borderColor: C.orange, fontSize: 12 }}><I.PauseCircle size={14} /> Pause</button>
              {advanceHistory.length > 0 && (
                <button className="btn btn-outline" onClick={undoAdvance} style={{ flex: 0.6, fontSize: 12 }}><I.Undo size={14} /> Undo ({advanceHistory.length})</button>
              )}
            </>
          )}
        </div>
      )}
      {confirmEnd && (
        <Modal onClose={() => setConfirmEnd(false)}>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: C.text }}>End Bracket Early?</div>
          <p style={{ color: C.orange, fontSize: 13, marginBottom: 20 }}>Tournament will close with results as they stand.</p>
          <div style={{ display: "flex", gap: 10 }}>
            <button className="btn btn-outline" onClick={() => setConfirmEnd(false)} style={{ flex: 1 }}>Cancel</button>
            <button className="btn" onClick={endTournament} style={{ flex: 1, background: C.red, color: "#fff" }}>End Tournament</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// VIEW FINISHED TOURNAMENT
// ════════════════════════════════════════════════════════════════════
function ViewTournament({ tournament, onBack, isAdmin = false, isSuperAdmin = false, onUpdate }) {
  const isPBR = tournament.format === "rrplayer";
  const board = isPBR ? [] : calcLeaderboard(tournament.rounds || []);
  const isTeamFormat = tournament.format === "bracket" || tournament.format === "roundrobin";
  const [copied, setCopied] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(tournament.name || "");
  const saveNameEdit = () => { if (nameInput.trim() && nameInput.trim() !== tournament.name) { onUpdate && onUpdate({ ...tournament, name: nameInput.trim() }); } setEditingName(false); };

  // PBR standings calc
  const calcPBRStandings = () => {
    const allPlayers = tournament.players || [];
    const stats = {};
    allPlayers.forEach(p => { stats[p.id] = { id: p.id, name: p.name, wins: 0, losses: 0, pf: 0, pa: 0, gp: 0 }; });
    (tournament.rounds || []).forEach(r => {
      if (!r.locked) return;
      (r.courts || []).forEach(c => {
        const scoreA = Number(c.scoreA), scoreB = Number(c.scoreB);
        if (isNaN(scoreA) || isNaN(scoreB)) return;
        const aWon = scoreA > scoreB;
        [...(c.teamA || [])].forEach(p => { if (!p || !stats[p.id]) return; stats[p.id].gp++; stats[p.id].pf += scoreA; stats[p.id].pa += scoreB; if (aWon) stats[p.id].wins++; else stats[p.id].losses++; });
        [...(c.teamB || [])].forEach(p => { if (!p || !stats[p.id]) return; stats[p.id].gp++; stats[p.id].pf += scoreB; stats[p.id].pa += scoreA; if (!aWon) stats[p.id].wins++; else stats[p.id].losses++; });
      });
    });
    return Object.values(stats).sort((a, b) => {
      const wpA = a.gp ? a.wins / a.gp : 0, wpB = b.gp ? b.wins / b.gp : 0;
      if (wpB !== wpA) return wpB - wpA;
      return (b.pf - b.pa) - (a.pf - a.pa);
    });
  };
  const pbrStandings = isPBR ? calcPBRStandings() : [];
  const pbrWinner = pbrStandings[0] || null;

  // For bracket: champion is who won the final match
  let teamWinner = null;
  if (tournament.format === "bracket" && tournament.bracketRounds) {
    const br = tournament.bracketRounds;
    const finalRound = br[br.length - 1];
    const finalMatch = finalRound && finalRound[0];
    if (finalMatch && finalMatch.winner) {
      const side = finalMatch.winner;
      teamWinner = { name: side === "A" ? finalMatch.teamAName : finalMatch.teamBName, players: side === "A" ? finalMatch.teamA : finalMatch.teamB };
    }
  }
  // For RR: build team standings, top team wins
  if (tournament.format === "roundrobin" && tournament.teams) {
    const stats = {};
    (tournament.teams || []).forEach(tm => { stats[tm.id] = { ...tm, wins: 0, losses: 0, diff: 0, pts: 0 }; });
    (tournament.rounds || []).forEach(r => {
      (r.courts || []).forEach(m => {
        if (m.scoreA == null || m.scoreB == null) return;
        if (stats[m.teamAId]) { stats[m.teamAId].pts += m.scoreA||0; stats[m.teamAId].diff += (m.scoreA||0) - (m.scoreB||0); if (m.scoreA > m.scoreB) stats[m.teamAId].wins++; else if (m.scoreB > m.scoreA) stats[m.teamAId].losses++; }
        if (stats[m.teamBId]) { stats[m.teamBId].pts += m.scoreB||0; stats[m.teamBId].diff += (m.scoreB||0) - (m.scoreA||0); if (m.scoreB > m.scoreA) stats[m.teamBId].wins++; else if (m.scoreA > m.scoreB) stats[m.teamBId].losses++; }
      });
    });
    const sorted = Object.values(stats).sort((a, b) => b.wins - a.wins || b.diff - a.diff || b.pts - a.pts);
    if (sorted.length > 0) teamWinner = sorted[0];
  }

  const indivWinner = board[0];

  const generateShareText = () => {
    const lines = [];
    const divider = "─────────────────────";
    lines.push("🏓 DINKS IN PARADISE RESULTS");
    lines.push(divider);
    lines.push("");
    lines.push(`📋 ${tournament.name}`);
    const fmt = fmtLabel(tournament.format);
    lines.push(`${fmt} · ${(tournament.rounds||[]).length} rounds · ${(tournament.players||[]).length} players`);
    lines.push("");
    if (isTeamFormat && teamWinner) {
      lines.push("🏆 CHAMPION");
      lines.push(`   ${teamWinner.name}`);
      if (teamWinner.players) lines.push(`   ${teamWinner.players.map(p => p.name).join(" & ")}`);
      if (teamWinner.wins != null) lines.push(`   ${teamWinner.wins}W-${teamWinner.losses}L · ${teamWinner.diff > 0 ? "+" : ""}${teamWinner.diff} diff`);
    } else if (indivWinner) {
      lines.push("🏆 CHAMPION");
      lines.push(`   ${indivWinner.name}`);
      lines.push(`   ${indivWinner.pts} pts · ${indivWinner.wins}W-${indivWinner.losses}L`);
    }
    lines.push("");
    lines.push(divider);
    lines.push("📊 FINAL STANDINGS");
    lines.push(divider);
    board.slice(0, 10).forEach((p, i) => {
      const medal = i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `  ${i + 1}.`;
      lines.push(`${medal} ${p.name}`);
      lines.push(`     ${p.wins}W-${p.losses}L · ${p.pts}pts · ${p.diff > 0 ? "+" : ""}${p.diff} diff`);
    });
    lines.push("");
    lines.push(divider);
    lines.push(`Full results: ${window.location.origin}${window.location.pathname}?spectate=${tournament.id}`);
    return lines.join("\n");
  };

  const handleShare = async () => {
    const text = generateShareText();
    if (navigator.share) {
      try { await navigator.share({ text }); return; } catch {}
    }
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
  };

  const handleEditScore = (ri, ci, team, val) => {
    if (!onUpdate) return;
    const nr = (tournament.rounds || []).map((r, i) => i !== ri ? r : { ...r, courts: (r.courts||[]).map((c, j) => j !== ci ? c : { ...c, [team === "A" ? "scoreA" : "scoreB"]: val }) });
    onUpdate({ ...tournament, rounds: nr });
  };

  const handleExportImage = () => {
    exportResultsImage({ name: tournament.name, format: tournament.format, rounds: tournament.rounds, players: tournament.players, finishedAt: tournament.finishedAt, board, isTeamFormat, teamWinner, indivWinner });
  };

  return (
    <div className="fade-in">
      <div style={{ padding: "12px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={onBack}><I.ChevLeft size={18} /></button>
          <div style={{ flex: 1 }}>
            {editingName ? (
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input className="input" value={nameInput} onChange={e => setNameInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter") saveNameEdit(); if (e.key === "Escape") setEditingName(false); }} autoFocus style={{ fontSize: 15, padding: "6px 10px", flex: 1 }} />
                <button className="btn btn-primary btn-sm" onClick={saveNameEdit}><I.Check size={14} /></button>
                <button className="btn btn-ghost btn-sm" onClick={() => setEditingName(false)}><I.X size={14} /></button>
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>{tournament.name}</div>
                <DivisionBadge division={tournament.division || "mens"} />
                {isMomEra(tournament) && <MomEraBadge />}
                {isMomClassic(tournament) && <img src="/mom-logo.png" alt="Men of Margaritaville" style={{ height: 26, width: "auto" }} />}
                {isSuperAdmin && <button className="btn btn-ghost btn-sm" onClick={() => { setNameInput(tournament.name); setEditingName(true); }} style={{ padding: 2, color: C.textMuted }}><I.Edit size={13} /></button>}
              </div>
            )}
            <div style={{ fontSize: 12, color: C.textSec }}>{(tournament.rounds||[]).length} rounds · {(tournament.players||[]).length} players</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, paddingLeft: 34 }}>
          <button className="btn btn-outline btn-sm" onClick={handleExportImage} style={{ fontSize: 12, color: C.blue, borderColor: C.blue }}>
            <I.Download size={14} /> Export Image
          </button>
          <button className="btn btn-outline btn-sm" onClick={handleShare} style={{ fontSize: 12, color: copied ? C.accent : C.textSec, borderColor: copied ? C.accent : C.border }}>
            {copied ? <><I.Check size={14} /> Copied!</> : <><I.Share size={14} /> Share Text</>}
          </button>
        </div>
      </div>
      {isTeamFormat && teamWinner && (
        <div style={{ textAlign: "center", padding: "24px 16px", margin: "0 16px 16px", background: `linear-gradient(135deg, ${C.goldDim}, ${C.accentDim})`, borderRadius: 16, border: `1px solid rgba(255,215,0,0.15)` }}>
          <div style={{ fontSize: 44, marginBottom: 6 }}>🏆</div>
          <div style={{ fontSize: 24, fontWeight: 900, color: C.gold }}>{teamWinner.name}</div>
          {teamWinner.players && <div style={{ fontSize: 15, color: C.text, marginTop: 4 }}>{teamWinner.players.map(p => p.name).join(" & ")}</div>}
          {teamWinner.wins != null && <div style={{ fontSize: 13, color: C.textSec, marginTop: 2 }}>{teamWinner.wins}W-{teamWinner.losses}L · {teamWinner.diff > 0 ? "+" : ""}{teamWinner.diff} diff</div>}
        </div>
      )}
      {!isTeamFormat && !isPBR && indivWinner && (
        <div style={{ textAlign: "center", padding: "24px 16px", margin: "0 16px 16px", background: `linear-gradient(135deg, ${C.goldDim}, ${C.accentDim})`, borderRadius: 16, border: `1px solid rgba(255,215,0,0.15)` }}>
          <div style={{ fontSize: 44, marginBottom: 6 }}>🏆</div>
          <div style={{ fontSize: 24, fontWeight: 900, color: C.gold }}>{indivWinner.name}</div>
          <div style={{ fontSize: 14, color: C.textSec }}>Champion · {indivWinner.pts} pts · {indivWinner.wins}W-{indivWinner.losses}L</div>
        </div>
      )}
      {isPBR && pbrWinner && (
        <div style={{ textAlign: "center", padding: "24px 16px", margin: "0 16px 16px", background: `linear-gradient(135deg, ${C.goldDim}, ${C.accentDim})`, borderRadius: 16, border: `1px solid rgba(255,215,0,0.15)` }}>
          <div style={{ fontSize: 44, marginBottom: 6 }}>🏆</div>
          <div style={{ fontSize: 24, fontWeight: 900, color: C.gold }}>{pbrWinner.name}</div>
          <div style={{ fontSize: 14, color: C.textSec }}>Champion · {pbrWinner.gp ? ((pbrWinner.wins / pbrWinner.gp) * 100).toFixed(0) : 0}% Win · {pbrWinner.wins}W-{pbrWinner.losses}L</div>
        </div>
      )}
      {isPBR ? (
        <div style={{ padding: "0 16px 16px" }}>
          <div className="section-title">Final Standings</div>
          <div style={{ display: "flex", padding: "0 12px 8px", fontSize: 10, fontWeight: 700, color: C.textMuted, letterSpacing: "0.08em" }}>
            <span style={{ width: 28 }}>#</span>
            <span style={{ flex: 1 }}>PLAYER</span>
            <span style={{ width: 48, textAlign: "center" }}>WIN%</span>
            <span style={{ width: 36, textAlign: "center" }}>W</span>
            <span style={{ width: 36, textAlign: "center" }}>L</span>
            <span style={{ width: 44, textAlign: "center" }}>+/-</span>
          </div>
          {pbrStandings.map((p, i) => {
            const wp = p.gp ? ((p.wins / p.gp) * 100).toFixed(0) : "-";
            const diff = p.pf - p.pa;
            const rankClass = i === 0 ? "rank-1" : i === 1 ? "rank-2" : i === 2 ? "rank-3" : "rank-n";
            return (
              <div key={p.id} className={`card ${rankClass}`} style={{ padding: "10px 12px", marginBottom: 4, display: "flex", alignItems: "center" }}>
                <span style={{ width: 28, fontWeight: 900, fontFamily: fontMono, fontSize: i < 3 ? 15 : 13 }}>{i < 3 ? ["🥇", "🥈", "🥉"][i] : i + 1}</span>
                <span style={{ flex: 1, fontWeight: 600, fontSize: 14 }}>{p.name}</span>
                <span style={{ width: 48, textAlign: "center", fontWeight: 700, fontSize: 13, color: C.accent }}>{wp}{p.gp ? "%" : ""}</span>
                <span style={{ width: 36, textAlign: "center", fontSize: 13, color: C.green }}>{p.wins}</span>
                <span style={{ width: 36, textAlign: "center", fontSize: 13, color: C.red }}>{p.losses}</span>
                <span style={{ width: 44, textAlign: "center", fontSize: 13, color: diff >= 0 ? C.green : C.red }}>{diff > 0 ? "+" : ""}{diff}</span>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ padding: "0 16px 16px" }}><div className="section-title">Final Standings</div><LeaderboardTable rounds={tournament.rounds || []} /></div>
      )}
      <div style={{ padding: "0 16px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <div className="section-title" style={{ margin: 0 }}>Round History</div>
          {isAdmin && <button className="btn btn-ghost btn-sm" onClick={() => setEditMode(!editMode)} style={{ fontSize: 12, color: editMode ? C.accent : C.textMuted }}><I.Edit size={12} /> {editMode ? "Done" : "Edit Scores"}</button>}
        </div>
        <RoundHistoryList rounds={tournament.rounds || []} editable={editMode} onEditScore={handleEditScore} />
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// TOURNAMENTS TAB
// ════════════════════════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════════════
// IMPORT TOURNAMENT (from xlsx template)
// ════════════════════════════════════════════════════════════════════
function ImportTournament({ existingPlayers, onImport, onClose }) {
  const [parsed, setParsed] = useState(null);
  const [error, setError] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = React.createRef();

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setError(null);
    setParsed(null);
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const XLSX = window.XLSX;
        if (!XLSX) { setError("XLSX library not loaded. Refresh and try again."); return; }
        const wb = XLSX.read(evt.target.result, { type: "array" });

        // Parse Tournament Info
        const infoSheet = wb.Sheets["Tournament Info"];
        let tName = "", tDate = "";
        if (infoSheet) {
          tName = (infoSheet["B1"] && infoSheet["B1"].v) || "";
          const dc = infoSheet["B2"];
          if (dc) {
            if (dc.t === "d") tDate = dc.v.toISOString();
            else if (dc.t === "n") { const d = new Date((dc.v - 25569) * 86400000); tDate = d.toISOString(); }
            else if (dc.w) tDate = new Date(dc.w).toISOString();
            else tDate = String(dc.v || "");
          }
        }

        // Parse Rounds
        const roundsSheet = wb.Sheets["Rounds"];
        if (!roundsSheet) { setError("No 'Rounds' sheet found."); return; }
        const rows = XLSX.utils.sheet_to_json(roundsSheet, { defval: "" });
        if (!rows.length) { setError("Rounds sheet is empty."); return; }

        const col = (row, names) => { for (const n of names) { if (row[n] !== undefined && row[n] !== "") return row[n]; } return null; };

        const roundsMap = {};
        const allNames = new Set();
        const parseErrors = [];

        rows.forEach((row, ri) => {
          const round = parseInt(col(row, ["Round", "round", "ROUND"]), 10);
          const court = parseInt(col(row, ["Court", "court", "COURT"]), 10);
          const p1 = String(col(row, ["Player 1 (Team A)", "Player 1", "P1"]) || "").trim();
          const p2 = String(col(row, ["Player 2 (Team A)", "Player 2", "P2"]) || "").trim();
          const p3 = String(col(row, ["Player 3 (Team B)", "Player 3", "P3"]) || "").trim();
          const p4 = String(col(row, ["Player 4 (Team B)", "Player 4", "P4"]) || "").trim();
          const scoreA = parseInt(col(row, ["Score A", "ScoreA", "scoreA"]), 10);
          const scoreB = parseInt(col(row, ["Score B", "ScoreB", "scoreB"]), 10);

          if (!round && !p1 && !p2 && !p3 && !p4) return;
          if (isNaN(round)) { parseErrors.push(`Row ${ri + 2}: invalid round`); return; }
          if (!p1 || !p2 || !p3 || !p4) { parseErrors.push(`Row ${ri + 2}: missing player name`); return; }
          if (isNaN(scoreA) || isNaN(scoreB)) { parseErrors.push(`Row ${ri + 2}: missing score`); return; }

          [p1, p2, p3, p4].forEach(n => allNames.add(n));
          if (!roundsMap[round]) roundsMap[round] = [];
          roundsMap[round].push({ court: court || roundsMap[round].length + 1, p1, p2, p3, p4, scoreA, scoreB });
        });

        if (Object.keys(roundsMap).length === 0) {
          setError("No valid rows found." + (parseErrors.length ? "\n" + parseErrors.slice(0, 5).join("\n") : ""));
          return;
        }

        // Match names to existing players or create new
        const nameToPlayer = {};
        const newNames = [];
        const existingNames = [];
        allNames.forEach(name => {
          const match = existingPlayers.find(p => p.name.toLowerCase().trim() === name.toLowerCase().trim());
          if (match) {
            nameToPlayer[name] = { id: match.id, name: match.name, ranking: match.ranking || null };
            existingNames.push(name);
          } else {
            const newId = "p" + Math.random().toString(36).slice(2, 10);
            nameToPlayer[name] = { id: newId, name };
            newNames.push(name);
          }
        });

        // Build rounds array
        const roundNums = Object.keys(roundsMap).map(Number).sort((a, b) => a - b);
        const rounds = roundNums.map(rn => ({
          roundNumber: rn,
          courts: roundsMap[rn].sort((a, b) => a.court - b.court).map(c => ({
            courtNumber: c.court,
            teamA: [nameToPlayer[c.p1], nameToPlayer[c.p2]],
            teamB: [nameToPlayer[c.p3], nameToPlayer[c.p4]],
            scoreA: c.scoreA, scoreB: c.scoreB,
          })),
        }));

        setParsed({
          name: tName || f.name.replace(/\.xlsx?$/i, ""),
          date: tDate,
          rounds,
          players: Object.values(nameToPlayer),
          newNames, existingNames, parseErrors,
          totalMatches: rounds.reduce((s, r) => s + r.courts.length, 0),
          totalRounds: rounds.length,
        });
      } catch (err) { setError("Parse error: " + err.message); }
    };
    reader.readAsArrayBuffer(f);
  };

  const doImport = async () => {
    if (!parsed) return;
    setImporting(true);
    try {
      const tId = "imp_" + Math.random().toString(36).slice(2, 10);
      const dateStr = parsed.date && !isNaN(new Date(parsed.date).getTime()) ? new Date(parsed.date).toISOString() : new Date().toISOString();
      await onImport({
        id: tId,
        name: parsed.name,
        format: "kingofhill",
        status: "finished",
        players: parsed.players,
        rounds: parsed.rounds,
        createdAt: dateStr,
        finishedAt: dateStr,
      }, parsed.players.filter(p => parsed.newNames.includes(p.name)));
      onClose();
    } catch (err) { setError("Import failed: " + err.message); setImporting(false); }
  };

  return (
    <Modal onClose={onClose}>
      <ModalHeader title="Import Tournament" onClose={onClose} />
      <div style={{ fontSize: 13, color: C.textSec, marginBottom: 14 }}>Upload a filled-in .xlsx from the Dinks In Paradise import template. New player names will be created automatically.</div>

      <label className="btn btn-outline btn-full" style={{ marginBottom: 14, cursor: "pointer", textAlign: "center" }}>
        <I.Plus size={14} /> Choose .xlsx File
        <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleFile} style={{ display: "none" }} />
      </label>

      {error && <div style={{ padding: "10px 12px", background: C.redDim, borderRadius: 10, fontSize: 13, color: C.red, marginBottom: 14, whiteSpace: "pre-wrap" }}>{error}</div>}

      {parsed && (
        <div className="fade-in">
          <div className="card" style={{ marginBottom: 14 }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: C.text, marginBottom: 6 }}>{parsed.name}</div>
            {parsed.date && <div style={{ fontSize: 12, color: C.textSec, marginBottom: 6 }}>{new Date(parsed.date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</div>}
            <div style={{ fontSize: 13, color: C.textSec }}>
              {parsed.totalRounds} rounds · {parsed.totalMatches} matches · {parsed.players.length} players
            </div>
          </div>

          {parsed.newNames.length > 0 && (
            <div style={{ padding: "10px 12px", background: C.accentDim, borderRadius: 10, marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.accent, marginBottom: 4 }}>New players to create ({parsed.newNames.length}):</div>
              <div style={{ fontSize: 13, color: C.text }}>{parsed.newNames.join(", ")}</div>
            </div>
          )}

          {parsed.existingNames.length > 0 && (
            <div style={{ padding: "10px 12px", background: C.surfaceHover, borderRadius: 10, marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.textSec, marginBottom: 4 }}>Matched existing ({parsed.existingNames.length}):</div>
              <div style={{ fontSize: 13, color: C.textSec }}>{parsed.existingNames.join(", ")}</div>
            </div>
          )}

          {parsed.parseErrors.length > 0 && (
            <div style={{ padding: "10px 12px", background: C.orangeDim, borderRadius: 10, marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.orange, marginBottom: 4 }}>Warnings ({parsed.parseErrors.length}):</div>
              <div style={{ fontSize: 12, color: C.orange }}>{parsed.parseErrors.slice(0, 5).join(" · ")}{parsed.parseErrors.length > 5 ? ` · +${parsed.parseErrors.length - 5} more` : ""}</div>
            </div>
          )}

          {/* Preview first 3 rounds */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Preview</div>
            {parsed.rounds.slice(0, 3).map((r, ri) => (
              <div key={ri} style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, marginBottom: 4 }}>Round {r.roundNumber}</div>
                {r.courts.map((c, ci) => {
                  const aW = c.scoreA > c.scoreB, bW = c.scoreB > c.scoreA;
                  return (
                    <div key={ci} style={{ padding: "6px 10px", background: C.card, borderRadius: 8, marginBottom: 3, fontSize: 12, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div>
                        <span style={{ fontWeight: aW ? 700 : 400, color: aW ? C.accent : C.text }}>{c.teamA.map(p => p.name).join(" & ")}</span>
                        <span style={{ color: C.textMuted, margin: "0 6px" }}>vs</span>
                        <span style={{ fontWeight: bW ? 700 : 400, color: bW ? C.accent : C.text }}>{c.teamB.map(p => p.name).join(" & ")}</span>
                      </div>
                      <span style={{ fontFamily: fontMono, fontWeight: 700, fontSize: 13 }}>
                        <span style={{ color: aW ? C.accent : C.textMuted }}>{c.scoreA}</span>
                        <span style={{ color: C.textMuted }}> - </span>
                        <span style={{ color: bW ? C.accent : C.textMuted }}>{c.scoreB}</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            ))}
            {parsed.rounds.length > 3 && <div style={{ fontSize: 12, color: C.textMuted }}>+ {parsed.rounds.length - 3} more rounds...</div>}
          </div>

          <button className="btn btn-primary btn-full" onClick={doImport} disabled={importing} style={{ padding: 14, fontSize: 15 }}>
            {importing ? "Importing..." : <><I.Plus size={16} /> Import Tournament{parsed.newNames.length > 0 ? ` + ${parsed.newNames.length} New Players` : ""}</>}
          </button>
        </div>
      )}
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════════
// ABOUT — Games in Paradise umbrella + Dinks In Paradise
// ════════════════════════════════════════════════════════════════════
function AboutPage({ onClose }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: C.bg, zIndex: 200, overflowY: "auto" }} className="fade-in">
      <div style={{ maxWidth: 520, margin: "0 auto", padding: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>About</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><I.X size={18} /></button>
        </div>

        <div style={{ textAlign: "center", padding: "8px 0 4px" }}>
          <img src="/gip-logo.png" alt="Games In Paradise" style={{ maxWidth: 200, width: "100%", height: "auto" }} />
        </div>
        <div className="card" style={{ padding: 16, marginBottom: 20 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 6 }}>Games in Paradise</div>
          <div style={{ fontSize: 13.5, color: C.textSec, lineHeight: 1.6 }}>
            The umbrella for Watersound's game communities — one home for every sport and social game
            our neighbors compete in, from pickleball to cornhole to whatever we dream up next.
            Find it all at <a href={GIP_URL} style={{ color: C.accent, textDecoration: "none" }}>gamesinparadise.com</a>.
          </div>
        </div>

        <div style={{ textAlign: "center", padding: "4px 0" }}>
          <img src="/dip-logo.png" alt="Dinks In Paradise" style={{ maxWidth: 200, width: "100%", height: "auto" }} />
        </div>
        <div className="card" style={{ padding: 16, marginBottom: 20 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 6 }}>Dinks in Paradise</div>
          <div style={{ fontSize: 13.5, color: C.textSec, lineHeight: 1.6 }}>
            The pickleball arm of Games in Paradise — and the app you're using right now.
            Men's, Women's, and Mixed Doubles tournaments on our ten courts: King of the Hill,
            Round Robin, PBR, and Brackets, with live scoring, player ratings, and leaderboards.
            <span style={{ color: C.accent }}> dinksinparadise.com</span>
          </div>
        </div>

        <div style={{ textAlign: "center", padding: "4px 0" }}>
          <img src="/mom-logo.png" alt="Men of Margaritaville — Watersound" style={{ maxWidth: 170, width: "85%", height: "auto" }} />
        </div>
        <div className="card" style={{ padding: 16, marginBottom: 20 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 6 }}>Our Story</div>
          <div style={{ fontSize: 13.5, color: C.textSec, lineHeight: 1.6 }}>
            It all started as <b style={{ color: C.text }}>Men of Margaritaville</b> — a handful of neighbors,
            ten courts, and impromptu King of the Hill Saturdays in Watersound. The parrot stayed;
            the name grew up. Men's tournaments from those days wear the <b style={{ color: C.text }}>MoM Era</b> badge,
            the founders and original members carry the MoM mark on their profiles, and once a year
            the old banner flies again for the <b style={{ color: C.text }}>MoM Classic</b>.
          </div>
        </div>

        <div style={{ textAlign: "center", fontSize: 11, color: C.textMuted, paddingBottom: 32 }}>
          Watersound, FL · v{APP_VERSION}
        </div>
      </div>
    </div>
  );
}

function TournamentsTab({ tournaments, onResume, onView, onBuild, onDelete, onImport, isAdmin, isSuperAdmin, canManageAny, canDelete, canEdit, onEditTournament, currentUserId }) {
  const [divFilter, setDivFilter] = useState("all");
  const [showManage, setShowManage] = useState(false);
  const byDiv = (t) => divFilter === "all" || (t.division || "mens") === divFilter;
  const active = tournaments.filter(t => t.status === "playing" && byDiv(t));
  const paused = tournaments.filter(t => t.status === "paused" && byDiv(t));
  const registration = tournaments.filter(t => t.status === "registration" && byDiv(t));
  const finished = tournaments.filter(t => t.status === "finished" && byDiv(t)).sort((a, b) => new Date(b.finishedAt) - new Date(a.finishedAt));
  const [showPast, setShowPast] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [copiedRegLink, setCopiedRegLink] = useState(null);
  const copyRegLink = (t, e) => {
    e.stopPropagation();
    const url = `${window.location.origin}${window.location.pathname}?register=${t.id}`;
    navigator.clipboard.writeText(url).then(() => { setCopiedRegLink(t.id); setTimeout(() => setCopiedRegLink(null), 2000); }).catch(() => {});
  };
  const renderFinishedCard = (t, i) => {
    const board = calcLeaderboard(t.rounds || []);
    const isTeam = t.format === "bracket" || t.format === "roundrobin";
    let podium = [];
    if (isTeam && t.format === "bracket" && t.bracketRounds) {
      // Single elimination decides 1st and 2nd only
      const br = t.bracketRounds;
      const fm = br[br.length - 1]?.[0];
      if (fm?.winner) {
        const s = fm.winner;
        podium = [s === "A" ? fm.teamAName : fm.teamBName, s === "A" ? fm.teamBName : fm.teamAName].filter(Boolean);
      }
    } else if (isTeam && t.format === "roundrobin" && t.teams) {
      const stats = {};
      (t.teams||[]).forEach(tm => { stats[tm.id] = { ...tm, wins: 0, diff: 0 }; });
      (t.rounds||[]).forEach(r => { (r.courts||[]).forEach(m => {
        if (m.scoreA == null || m.scoreB == null) return;
        if (stats[m.teamAId]) { stats[m.teamAId].diff += (m.scoreA||0)-(m.scoreB||0); if (m.scoreA>m.scoreB) stats[m.teamAId].wins++; }
        if (stats[m.teamBId]) { stats[m.teamBId].diff += (m.scoreB||0)-(m.scoreA||0); if (m.scoreB>m.scoreA) stats[m.teamBId].wins++; }
      }); });
      podium = Object.values(stats).sort((a,b) => b.wins-a.wins || b.diff-a.diff).slice(0, 3).map(s => s.name);
    } else {
      podium = board.slice(0, 3).map(b => b.name);
    }
    const medals = ["🥇", "🥈", "🥉"];
    return (
      <div key={t.id} className="card fade-in" style={{ marginBottom: 8, animationDelay: `${i*40}ms` }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={() => onView(t)}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              {isMomClassic(t) && <img src="/mom-logo.png" alt="Men of Margaritaville" style={{ height: 30, width: "auto" }} />}
              <span style={{ fontWeight: 700, fontSize: 15, color: C.text }}>{t.name}</span>
              <DivisionBadge division={t.division} />
              {isMomEra(t) && <MomEraBadge />}
            </div>
            <div style={{ fontSize: 12, color: C.textSec, marginTop: 2 }}>{(t.rounds||[]).length} rds · {(t.players||[]).length} players</div>
            {podium.length > 0 && (
              <div style={{ fontSize: 12, color: C.textSec, marginTop: 2, display: "flex", flexWrap: "wrap", columnGap: 10, rowGap: 2 }}>
                {podium.map((name, pi) => <span key={pi} style={{ whiteSpace: "nowrap" }}>{medals[pi]} {name}</span>)}
              </div>
            )}
            <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>{t.format === "roundrobin" ? "Round Robin" : t.format === "bracket" ? "Bracket" : "King of the Hill"}{t.finishedAt ? " · " + new Date(t.finishedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{ cursor: "pointer" }} onClick={() => onView(t)}><I.ChevRight size={18} style={{ color: C.textMuted }} /></div>
          </div>
        </div>
      </div>
    );
  };
  return (
    <div style={{ padding: 16 }} className="fade-in">
      <div style={{ display: "flex", gap: 4, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        {[["all", "All"], ["mens", "Men's"], ["womens", "Women's"], ["mixed", "Mixed"]].map(([key, label]) => (
          <button key={key} className={`btn btn-sm ${divFilter === key ? "btn-primary" : "btn-outline"}`} onClick={() => setDivFilter(key)} style={{ fontSize: 11, padding: "4px 10px" }}>{label}</button>
        ))}
        {canManageAny && <button className="btn btn-ghost btn-sm" onClick={() => setShowManage(true)} title="Manage tournaments" style={{ marginLeft: "auto", color: C.textSec }}><I.Settings size={15} /></button>}
      </div>
      {showManage && <ManageTournamentsScreen tournaments={tournaments} canDelete={canDelete} canEdit={canEdit} onEdit={onEditTournament} currentUserId={currentUserId} onDelete={onDelete} onClose={() => setShowManage(false)} />}
      {isAdmin && (
        <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
          <button className="btn btn-primary" onClick={onBuild} style={{ padding: 14, fontSize: 16, flex: 1 }}><I.Hammer size={18} /> Build Tournament</button>
          <button className="btn btn-outline" onClick={() => setShowImport(true)} style={{ padding: 14, fontSize: 13 }}><I.Plus size={14} /> Import</button>
        </div>
      )}
      {active.length > 0 && (
        <>
          <div className="section-title">Active Tournament</div>
          {active.map(t => (
            <div key={t.id} className="card" style={{ marginBottom: 10, cursor: "pointer", borderColor: C.accent, background: C.accentDim }} onClick={() => onResume(t)}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontWeight: 700, fontSize: 16, color: C.text }}>{t.name}</span>
                    <DivisionBadge division={t.division} />
                  </div>
                  <div style={{ fontSize: 12, color: C.textSec, marginTop: 2 }}>Round {t.currentRound || ((t.currentMatchRound || 0) + 1)} · {fmtLabel(t.format)}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  {t.preRegistration?.enabled && isAdmin && (
                    <button className="btn btn-ghost btn-sm" onClick={(e) => copyRegLink(t, e)} style={{ fontSize: 11, color: copiedRegLink === t.id ? C.accent : C.blue }}>{copiedRegLink === t.id ? "Copied!" : "Reg Link"}</button>
                  )}
                  <span className="badge live-badge">LIVE</span>
                </div>
              </div>
              {t.preRegistration?.enabled && <div style={{ fontSize: 11, color: C.textSec, marginTop: 4 }}>{(t.preRegistration.registrants||[]).length}/{t.preRegistration.maxPlayers} registered{(t.preRegistration.waitlist||[]).length > 0 && ` · ${(t.preRegistration.waitlist||[]).length} waitlisted`}</div>}
            </div>
          ))}
        </>
      )}
      {paused.length > 0 && (
        <>
          <div className="section-title" style={{ marginTop: active.length > 0 ? 16 : 0 }}>Paused</div>
          {paused.map(t => (
            <div key={t.id} className="card" style={{ marginBottom: 10, cursor: "pointer", borderColor: C.orange, background: C.orangeDim }} onClick={() => onResume(t)}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontWeight: 700, fontSize: 16, color: C.text }}>{t.name}</span>
                    <DivisionBadge division={t.division} />
                  </div>
                  <div style={{ fontSize: 12, color: C.textSec, marginTop: 2 }}>
                    Round {t.currentRound || ((t.currentMatchRound || 0) + 1)} · {fmtLabel(t.format)}
                    {t.pausedAt && <> · Paused {new Date(t.pausedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</>}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span className="badge paused-badge"><I.PauseCircle size={12} /> PAUSED</span>
                </div>
              </div>
            </div>
          ))}
        </>
      )}
      {registration.length > 0 && (
        <>
          <div className="section-title" style={{ marginTop: (active.length > 0 || paused.length > 0) ? 16 : 0 }}>Registration Open</div>
          {registration.map(t => {
            const pr = t.preRegistration || {};
            const regCount = (pr.registrants || []).length;
            const wlCount = (pr.waitlist || []).length;
            return (
              <div key={t.id} className="card" style={{ marginBottom: 10, cursor: "pointer", borderColor: C.blue, background: C.blueDim }} onClick={() => onResume(t)}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontWeight: 700, fontSize: 16, color: C.text }}>{t.name}</span>
                      <DivisionBadge division={t.division} />
                    </div>
                    <div style={{ fontSize: 12, color: C.textSec, marginTop: 2 }}>
                      {fmtLabel(t.format)}
                      {" · "}{regCount}/{pr.maxPlayers || "?"} registered{wlCount > 0 && ` · ${wlCount} waitlisted`}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {isAdmin && (
                      <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); const url = `${window.location.origin}${window.location.pathname}?register=${t.id}`; navigator.clipboard.writeText(url).then(() => { setCopiedRegLink(t.id); setTimeout(() => setCopiedRegLink(null), 2000); }); }} style={{ fontSize: 11, color: copiedRegLink === t.id ? C.accent : C.blue }}>
                        {copiedRegLink === t.id ? "Copied!" : "Reg Link"}
                      </button>
                    )}
                    <span className="badge" style={{ background: C.blueDim, color: C.blue }}>OPEN</span>
                  </div>
                </div>
              </div>
            );
          })}
        </>
      )}
      {finished.length > 0 && (
        <>
          <div className="section-title" style={{ marginTop: active.length > 0 ? 12 : 0, marginBottom: 8 }}>Most Recent</div>
          {renderFinishedCard(finished[0], 0)}
          {finished.length > 1 && (
            <button onClick={() => setShowPast(!showPast)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "10px 0", background: "none", border: "none", cursor: "pointer", fontFamily: font }}>
              <span className="section-title" style={{ margin: 0 }}>Past Tournaments ({finished.length - 1})</span>
              <span style={{ color: C.textMuted, transform: showPast ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}><I.ChevDown size={16} /></span>
            </button>
          )}
          {showPast && finished.slice(1).map(renderFinishedCard)}
        </>
      )}
      {active.length === 0 && paused.length === 0 && registration.length === 0 && finished.length === 0 && <div className="empty-state">{isAdmin ? "Build your first tournament above" : "No tournaments yet"}</div>}

      {showImport && <ImportTournament existingPlayers={onImport.players} onImport={onImport.handler} onClose={() => setShowImport(false)} />}
      {/* Club logo + version (tap for About) */}
      <div style={{ textAlign: "center", padding: "32px 16px 16px", opacity: 0.7, cursor: "pointer" }} onClick={() => setShowAbout(true)} title="About">
        <img src="/dip-logo.png" alt="Dinks In Paradise - Watersound" style={{ maxWidth: 180, width: "100%", height: "auto" }} />
        <div style={{ fontSize: 11, color: C.textMuted, marginTop: 8 }}>dinksinparadise.com · v{APP_VERSION} · About</div>
        <div style={{ fontSize: 11, marginTop: 4 }}>
          <a href={GIP_URL} onClick={e => e.stopPropagation()} style={{ color: C.accent, textDecoration: "none" }}>Games in Paradise family →</a>
        </div>
        <div style={{ fontSize: 10.5, color: C.textMuted, marginTop: 4 }}>© {new Date().getFullYear()} Games in Paradise · All rights reserved</div>
      </div>
      {showAbout && <AboutPage onClose={() => setShowAbout(false)} />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// USERS TAB (with delete confirmation)
// ════════════════════════════════════════════════════════════════════
function UsersTab({ users, onAddUser, onUpdateUser, onDeleteUser, onUnlockUser, currentUser }) {
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPass, setNewPass] = useState("");
  const [newRole, setNewRole] = useState("viewer");
  const [addError, setAddError] = useState("");
  const [confirmDel, setConfirmDel] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editPass, setEditPass] = useState("");
  const [editRole, setEditRole] = useState("viewer");
  const [editError, setEditError] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [roleFilter, setRoleFilter] = useState("all");
  const [newPhone, setNewPhone] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editSms, setEditSms] = useState(false);
  const [showActivity, setShowActivity] = useState(false);

  const isSuper = currentUser?.role === "superadmin";
  const shownUsers = roleFilter === "all" ? users : users.filter(u => u.role === roleFilter);

  const validEmail = (e) => /^\S+@\S+\.\S+$/.test(e);

  const handleAdd = async () => {
    if (newName.trim().length < 2) { setAddError("Username must be at least 2 characters"); return; }
    if (!validEmail(newEmail.trim())) { setAddError("A valid email address is required"); return; }
    if (newPass.trim().length < 6) { setAddError("Password must be at least 6 characters"); return; }
    if (users.find(u => u.username.toLowerCase() === newName.trim().toLowerCase())) { setAddError("That username is already taken"); return; }
    setAddError("");
    try {
      await onAddUser({ id: uid(), username: newName.trim(), email: newEmail.trim(), password: newPass.trim(), role: newRole, phone: newPhone.trim() });
      setNewName(""); setNewEmail(""); setNewPass(""); setNewRole("viewer"); setNewPhone(""); setShowAdd(false);
    } catch (e) {
      setAddError(/email is already in use/i.test(e.message) ? "That email is already in use" : "Could not create user");
    }
  };

  const openEdit = (u) => {
    setEditing(u); setEditName(u.username); setEditEmail(u.email || ""); setEditPass(""); setEditRole(u.role); setEditPhone(u.phone || ""); setEditSms(!!u.smsAlerts); setEditError("");
  };

  const handleEditSave = async () => {
    if (editName.trim().length < 2) { setEditError("Username must be at least 2 characters"); return; }
    if (editEmail.trim() && !validEmail(editEmail.trim())) { setEditError("Email address is not valid"); return; }
    if (editPass && editPass.trim().length < 6) { setEditError("Password must be at least 6 characters"); return; }
    if (users.find(u => u.id !== editing.id && u.username.toLowerCase() === editName.trim().toLowerCase())) {
      setEditError("That username is already taken"); return;
    }
    setEditSaving(true); setEditError("");
    try {
      await onUpdateUser({ id: editing.id, username: editName.trim(), email: editEmail.trim(), password: editPass.trim() || "", role: editRole, phone: editPhone.trim(), smsAlerts: editRole === "superadmin" ? editSms : false });
      setEditing(null);
    } catch (e) {
      setEditError(
        /last superadmin/i.test(e.message) ? "Cannot demote the last superadmin"
        : /email is already in use/i.test(e.message) ? "That email is already in use"
        : "Could not save changes");
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <div style={{ padding: 16 }} className="fade-in">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, gap: 8, flexWrap: "wrap" }}>
        <div className="section-title" style={{ margin: 0 }}>Users ({users.length})</div>
        <div style={{ display: "flex", gap: 4 }}>
          {[["all", "All"], ["superadmin", "Super"], ["admin", "Admin"], ["manager", "Manager"], ["viewer", "Viewer"]].map(([key, label]) => (
            <button key={key} className={`btn btn-sm ${roleFilter === key ? "btn-primary" : "btn-outline"}`} onClick={() => setRoleFilter(key)} style={{ fontSize: 11, padding: "4px 10px" }}>{label}</button>
          ))}
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}><I.Plus size={14} /> Add User</button>
      </div>
      {isSuper && (
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <button className="btn btn-outline btn-sm" style={{ flex: 1 }} onClick={() => setShowActivity(true)}><I.Clock size={14} /> Activity Log</button>
        </div>
      )}
      {shownUsers.length === 0 && <div className="empty-state">No {roleFilter === "all" ? "" : roleFilter + " "}users</div>}
      {shownUsers.map((u, i) => (
        <div key={u.id} className="card fade-in" style={{ padding: "12px 14px", marginBottom: 8, animationDelay: `${i*30}ms` }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: "50%", background: u.role === "superadmin" ? C.goldDim : u.role === "admin" ? C.goldDim : C.blueDim, color: u.role === "superadmin" ? C.gold : u.role === "admin" ? C.gold : C.blue, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 800 }}>
                {u.username.charAt(0).toUpperCase()}
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, color: C.text }}>{u.username}{u.id === currentUser?.id && <span style={{ fontSize: 11, color: C.textMuted, marginLeft: 4 }}>(you)</span>}</div>
                <div style={{ fontSize: 12, color: u.role === "superadmin" ? C.gold : u.role === "admin" ? C.gold : C.blue }}>
                  {u.role === "superadmin" ? "superadmin" : u.role}
                  {u.email ? <span style={{ color: C.textMuted }}> · {u.email}</span> : <span style={{ color: C.textMuted }}> · no email</span>}
                  {u.lockedOut && <span className="badge" style={{ marginLeft: 6, background: C.redDim, color: C.red, fontSize: 9, padding: "1px 7px" }}>locked</span>}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 4 }}>
              {u.lockedOut && (
                <button className="btn btn-ghost btn-sm" style={{ color: C.orange, fontSize: 11, padding: "4px 8px" }}
                  title="Clear the sign-in lockout" onClick={() => onUnlockUser(u.id)}>Unlock</button>
              )}
              {(isSuper || u.role !== "superadmin") && (
                <button className="btn btn-ghost btn-sm" onClick={() => openEdit(u)} title="Edit user"><I.Edit size={14} /></button>
              )}
              {isSuper && u.id !== currentUser?.id && u.role !== "superadmin" && (
                <button className="btn btn-ghost btn-sm" style={{ color: C.red }} onClick={() => setConfirmDel(u)}><I.Trash /></button>
              )}
            </div>
          </div>
        </div>
      ))}
      {showAdd && (
        <Modal onClose={() => setShowAdd(false)}>
          <ModalHeader title="Add User" onClose={() => setShowAdd(false)} />
          <input className="input" placeholder="Username" autoComplete="off" name="new-member-username" value={newName} onChange={e => setNewName(e.target.value)} style={{ marginBottom: 10 }} autoFocus />
          <input className="input" type="email" placeholder="Email" autoComplete="off" name="new-member-email" value={newEmail} onChange={e => setNewEmail(e.target.value)} style={{ marginBottom: 10 }} />
          <input className="input" type="tel" placeholder="Mobile phone (for SMS alerts, optional)" autoComplete="off" name="new-member-phone" value={newPhone} onChange={e => setNewPhone(e.target.value)} style={{ marginBottom: 10 }} />
          <input className="input" type="password" placeholder="Password" autoComplete="new-password" name="new-member-password" value={newPass} onChange={e => setNewPass(e.target.value)} style={{ marginBottom: 14 }} />
          <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
            <button className={`btn btn-sm ${newRole === "admin" ? "btn-primary" : "btn-outline"}`} onClick={() => setNewRole("admin")} style={{ flex: 1, minWidth: 90 }}><I.Settings size={13} /> Admin</button>
            <button className={`btn btn-sm ${newRole === "manager" ? "btn-primary" : "btn-outline"}`} onClick={() => setNewRole("manager")} style={{ flex: 1, minWidth: 90 }}><I.Trophy size={13} /> Manager</button>
            <button className={`btn btn-sm ${newRole === "viewer" ? "btn-primary" : "btn-outline"}`} onClick={() => setNewRole("viewer")} style={{ flex: 1, minWidth: 90 }}><I.Eye size={13} /> Viewer</button>
          </div>
          <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 }}>
            {newRole === "admin" ? "Manage tournaments, players and users. Deletes only own tournaments."
              : newRole === "manager" ? "Create and run their own tournaments; add players. View-only elsewhere."
              : "Read-only: can view tournaments and players."}
          </div>
          {addError && <div style={{ color: C.red, fontSize: 13, marginBottom: 12 }}>{addError}</div>}
          <button className="btn btn-primary btn-full" onClick={handleAdd} style={{ padding: 14 }}>Add User</button>
        </Modal>
      )}
      {editing && (
        <Modal onClose={() => setEditing(null)}>
          <ModalHeader title={`Edit ${editing.username}`} onClose={() => setEditing(null)} />
          <input className="input" placeholder="Username" autoComplete="off" name="edit-member-username" value={editName} onChange={e => setEditName(e.target.value)} style={{ marginBottom: 10 }} autoFocus />
          <input className="input" type="email" placeholder="Email" autoComplete="off" name="edit-member-email" value={editEmail} onChange={e => setEditEmail(e.target.value)} style={{ marginBottom: 10 }} />
          <input className="input" type="tel" placeholder="Mobile phone (for SMS alerts, optional)" autoComplete="off" name="edit-member-phone" value={editPhone} onChange={e => setEditPhone(e.target.value)} style={{ marginBottom: 10 }} />
          <input className="input" type="password" placeholder="New password (leave blank to keep)" autoComplete="new-password" name="edit-member-password" value={editPass} onChange={e => setEditPass(e.target.value)} style={{ marginBottom: 14 }} />
          {editing.id === currentUser?.id ? (
            <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 16 }}>You can't change your own role.</div>
          ) : (
            <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
              {isSuper && <button className={`btn btn-sm ${editRole === "superadmin" ? "btn-primary" : "btn-outline"}`} onClick={() => setEditRole("superadmin")} style={{ flex: 1, minWidth: 80 }}><I.Star size={13} /> Super</button>}
              <button className={`btn btn-sm ${editRole === "admin" ? "btn-primary" : "btn-outline"}`} onClick={() => setEditRole("admin")} style={{ flex: 1, minWidth: 80 }}><I.Settings size={13} /> Admin</button>
              <button className={`btn btn-sm ${editRole === "manager" ? "btn-primary" : "btn-outline"}`} onClick={() => setEditRole("manager")} style={{ flex: 1, minWidth: 80 }}><I.Trophy size={13} /> Manager</button>
              <button className={`btn btn-sm ${editRole === "viewer" ? "btn-primary" : "btn-outline"}`} onClick={() => setEditRole("viewer")} style={{ flex: 1, minWidth: 80 }}><I.Eye size={13} /> Viewer</button>
            </div>
          )}
          {editRole === "superadmin" && (
            <button onClick={() => setEditSms(v => !v)} style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 12px", marginBottom: 16, cursor: "pointer" }}>
              <span style={{ width: 38, height: 22, borderRadius: 12, background: editSms ? C.accent : C.border, position: "relative", flexShrink: 0, transition: "background 0.15s" }}>
                <span style={{ position: "absolute", top: 2, left: editSms ? 18 : 2, width: 18, height: 18, borderRadius: "50%", background: "#fff", transition: "left 0.15s" }} />
              </span>
              <span style={{ flex: 1 }}>
                <span style={{ display: "block", fontSize: 13, fontWeight: 600, color: C.text }}>Text me on logins &amp; sign-outs</span>
                <span style={{ display: "block", fontSize: 11, color: C.textMuted }}>{editPhone.trim() ? "SMS alerts to this superadmin's mobile" : "Add a mobile number above to receive texts"}</span>
              </span>
            </button>
          )}
          {editError && <div style={{ color: C.red, fontSize: 13, marginBottom: 12 }}>{editError}</div>}
          <button className="btn btn-primary btn-full" onClick={handleEditSave} disabled={editSaving} style={{ padding: 14 }}>{editSaving ? "Saving..." : "Save Changes"}</button>
        </Modal>
      )}
      {confirmDel && (
        <ConfirmModal
          title="Delete User?"
          message={<>Delete user <strong style={{ color: C.text }}>{confirmDel.username}</strong>?</>}
          warning="This cannot be undone."
          onConfirm={() => { onDeleteUser(confirmDel.id); setConfirmDel(null); }}
          onCancel={() => setConfirmDel(null)}
        />
      )}
      {showActivity && <ActivityLogScreen onClose={() => setShowActivity(false)} />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// ACTIVITY LOG (superadmin — login/logout audit trail)
// ════════════════════════════════════════════════════════════════════
function ActivityLogScreen({ onClose }) {
  const [events, setEvents] = useState(null);
  const [error, setError] = useState(null);
  const load = () => { setError(null); api.listActivity(200).then(setEvents).catch(() => setError("Could not load activity")); };
  useEffect(() => { load(); }, []);
  const fmtWhen = (iso) => { try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return iso; } };
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9998, background: C.bg, display: "flex", flexDirection: "column", fontFamily: font }}>
      <div style={{ padding: "14px 16px", display: "flex", alignItems: "center", gap: 10, borderBottom: `1px solid ${C.border}`, flexShrink: 0, width: "100%", maxWidth: 520, margin: "0 auto" }}>
        <button className="btn btn-ghost btn-sm" onClick={onClose} style={{ padding: 4 }}><I.ChevLeft size={20} /></button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: C.text }}>Activity Log</div>
          <div style={{ fontSize: 11, color: C.textMuted }}>Sign-ins and sign-outs</div>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={load} title="Refresh"><I.Refresh size={16} /></button>
      </div>
      <div style={{ flex: 1, overflow: "auto", padding: "12px 16px 40px", width: "100%", maxWidth: 520, margin: "0 auto" }}>
        {error && <div className="empty-state" style={{ color: C.red }}>{error}</div>}
        {!error && events === null && <div className="empty-state">Loading…</div>}
        {events && events.length === 0 && <div className="empty-state">No activity recorded yet</div>}
        {events && events.map(e => {
          const isLogin = e.eventType === "Login";
          return (
            <div key={e.id} className="card" style={{ padding: "10px 13px", marginBottom: 6, display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: isLogin ? C.accent : C.textMuted, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{e.userName} <span style={{ fontWeight: 400, color: C.textSec }}>{isLogin ? "signed in" : "signed out"}</span></div>
                {e.email && <div style={{ fontSize: 11, color: C.textMuted }}>{e.email}</div>}
              </div>
              <div style={{ fontSize: 11.5, color: C.textSec, textAlign: "right", flexShrink: 0 }}>{fmtWhen(e.timestampUtc)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// MANAGE TOURNAMENTS (superadmin — the only place tournaments can be deleted)
// ════════════════════════════════════════════════════════════════════
function ManageTournamentsScreen({ tournaments, onDelete, onEdit, onClose, canDelete = () => true, canEdit = () => false, currentUserId }) {
  const [target, setTarget] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [search, setSearch] = useState("");
  const [editTarget, setEditTarget] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDivision, setEditDivision] = useState("mens");
  const [editSaving, setEditSaving] = useState(false);
  const sorted = [...tournaments].sort((a, b) => new Date(b.finishedAt || b.createdAt || 0) - new Date(a.finishedAt || a.createdAt || 0));
  const shown = search.trim() ? sorted.filter(t => t.name.toLowerCase().includes(search.trim().toLowerCase())) : sorted;
  const statusLabel = (t) => t.status === "playing" ? "Live" : t.status === "paused" ? "Paused" : t.status === "registration" ? "Registration" : "Finished";
  const openEdit = (t) => { setEditTarget(t); setEditName(t.name); setEditDivision(t.division || "mens"); };
  const saveEdit = async () => {
    if (editName.trim().length < 1) return;
    setEditSaving(true);
    try {
      await onEdit({ ...editTarget, name: editName.trim(), division: editDivision });
      setEditTarget(null);
    } finally { setEditSaving(false); }
  };
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9998, background: C.bg, display: "flex", flexDirection: "column", fontFamily: font }}>
      <div style={{ padding: "14px 16px", display: "flex", alignItems: "center", gap: 10, borderBottom: `1px solid ${C.border}`, flexShrink: 0, width: "100%", maxWidth: 520, margin: "0 auto" }}>
        <button className="btn btn-ghost btn-sm" onClick={onClose} style={{ padding: 4 }}><I.ChevLeft size={20} /></button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: C.text }}>Manage Tournaments</div>
          <div style={{ fontSize: 11, color: C.textMuted }}>Rename, re-classify, or delete tournaments</div>
        </div>
      </div>
      <div style={{ flex: 1, overflow: "auto", padding: "12px 16px 40px", width: "100%", maxWidth: 520, margin: "0 auto" }}>
        <input className="input" placeholder="Search tournaments…" value={search} onChange={e => setSearch(e.target.value)} style={{ marginBottom: 12 }} />
        {shown.length === 0 && <div className="empty-state">No tournaments</div>}
        {shown.map(t => {
          const mine = currentUserId && t.createdBy === currentUserId;
          const deletable = canDelete(t);
          return (
            <div key={t.id} className="card" style={{ padding: "11px 13px", marginBottom: 6, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.text, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  {t.name} <DivisionBadge division={t.division} />
                  {mine && <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: C.accent, background: C.accentDim, padding: "1px 6px", borderRadius: 8 }}>Yours</span>}
                </div>
                <div style={{ fontSize: 11.5, color: C.textSec, marginTop: 1 }}>{statusLabel(t)} · {(t.players || []).length} players · {fmtLabel(t.format)}{t.createdByName ? ` · by ${t.createdByName}` : ""}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
                {canEdit(t) && <button className="btn btn-ghost btn-sm" style={{ color: C.textSec }} onClick={() => openEdit(t)} title="Edit name & division"><I.Edit size={15} /></button>}
                {deletable
                  ? <button className="btn btn-ghost btn-sm" style={{ color: C.red }} onClick={() => { setTarget(t); setConfirmText(""); }}><I.Trash /></button>
                  : <span style={{ fontSize: 10, color: C.textMuted, padding: "0 6px" }} title="You can only delete tournaments you created">—</span>}
              </div>
            </div>
          );
        })}
      </div>
      {editTarget && (
        <Modal onClose={() => setEditTarget(null)}>
          <ModalHeader title="Edit Tournament" onClose={() => setEditTarget(null)} />
          <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 6 }}>Name</div>
          <input className="input" value={editName} onChange={e => setEditName(e.target.value)} style={{ marginBottom: 14 }} autoFocus />
          <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 6 }}>Division</div>
          <div style={{ display: "flex", gap: 6, marginBottom: 18 }}>
            {[["mens", "Men's"], ["womens", "Women's"], ["mixed", "Mixed"]].map(([key, label]) => (
              <button key={key} className={`btn btn-sm ${editDivision === key ? "btn-primary" : "btn-outline"}`} onClick={() => setEditDivision(key)} style={{ flex: 1 }}>{label}</button>
            ))}
          </div>
          <button className="btn btn-primary btn-full" onClick={saveEdit} disabled={editSaving || editName.trim().length < 1} style={{ padding: 14 }}>{editSaving ? "Saving…" : "Save Changes"}</button>
        </Modal>
      )}
      {target && (
        <Modal onClose={() => setTarget(null)}>
          <ModalHeader title="Delete Tournament?" onClose={() => setTarget(null)} />
          <div style={{ fontSize: 13.5, color: C.textSec, lineHeight: 1.6, marginBottom: 12 }}>
            This permanently deletes <strong style={{ color: C.text }}>{target.name}</strong> and all its scores and history. This cannot be undone.
          </div>
          <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 6 }}>Type the tournament name to confirm:</div>
          <input className="input" value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder={target.name} style={{ marginBottom: 16 }} autoFocus />
          <button className="btn btn-full" disabled={confirmText.trim() !== target.name} onClick={() => { onDelete(target.id); setTarget(null); }}
            style={{ padding: 14, background: confirmText.trim() === target.name ? C.red : C.border, color: confirmText.trim() === target.name ? "#fff" : C.textMuted, fontWeight: 700 }}>
            <I.Trash size={14} /> Delete Permanently
          </button>
        </Modal>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// APP (ROOT)
// ════════════════════════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════════════
// PLAYER PROFILE (full stats page)
// ════════════════════════════════════════════════════════════════════
function PlayerProfile({ player, tournaments, dprMap, onClose }) {
  const scrollRef = useRef(null);
  React.useLayoutEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    window.scrollTo(0, 0);
  }, [player.id]);
  const dpr = dprMap[player.id];

  // KotH-only tournaments
  const kothTournaments = tournaments.filter(t => t.status === "finished" && t.rounds && (!t.format || t.format === "kingofhill"));

  // Per-tournament stats + partner/opponent maps (KotH only)
  const tourneyStats = [];
  const partnerMap = {};
  const opponentMap = {};
  // Chronological game log for streak calculation
  const gameLog = [];

  kothTournaments.forEach(t => {
    try {
      let wins = 0, losses = 0, pts = 0, diff = 0, gp = 0;
      let played = false;
      (t.rounds || []).forEach(r => {
        (r.courts || []).forEach(c => {
          const inA = (c.teamA || []).find(p => p.id === player.id);
          const inB = (c.teamB || []).find(p => p.id === player.id);
          if (!inA && !inB) return;
          if (c.scoreA === null || c.scoreB === null) return;
          played = true; gp++;
          const myScore = inA ? c.scoreA : c.scoreB;
          const oppScore = inA ? c.scoreB : c.scoreA;
          pts += myScore || 0;
          diff += (myScore || 0) - (oppScore || 0);
          const won = myScore > oppScore;
          if (won) wins++; else losses++;
          gameLog.push({ date: t.finishedAt, won });
          const myTeam = inA ? c.teamA : c.teamB;
          const oppTeam = inA ? c.teamB : c.teamA;
          myTeam.forEach(p => { if (p.id !== player.id) { if (!partnerMap[p.id]) partnerMap[p.id] = { ...p, gp: 0, wins: 0 }; partnerMap[p.id].gp++; if (won) partnerMap[p.id].wins++; } });
          oppTeam.forEach(p => { if (!opponentMap[p.id]) opponentMap[p.id] = { ...p, gp: 0, winsVs: 0 }; opponentMap[p.id].gp++; if (won) opponentMap[p.id].winsVs++; });
        });
      });

      // Medal: finish position on KotH leaderboard
      let medal = null;
      try {
        const board = calcLeaderboard(t.rounds || []);
        const pos = board.findIndex(x => x.id === player.id);
        if (pos === 0) medal = "gold";
        else if (pos === 1) medal = "silver";
        else if (pos === 2) medal = "bronze";
      } catch(e) {}

      if (played) tourneyStats.push({ id: t.id, name: t.name, date: t.finishedAt, wins, losses, pts, diff, gp, medal, winPct: gp > 0 ? wins / gp : 0 });
    } catch(e) {}
  });

  // Totals
  const totalTourneys = tourneyStats.length;
  const totalGames = tourneyStats.reduce((a, t) => a + t.gp, 0);
  const totalWins = tourneyStats.reduce((a, t) => a + t.wins, 0);
  const totalLosses = tourneyStats.reduce((a, t) => a + t.losses, 0);
  const totalPts = tourneyStats.reduce((a, t) => a + t.pts, 0);
  const totalDiff = tourneyStats.reduce((a, t) => a + t.diff, 0);
  const totalPtsPossible = totalGames * 11;
  const winRate = totalGames > 0 ? Math.round((totalWins / totalGames) * 100) : 0;
  const ptsCaptureRate = totalPtsPossible > 0 ? Math.round((totalPts / totalPtsPossible) * 100) : 0;

  // Medals
  const golds = tourneyStats.filter(t => t.medal === "gold").length;
  const silvers = tourneyStats.filter(t => t.medal === "silver").length;
  const bronzes = tourneyStats.filter(t => t.medal === "bronze").length;
  const podiums = golds + silvers + bronzes;
  const podiumRate = totalTourneys > 0 ? Math.round((podiums / totalTourneys) * 100) : 0;

  // Averages per tournament
  const avgWinsPerTourney = totalTourneys > 0 ? (totalWins / totalTourneys).toFixed(1) : "-";
  const avgWinPctPerTourney = totalTourneys > 0 ? Math.round(tourneyStats.reduce((a, t) => a + t.winPct, 0) / totalTourneys * 100) : 0;
  const avgPtsPerTourney = totalTourneys > 0 ? (totalPts / totalTourneys).toFixed(1) : "-";
  const avgPtsPerGame = totalGames > 0 ? (totalPts / totalGames).toFixed(1) : "-";
  const avgDiffPerTourney = totalTourneys > 0 ? (totalDiff / totalTourneys).toFixed(1) : "-";
  const avgDiffPerGame = totalGames > 0 ? (totalDiff / totalGames).toFixed(1) : "-";

  // Streaks
  const sortedGames = [...gameLog].sort((a, b) => new Date(a.date) - new Date(b.date));
  let currentStreak = 0, currentStreakType = null, bestWinStreak = 0, tempStreak = 0;
  sortedGames.forEach(g => {
    if (currentStreakType === null) { currentStreakType = g.won; currentStreak = 1; }
    else if (g.won === currentStreakType) currentStreak++;
    else { currentStreakType = g.won; currentStreak = 1; }
    if (g.won) { tempStreak++; if (tempStreak > bestWinStreak) bestWinStreak = tempStreak; }
    else tempStreak = 0;
  });
  const streakLabel = sortedGames.length === 0 ? "-" : (currentStreakType ? "W" : "L") + currentStreak;
  const streakColor = currentStreakType ? C.accent : C.red;

  // Best single tournament
  const bestTourney = tourneyStats.length > 0 ? [...tourneyStats].sort((a, b) => b.winPct - a.winPct || b.wins - a.wins)[0] : null;

  const partners = Object.values(partnerMap).sort((a, b) => b.gp - a.gp).slice(0, 8);
  const opponents = Object.values(opponentMap).sort((a, b) => b.gp - a.gp).slice(0, 8);

  const StatCard = ({ label, val, sub, color }) => (
    <div className="card" style={{ padding: "10px 12px", textAlign: "center" }}>
      <div style={{ fontSize: 18, fontWeight: 800, color: color || C.text, fontFamily: fontMono }}>{val}</div>
      {sub && <div style={{ fontSize: 10, color: C.accent, fontWeight: 600 }}>{sub}</div>}
      <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>{label}</div>
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9998, background: C.bg, display: "flex", flexDirection: "column", fontFamily: font }}>
      <div style={{ padding: "14px 16px", display: "flex", alignItems: "center", gap: 10, borderBottom: `1px solid ${C.border}`, flexShrink: 0, width: "100%", maxWidth: 520, margin: "0 auto" }}>
        <button className="btn btn-ghost btn-sm" onClick={onClose} style={{ padding: 4 }}><I.ChevLeft size={20} /></button>
        <div>
          <div style={{ fontSize: 17, fontWeight: 800, color: C.text }}>{player.name}</div>
          <div style={{ fontSize: 11, color: C.textMuted }}>King of the Hill stats only</div>
        </div>
      </div>
      <div ref={scrollRef} key={player.id} style={{ flex: 1, overflow: "auto", padding: "16px 16px 40px", width: "100%", maxWidth: 520, margin: "0 auto" }}>

        {/* Badges */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
          {player.ranking != null && <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 13, fontWeight: 700, color: C.gold, background: C.goldDim, padding: "4px 10px", borderRadius: 10 }}><I.Star size={12} /> Rank {player.ranking}</span>}
          {tournaments.some(t => isMomEra(t) && (t.players || []).some(p => p.id === player.id)) && (
            <MomEraBadge label={isMomFounder(player.name) ? "MoM Founding Member" : "MoM Legacy Member"} />
          )}
        </div>

        {/* Ratings: single-gender DPR + mixed mDPR */}
        {dpr && (dpr.tourneyCount > 0 || dpr.mixedTourneyCount > 0) && (
          <div style={{ display: "grid", gridTemplateColumns: dpr.mixedTourneyCount > 0 ? "1fr 1fr" : "1fr", gap: 8, marginBottom: 20 }}>
            <div className="card" style={{ padding: "12px 14px", textAlign: "center", borderColor: C.blue }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", color: C.blue, textTransform: "uppercase", marginBottom: 4 }}>
                {player.gender === "F" ? "Women's Doubles" : "Men's Doubles"}
              </div>
              {dpr.dpr != null ? (
                <div style={{ fontSize: 26, fontWeight: 900, fontFamily: fontMono, color: C.blue, opacity: dpr.tourneyCount < 3 ? 0.7 : 1 }}>
                  {dpr.dpr}{dpr.trend === "up" ? <span style={{ fontSize: 14 }}> ▲</span> : dpr.trend === "down" ? <span style={{ fontSize: 14 }}> ▼</span> : null}
                </div>
              ) : (
                <div style={{ fontSize: 26, fontWeight: 900, fontFamily: fontMono, color: C.textMuted }}>{dpr.tourneyCount}/2</div>
              )}
              <div style={{ fontSize: 10, color: C.textMuted, marginTop: 2 }}>
                {dpr.dpr != null
                  ? `${dpr.tourneyCount} tournament${dpr.tourneyCount === 1 ? "" : "s"}${dpr.tourneyCount < 3 ? " · provisional" : ""}`
                  : "tournaments to rating"}
              </div>
              {dpr.dpr != null && dpr.confidence != null && (
                <div style={{ fontSize: 10, marginTop: 3 }}><ConfidencePct pct={dpr.confidence} /> <span style={{ color: C.textMuted }}>confidence</span></div>
              )}
            </div>
            {dpr.mixedTourneyCount > 0 && (
              <div className="card" style={{ padding: "12px 14px", textAlign: "center", borderColor: C.gold }}>
                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", color: C.gold, textTransform: "uppercase", marginBottom: 4 }}>Mixed Doubles</div>
                <div style={{ fontSize: 26, fontWeight: 900, fontFamily: fontMono, color: C.gold }}>
                  {dpr.mdpr}{dpr.mixedTrend === "up" ? <span style={{ fontSize: 14 }}> ▲</span> : dpr.mixedTrend === "down" ? <span style={{ fontSize: 14 }}> ▼</span> : null}
                </div>
                <div style={{ fontSize: 10, color: C.textMuted, marginTop: 2 }}>
                  {dpr.mixedTourneyCount} tournament{dpr.mixedTourneyCount === 1 ? "" : "s"}{dpr.mixedTourneyCount < 3 ? " · provisional" : ""}
                </div>
                {dpr.mixedConfidence != null && (
                  <div style={{ fontSize: 10, marginTop: 3 }}><ConfidencePct pct={dpr.mixedConfidence} /> <span style={{ color: C.textMuted }}>confidence</span></div>
                )}
                {dpr.mdprSeedSource === "dpr" && dpr.mdprSeededFrom != null && (
                  <div style={{ fontSize: 9, color: C.textMuted, marginTop: 2 }}>seeded from DPR {dpr.mdprSeededFrom}</div>
                )}
              </div>
            )}
          </div>
        )}

        {totalTourneys === 0 && <div className="empty-state">No King of the Hill history yet</div>}

        {totalTourneys > 0 && (<>

          {/* MEDALS */}
          <div className="section-title">Medals</div>
          <div className="card" style={{ padding: "14px 16px", marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-around", marginBottom: 12 }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 28 }}>🥇</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: C.gold, fontFamily: fontMono }}>{golds}</div>
                <div style={{ fontSize: 11, color: C.textMuted }}>Gold</div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 28 }}>🥈</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: "#aaa", fontFamily: fontMono }}>{silvers}</div>
                <div style={{ fontSize: 11, color: C.textMuted }}>Silver</div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 28 }}>🥉</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: "#cd7f32", fontFamily: fontMono }}>{bronzes}</div>
                <div style={{ fontSize: 11, color: C.textMuted }}>Bronze</div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 22, fontWeight: 900, color: C.accent, fontFamily: fontMono }}>{podiumRate}%</div>
                <div style={{ fontSize: 11, color: C.textMuted }}>Podium Rate</div>
                <div style={{ fontSize: 10, color: C.textMuted }}>{podiums} of {totalTourneys}</div>
              </div>
            </div>
          </div>

          {/* OVERALL RECORD */}
          <div className="section-title">Overall Record</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 20 }}>
            <StatCard label="Tournaments" val={totalTourneys} />
            <StatCard label="Games" val={totalGames} />
            <StatCard label="Win %" val={winRate + "%"} color={winRate >= 50 ? C.accent : C.red} />
            <StatCard label="W-L" val={`${totalWins}-${totalLosses}`} />
            <StatCard label="Avg Wins/Tourney" val={avgWinsPerTourney} />
            <StatCard label="Avg Win %/Tourney" val={avgWinPctPerTourney + "%"} />
          </div>

          {/* POINTS */}
          <div className="section-title">Points</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 20 }}>
            <StatCard label="Total Pts" val={totalPts} />
            <StatCard label="Pts Possible" val={totalPtsPossible} />
            <StatCard label="Capture %" val={ptsCaptureRate + "%"} color={ptsCaptureRate >= 50 ? C.accent : C.red} />
            <StatCard label="Avg Pts/Game" val={avgPtsPerGame} />
            <StatCard label="Avg Pts/Tourney" val={avgPtsPerTourney} />
            <StatCard label="Pt Diff Total" val={(totalDiff > 0 ? "+" : "") + totalDiff} color={totalDiff >= 0 ? C.accent : C.red} />
            <StatCard label="Avg Diff/Game" val={(Number(avgDiffPerGame) > 0 ? "+" : "") + avgDiffPerGame} color={Number(avgDiffPerGame) >= 0 ? C.accent : C.red} />
            <StatCard label="Avg Diff/Tourney" val={(Number(avgDiffPerTourney) > 0 ? "+" : "") + avgDiffPerTourney} color={Number(avgDiffPerTourney) >= 0 ? C.accent : C.red} />
          </div>

          {/* STREAKS & BESTS */}
          <div className="section-title">Streaks &amp; Bests</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 20 }}>
            <div className="card" style={{ padding: "10px 12px", textAlign: "center" }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: streakColor, fontFamily: fontMono }}>{streakLabel}</div>
              <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>Current Streak</div>
            </div>
            <div className="card" style={{ padding: "10px 12px", textAlign: "center" }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: C.accent, fontFamily: fontMono }}>{bestWinStreak > 0 ? "W" + bestWinStreak : "-"}</div>
              <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>Best Win Streak</div>
            </div>
          </div>
          {bestTourney && (
            <div className="card" style={{ padding: "12px 14px", marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, marginBottom: 4 }}>BEST TOURNAMENT</div>
              <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>{bestTourney.name}</div>
              <div style={{ fontSize: 12, color: C.textSec, marginTop: 2 }}>{bestTourney.wins}W-{bestTourney.losses}L · {Math.round(bestTourney.winPct * 100)}% win rate · {new Date(bestTourney.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</div>
            </div>
          )}

          {/* TOURNAMENT HISTORY */}
          <div className="section-title">Tournament History</div>
          {tourneyStats.sort((a, b) => new Date(b.date) - new Date(a.date)).map((ts, i) => (
            <div key={i} className="card" style={{ padding: "10px 14px", marginBottom: 6 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 16 }}>{ts.medal === "gold" ? "🥇" : ts.medal === "silver" ? "🥈" : ts.medal === "bronze" ? "🥉" : ""}</span>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: C.text }}>{ts.name}</div>
                    <div style={{ fontSize: 11, color: C.textMuted }}>KotH · {new Date(ts.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</div>
                  </div>
                </div>
                <div style={{ textAlign: "right", fontFamily: fontMono, fontSize: 13 }}>
                  <div style={{ fontWeight: 700, color: ts.wins > ts.losses ? C.accent : ts.losses > ts.wins ? C.red : C.text }}>{ts.wins}W-{ts.losses}L</div>
                  <div style={{ fontSize: 11, color: C.textMuted }}>{ts.pts}pts · {ts.diff > 0 ? "+" : ""}{ts.diff}</div>
                </div>
              </div>
            </div>
          ))}

          {/* TOP PARTNERS */}
          {partners.length > 0 && (<>
            <div className="section-title" style={{ marginTop: 16 }}>Top Partners</div>
            {partners.map(p => (
              <div key={p.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: `1px solid ${C.border}22`, fontSize: 13 }}>
                <span style={{ color: C.text }}>{p.name}</span>
                <span style={{ fontFamily: fontMono, color: C.textSec }}>{p.gp}G · {p.wins}W · {p.gp > 0 ? Math.round((p.wins / p.gp) * 100) : 0}%</span>
              </div>
            ))}
          </>)}

          {/* TOP OPPONENTS */}
          {opponents.length > 0 && (<>
            <div className="section-title" style={{ marginTop: 16 }}>Top Opponents</div>
            {opponents.map(p => (
              <div key={p.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: `1px solid ${C.border}22`, fontSize: 13 }}>
                <span style={{ color: C.text }}>{p.name}</span>
                <span style={{ fontFamily: fontMono, color: C.textSec }}>{p.gp}G · {p.winsVs}W vs · {p.gp > 0 ? Math.round((p.winsVs / p.gp) * 100) : 0}%</span>
              </div>
            ))}
          </>)}

        </>)}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// USER GUIDE (searchable, collapsible in-app documentation)
// ════════════════════════════════════════════════════════════════════
const GUIDE_SECTIONS = [
  { id: "overview", title: "Getting Started", content: [
    { q: "What is Dinks In Paradise?", a: "Dinks In Paradise is a pickleball tournament management app. Create tournaments, track scores, manage players, and view leaderboards \u2014 all from your phone or browser." },
    { q: "How do I log in?", a: "Enter your username and password on the login screen. If no users exist yet, the first person to sign up becomes the admin." },
    { q: "What\u2019s the difference between Admin and Viewer?", a: "Admins can create tournaments, add/edit players and courts, manage users, and enter scores. Viewers can see tournaments, leaderboards, and player stats but cannot make changes." },
    { q: "Dark mode and light mode", a: "Tap the sun/moon icon in the top-right header to switch between dark and light themes. Your preference is saved to your device." },
  ]},
  { id: "formats", title: "Tournament Formats", content: [
    { q: "King of the Hill", a: "The classic format. 4 players per court, 2v2. Winners move up a court, losers move down. Partners split and remix every round so you play with different people. Best player rises to Court 1." },
    { q: "Round Robin", a: "Teams of 2 players each. Every team plays every other team once. The schedule is auto-generated. Great for fair skill assessment across a group. When finished, you can seed results into a Bracket." },
    { q: "Bracket (Single Elimination)", a: "Teams of 2. Lose and you\u2019re out. The bracket is seeded by team ranking. Top seeds get first-round byes when the number of teams isn\u2019t a power of 2 (e.g. 5 teams in an 8-slot bracket)." },
    { q: "Which format should I pick?", a: "King of the Hill for casual, rotating play. Round Robin for fair assessment where everyone plays everyone. Bracket for a fast, high-stakes finish. Many groups run Round Robin first, then seed into Bracket for a finals phase." },
  ]},
  { id: "building", title: "Building a Tournament", content: [
    { q: "Step 1: Name and settings", a: "Name your tournament, pick a format (King of the Hill, Round Robin, or Bracket), and toggle the game timer on or off. If the timer is on, choose a preset (5, 8, 10, 12, 15 min) or type a custom number." },
    { q: "Step 2: Courts and teams", a: "For King of the Hill: select which courts to use. For Round Robin/Bracket: choose how many teams (3–32) and optionally select courts for Round Robin scheduling." },
    { q: "Step 3: Assign players", a: "For King of the Hill: assign 4 players to each court. For Round Robin/Bracket: build teams of 2 players each. Use the A-Z / Rank / DPR sort toggle to find players quickly. You can also tap \u2018Assign by Rank\u2019 or \u2018Assign by DPR\u2019 to auto-fill." },
    { q: "Step 4: Seed bracket (Bracket only)", a: "Reorder teams for bracket seeding. Use \u2018Seed by Rank\u2019 (gold, admin-assigned rankings) or \u2018Seed by DPR\u2019 (blue, performance-based). Or manually drag with \u25B2\u25BC arrows. #1 seed is placed opposite #2 so they can only meet in the final." },
    { q: "Sorting players: A-Z, Rank, DPR", a: "Every player list has an A-Z / Rank / DPR toggle. A-Z sorts alphabetically (default). Rank sorts by the admin-assigned ranking number (highest first). DPR sorts by the calculated Dink Power Rating (highest first)." },
  ]},
  { id: "playing", title: "Playing a Tournament", content: [
    { q: "Entering scores", a: "Tap the score fields on each match card and type the score for each team/side. Both scores must be entered before you can advance." },
    { q: "Game timer", a: "If enabled, the timer appears above the match cards. The admin controls Start, Pause, Resume, and Reset. The timer syncs across all devices in real time \u2014 viewers, spectators, and other admins all see the same countdown. At 2 minutes remaining, the bar turns orange and pulses. At zero, a green \u2018Time!\u2019 alert appears with audio." },
    { q: "Next Round (King of the Hill)", a: "Once all courts are scored, tap \u2018Next Round\u2019. Winners move up, losers move down, and partners are split. The app avoids re-pairing you with the same partner when possible (Smart Partner Rotation)." },
    { q: "Next Round (Round Robin)", a: "Once all matches in the current round are scored, tap \u2018Next Round\u2019 to advance to the next set of matchups." },
    { q: "Advance Winner (Bracket)", a: "Enter scores for both teams, then tap \u2018Advance Winner\u2019 to push the winning team to the next round. This cannot be undone \u2014 double-check scores first." },
    { q: "Viewing the leaderboard", a: "Tap the trophy icon to see live standings. Toggle between PTS (total points) and DTR (Dink Tournament Rating) at the top of the leaderboard." },
    { q: "Round history", a: "Tap the clock icon to see scores from previous rounds." },
    { q: "End tournament early", a: "Tap \u2018End\u2019 at any time. If scores are incomplete, a confirmation prompt explains that unscored matches receive 0 points. This cannot be undone." },
    { q: "Pause a tournament", a: "Tap \u2018Pause\u2019 in the bottom bar to save the tournament mid-session. It appears in the \u2018Paused\u2019 section on the Tournaments tab. Tap it to resume exactly where you left off. Spectators see a \u2018PAUSED\u2019 badge." },
    { q: "Undo Advance (Bracket)", a: "After advancing a winner in Bracket format, an \u2018Undo\u2019 button appears in the bottom bar with a count showing how many advances can be undone. Tap it repeatedly to walk back multiple advances. This only works if downstream matches haven\u2019t been played yet." },
    { q: "Pre-Registration", a: "When building a tournament, toggle Pre-Registration ON in Step 1. Set a max player count and tap \u2018Open Registration.\u2019 This saves the tournament with an \u2018OPEN\u2019 badge on the Tournaments tab. Copy the registration link and share it with your group. Players open the link, pick their name from the roster (or add a new one), and get confirmed or waitlisted when full." },
    { q: "Check-In on tournament day", a: "Tap a tournament with the OPEN badge to open the Check-In screen. You\u2019ll see confirmed and waitlisted players. Remove no-shows with X. Promote waitlist players with +. When ready, tap \u2018Build Tournament\u2019 to proceed to courts/teams with checked-in players pre-loaded." },
    { q: "Substitutes from waitlist", a: "During an active KotH tournament, when you substitute a player, the sub list shows waitlisted players first (in registration order with W1, W2 labels), followed by all other available players. You choose who subs in \u2014 no auto-pull. Round Robin and Bracket do not currently support mid-tournament substitution." },
    { q: "Player search", a: "When you have 8+ players, a search bar appears on player selection screens (court assignment, team building, registration). Type to filter the list." },
    { q: "Tiebreaker rules", a: "Tiebreakers are shown at the bottom of every leaderboard. Order: Total pts (or DTR) \u2192 Wins \u2192 Head-to-head record \u2192 Point differential. Head-to-head compares direct matchup results between tied players." },
    { q: "Haptic feedback", a: "On supported devices, the app provides a subtle vibration when you enter a score and a confirmation pulse when you advance a round." },
  ]},
  { id: "rrbracket", title: "Round Robin to Bracket", content: [
    { q: "How does seeding from Round Robin work?", a: "After the last Round Robin round, tap \u2018Seed to Bracket\u2019 instead of \u2018Finish\u2019. A modal shows team standings ranked by Wins, then Point Differential, then Total Points." },
    { q: "Choosing how many teams", a: "You can seed all teams or just the top N (e.g. top 4 of 6). Tap a number button to choose." },
    { q: "What happens to the Round Robin?", a: "The Round Robin is saved as a finished tournament. A new Bracket tournament is created named \u2018[Name] \u2014 Bracket\u2019. Both appear in your Tournaments list so you keep the full history." },
  ]},
  { id: "scoring", title: "Scoring Systems", content: [
    { q: "PTS (Points)", a: "The default scoring. Simply adds up all points scored across all games. Higher total = higher rank. Rewards consistency and volume." },
    { q: "DTR (Dink Tournament Rating)", a: "DTR = (10 \u00D7 Wins) + Game Points + Point Differential. Wins are heavily weighted (10 pts each). Point differential rewards dominant play. A player who wins big ranks higher than one who barely wins." },
    { q: "DTR example", a: "A player going 2-1 with scores 11-3, 9-11, 11-5 earns: Win Points 20 (2\u00D710) + Game Points 31 (11+9+11) + Differential +12 = DTR 63." },
    { q: "Switching between PTS and DTR", a: "On any leaderboard, tap the PTS or DTR button at the top to toggle. Both are always available." },
  ]},
  { id: "dpr", title: "DPR (Dink Power Rating)", content: [
    { q: "What is DPR?", a: "DPR is an automatic player rating calculated from tournament results. It\u2019s shown as a blue badge on player cards, separate from the gold admin-assigned ranking." },
    { q: "How is DPR calculated?", a: "Every player starts at 3.0 on a 0.5–5.0 scale. After each tournament, the system adjusts based on win rate vs. expected performance, plus a small bonus/penalty for margin of victory. Your last 3 tournaments count double so recent form matters more." },
    { q: "When does DPR appear?", a: "DPR requires 3 completed tournaments. Until then, you\u2019ll see \u2018DPR: 1/3 tourneys\u2019 or \u2018DPR: 2/3 tourneys\u2019 on the player card." },
    { q: "What do the arrows mean?", a: "\u25B2 means the player\u2019s rating improved after their last tournament. \u25BC means it dropped. No arrow means it stayed flat." },
    { q: "DPR vs. Rank", a: "Rank (gold star) is assigned manually by the admin. DPR (blue badge) is calculated automatically from game results. You can seed tournaments by either one." },
  ]},
  { id: "partners", title: "Smart Partner Rotation", content: [
    { q: "What is it?", a: "In King of the Hill, when partners split each round, the app tracks who has partnered with whom. It picks pairings where you play with someone you haven\u2019t been teamed with yet in this tournament." },
    { q: "What if everyone has been partners already?", a: "After enough rounds with a small group, repeat partners are inevitable. The app picks the least-repeated pairing available \u2014 it minimizes repeats rather than requiring none." },
  ]},
  { id: "players", title: "Managing Players", content: [
    { q: "Adding a player", a: "Go to the Players tab. Type a name and optionally a ranking number, then tap +. Rankings support decimals (e.g. 3.75)." },
    { q: "Editing a player", a: "Tap the pencil icon on any player card. Change the name or ranking and tap the checkmark to save." },
    { q: "Player stats", a: "Each player card shows tournament count, games played, win-loss record, and total points. DPR appears as a blue badge once 3 tournaments are completed." },
    { q: "Deleting a player", a: "Tap the trash icon. A confirmation prompt appears. This cannot be undone." },
  ]},
  { id: "subs", title: "Substitutions", content: [
    { q: "How to substitute a player", a: "At the start of any round (before scores are entered), tap the swap icon (\u21C4) on a court card. Select which player to replace, then choose the substitute from available players not currently in the tournament." },
    { q: "When can I substitute?", a: "At the beginning of any round, before any scores have been entered for that round. Once you enter a score, the swap option hides until the next round starts." },
    { q: "Does the substitute need to exist?", a: "Yes \u2014 the substitute must already be in your Players roster. Add them first if needed." },
  ]},
  { id: "courts", title: "Courts", content: [
    { q: "Adding courts", a: "Go to the Courts tab (admin only). Tap + to add a court. Courts are numbered automatically." },
    { q: "How courts are used", a: "In King of the Hill, courts define the ladder (Court 1 is top). In Round Robin, matches rotate across selected courts. In Bracket, courts are not used \u2014 matches play sequentially." },
  ]},
  { id: "winners", title: "Winners & Champions", content: [
    { q: "King of the Hill winner", a: "The individual player with the most points (or highest DTR) across all rounds is the champion." },
    { q: "Round Robin winner", a: "The team with the most wins is the champion. Tiebreakers: point differential, then total points. The champion banner shows the team name and both player names." },
    { q: "Bracket champion", a: "The team that wins the final match. The champion banner shows the team name and both player names." },
    { q: "Viewing past tournament results", a: "Go to the Tournaments tab and tap any past tournament. You\u2019ll see the champion banner, final standings, and round-by-round history." },
  ]},
  { id: "tips", title: "Tips & Troubleshooting", content: [
    { q: "Editing past scores", a: "View any finished tournament and tap \u2018Edit Scores\u2019 in the Round History section (admin only). Tap any score number to change it. The leaderboard and standings update instantly. You can also edit past round scores during a live KotH tournament by opening Round History." },
    { q: "Player stats page", a: "Tap any player card in the Players tab to open their full profile. You\u2019ll see career stats (win rate, total games, point differential), tournament-by-tournament history, top partners with win rates, and head-to-head records against opponents." },
    { q: "Sharing results", a: "After a tournament finishes, view it and tap the share icon (top-right). On phones this opens your native share sheet (text, iMessage, WhatsApp, etc). On desktop it copies a formatted text summary to your clipboard with champion, medal standings, and a spectator link." },
    { q: "Spectator link (live viewing)", a: "During any active tournament, tap the \u2018Share Live\u2019 button in the tournament header. This copies a spectator URL. Anyone with the link can watch scores update live without logging in. Spectators see the synced game timer, current round scores, live leaderboard with PTS/DTR toggle, and completed round history. It works for all formats." },
    { q: "Timer not syncing", a: "The game timer now syncs across all devices in real time via Firestore. All viewers and spectators see the same countdown the admin controls. If you see a stale timer, try refreshing the page." },
    { q: "Scores look wrong", a: "Check that all courts have scores entered. The \u2018End\u2019 button with incomplete scores will zero-out unscored matches." },
    { q: "Delete confirmations", a: "All delete actions (players, courts, tournaments, users) require a confirmation prompt. Destructive actions warn that they cannot be undone." },
    { q: "Browser refresh", a: "All data is saved to the cloud in real time. Refreshing the page will not lose any tournament data. However, the game timer resets on refresh." },
    { q: "How many players can I add?", a: "There is no hard limit. The app handles large rosters, though tournament formats work best with groups of 8–32 players." },
  ]},
];

function UserGuide({ onClose }) {
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});
  const inputRef = useRef(null);

  useEffect(() => { if (inputRef.current) inputRef.current.focus(); }, []);

  const q = search.trim().toLowerCase();
  const filtered = q ? GUIDE_SECTIONS.map(s => ({
    ...s,
    content: s.content.filter(item => item.q.toLowerCase().includes(q) || item.a.toLowerCase().includes(q))
  })).filter(s => s.content.length > 0) : GUIDE_SECTIONS;

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  // When searching, auto-expand all matching sections
  const isExpanded = (id) => q ? true : !!expanded[id];

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: C.bg, display: "flex", flexDirection: "column", fontFamily: font }}>
      <div style={{ padding: "14px 16px", display: "flex", alignItems: "center", gap: 10, borderBottom: `1px solid ${C.border}`, flexShrink: 0, width: "100%", maxWidth: 520, margin: "0 auto" }}>
        <button className="btn btn-ghost btn-sm" onClick={onClose} style={{ padding: 4 }}><I.ChevLeft size={20} /></button>
        <div style={{ flex: 1, position: "relative" }}>
          <I.Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: C.textMuted }} />
          <input ref={inputRef} className="input" placeholder="Search help topics..." value={search} onChange={e => setSearch(e.target.value)} style={{ width: "100%", paddingLeft: 32, fontSize: 14 }} />
        </div>
        {search && <button className="btn btn-ghost btn-sm" onClick={() => setSearch("")} style={{ padding: 4 }}><I.X size={14} /></button>}
      </div>
      <div style={{ flex: 1, overflow: "auto", padding: "8px 0 40px", width: "100%", maxWidth: 520, margin: "0 auto" }}>
        {filtered.length === 0 && (
          <div style={{ textAlign: "center", padding: 40, color: C.textMuted }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🔍</div>
            <div>No results for "{search}"</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>Try different keywords</div>
          </div>
        )}
        {filtered.map(section => (
          <div key={section.id} style={{ marginBottom: 2 }}>
            <button onClick={() => toggle(section.id)} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: isExpanded(section.id) ? C.surfaceHover : "transparent", border: "none", borderBottom: `1px solid ${C.border}`, cursor: "pointer", fontFamily: font }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{section.title}</span>
              <span style={{ color: C.textMuted, transform: isExpanded(section.id) ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}><I.ChevDown size={16} /></span>
            </button>
            {isExpanded(section.id) && (
              <div style={{ padding: "4px 0" }}>
                {section.content.map((item, i) => {
                  const matchQ = q && item.q.toLowerCase().includes(q);
                  const matchA = q && item.a.toLowerCase().includes(q);
                  return (
                    <div key={i} style={{ padding: "10px 16px 10px 24px", borderBottom: `1px solid ${C.border}22` }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: matchQ ? C.accent : C.text, marginBottom: 4 }}>{item.q}</div>
                      <div style={{ fontSize: 13, color: matchA ? C.text : C.textSec, lineHeight: 1.5 }}>{item.a}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
        <div style={{ textAlign: "center", padding: "24px 16px", color: C.textMuted, fontSize: 12 }}>
          Dinks In Paradise · dinksinparadise.com
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// PLAYER SEARCH (reusable filter input for any player list)
// ════════════════════════════════════════════════════════════════════
function PlayerSearch({ value, onChange, placeholder = "Search players..." }) {
  return (
    <div style={{ position: "relative", marginBottom: 10 }}>
      <I.Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: C.textMuted }} />
      <input className="input" placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)} style={{ paddingLeft: 32, fontSize: 14 }} />
      {value && <button onClick={() => onChange("")} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: C.textMuted, cursor: "pointer", padding: 2 }}><I.X size={14} /></button>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// CHECK-IN SCREEN (admin manages who plays from pre-registration)
// ════════════════════════════════════════════════════════════════════
function CheckInScreen({ tournament, allPlayers, onUpdateTournament, onProceedToBuild, onDelete, onBack, isAdmin }) {
  const [copiedLink, setCopiedLink] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const t = tournament;
  const pr = t.preRegistration || { registrants: [], waitlist: [] };
  const registrants = pr.registrants || [];
  const waitlist = pr.waitlist || [];
  const spotsLeft = Math.max(0, (pr.maxPlayers || 24) - registrants.length);

  const copyRegLink = () => {
    const url = `${window.location.origin}${window.location.pathname}?register=${t.id}`;
    navigator.clipboard.writeText(url).then(() => { setCopiedLink(true); setTimeout(() => setCopiedLink(false), 2000); }).catch(() => {});
  };

  // Toggle check-in status for a registrant
  const toggleCheckIn = (playerId) => {
    if (!isAdmin) return;
    const newRegistrants = registrants.map(r => r.playerId === playerId ? { ...r, checkedIn: !r.checkedIn } : r);
    const newPr = { ...pr, registrants: newRegistrants };
    onUpdateTournament({ ...t, preRegistration: newPr });
  };

  // Remove a registrant (no-show) — admin only
  const removeRegistrant = (playerId) => {
    if (!isAdmin) return;
    const newRegistrants = registrants.filter(r => r.playerId !== playerId);
    const newWaitlist = [...waitlist];
    // Auto-promote first waitlist player to fill the spot
    if (newWaitlist.length > 0 && newRegistrants.length < (pr.maxPlayers || 24)) {
      const promoted = newWaitlist.shift();
      newRegistrants.push({ ...promoted, status: "confirmed", promotedAt: new Date().toISOString() });
    }
    const newPr = { ...pr, registrants: newRegistrants, waitlist: newWaitlist };
    onUpdateTournament({ ...t, preRegistration: newPr });
  };

  // Pull a specific waitlisted player into confirmed (admin picks who)
  const promoteFromWaitlist = (playerId) => {
    if (!isAdmin) return;
    const person = waitlist.find(w => w.playerId === playerId);
    if (!person) return;
    const newPr = {
      ...pr,
      registrants: [...registrants, { ...person, status: "confirmed", promotedAt: new Date().toISOString() }],
      waitlist: waitlist.filter(w => w.playerId !== playerId),
    };
    onUpdateTournament({ ...t, preRegistration: newPr });
  };

  // Remove from waitlist
  const removeFromWaitlist = (playerId) => {
    if (!isAdmin) return;
    const newPr = { ...pr, waitlist: waitlist.filter(w => w.playerId !== playerId) };
    onUpdateTournament({ ...t, preRegistration: newPr });
  };

  // Proceed to build — passes the checked-in player IDs to the build flow
  const handleProceed = () => {
    const checkedInPlayerIds = registrants.map(r => r.playerId);
    const checkedInPlayers = checkedInPlayerIds.map(id => allPlayers.find(p => p.id === id)).filter(Boolean);
    onProceedToBuild(t, checkedInPlayers);
  };

  return (
    <div style={{ padding: 16 }} className="fade-in">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, color: C.text }}>{t.name}</div>
          <div style={{ fontSize: 13, color: C.textSec }}>{t.format === "roundrobin" ? "Round Robin" : t.format === "bracket" ? "Bracket" : "King of the Hill"} · Registration Open</div>
        </div>
      </div>

      {/* Share link */}
      <div className="card" style={{ marginBottom: 16, padding: "14px 16px", borderColor: C.blue }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>Registration Link</div>
            <div style={{ fontSize: 12, color: C.textSec }}>Share this with your group</div>
          </div>
          <button className="btn btn-outline btn-sm" onClick={copyRegLink} style={{ color: copiedLink ? C.accent : C.blue, borderColor: copiedLink ? C.accent : C.blue }}>
            {copiedLink ? <><I.Check size={14} /> Copied!</> : <><I.Share size={14} /> Copy Link</>}
          </button>
        </div>
      </div>

      {/* Status bar */}
      <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        <div className="card" style={{ flex: 1, padding: "12px 14px", textAlign: "center" }}>
          <div style={{ fontSize: 24, fontWeight: 900, fontFamily: fontMono, color: C.accent }}>{registrants.filter(r => r.checkedIn).length}/{registrants.length}</div>
          <div style={{ fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: "uppercase" }}>Checked In</div>
        </div>
        <div className="card" style={{ flex: 1, padding: "12px 14px", textAlign: "center" }}>
          <div style={{ fontSize: 24, fontWeight: 900, fontFamily: fontMono, color: spotsLeft > 0 ? C.text : C.orange }}>{spotsLeft}</div>
          <div style={{ fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: "uppercase" }}>Spots Left</div>
        </div>
        <div className="card" style={{ flex: 1, padding: "12px 14px", textAlign: "center" }}>
          <div style={{ fontSize: 24, fontWeight: 900, fontFamily: fontMono, color: waitlist.length > 0 ? C.orange : C.textMuted }}>{waitlist.length}</div>
          <div style={{ fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: "uppercase" }}>Waitlist</div>
        </div>
      </div>

      {/* Confirmed registrants */}
      <div className="section-title">Confirmed ({registrants.length}/{pr.maxPlayers})</div>
      {registrants.length === 0 && <div className="empty-state" style={{ padding: "20px 0" }}>No one registered yet. Share the link above.</div>}
      {registrants.map((r, i) => (
        <div key={r.playerId} className="card" style={{ padding: "8px 14px", marginBottom: 4, display: "flex", alignItems: "center", justifyContent: "space-between", borderColor: r.checkedIn ? C.accent : C.border, background: r.checkedIn ? C.accentDim : C.card }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {isAdmin && (
              <button onClick={() => toggleCheckIn(r.playerId)} style={{ width: 24, height: 24, borderRadius: 6, border: `2px solid ${r.checkedIn ? C.accent : C.border}`, background: r.checkedIn ? C.accent : "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0, padding: 0 }}>
                {r.checkedIn && <I.Check size={14} style={{ color: "#fff" }} />}
              </button>
            )}
            <span style={{ fontWeight: 700, color: C.textMuted, fontFamily: fontMono, fontSize: 11, width: 24 }}>#{i + 1}</span>
            <span style={{ fontWeight: 600, fontSize: 14, color: C.text, textDecoration: r.checkedIn ? "none" : "none" }}>{r.playerName}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {r.checkedIn && <span style={{ fontSize: 10, fontWeight: 700, color: C.accent, textTransform: "uppercase" }}>Here</span>}
            <span style={{ fontSize: 10, color: C.textMuted }}>{new Date(r.registeredAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}</span>
            {isAdmin && <button className="btn btn-ghost btn-sm" style={{ color: C.red, padding: 4 }} onClick={() => removeRegistrant(r.playerId)} title="Remove (no-show)"><I.X size={14} /></button>}
          </div>
        </div>
      ))}

      {/* Waitlist */}
      {waitlist.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div className="section-title" style={{ color: C.orange }}>Waitlist ({waitlist.length})</div>
          <div style={{ fontSize: 12, color: C.textSec, marginBottom: 8 }}>Remove a no-show above and the first waitlisted player auto-promotes.</div>
          {waitlist.map((w, i) => (
            <div key={w.playerId} className="card" style={{ padding: "8px 14px", marginBottom: 4, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontWeight: 700, color: C.orange, fontFamily: fontMono, fontSize: 11, width: 24 }}>W{i + 1}</span>
                <span style={{ fontWeight: 600, fontSize: 14, color: C.text }}>{w.playerName}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ fontSize: 10, color: C.textMuted }}>{new Date(w.registeredAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}</span>
                {isAdmin && <button className="btn btn-ghost btn-sm" style={{ color: C.red, padding: 4 }} onClick={() => removeFromWaitlist(w.playerId)} title="Remove from waitlist"><I.X size={14} /></button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      {isAdmin && (
        <div style={{ marginTop: 24, display: "flex", gap: 8 }}>
          <button className="btn btn-outline" onClick={onBack} style={{ flex: 0.5 }}><I.ChevLeft /> Back</button>
          <button className="btn btn-primary" disabled={registrants.length < 2} onClick={handleProceed} style={{ flex: 1, padding: 14 }}>
            <I.Play size={16} /> Build Tournament ({registrants.length} players)
          </button>
        </div>
      )}
      {isAdmin && (
        <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
          <button className="btn btn-outline btn-sm" style={{ color: C.red, borderColor: C.red }} onClick={() => setConfirmDelete(true)}>
            <I.Trash size={12} /> Delete Registration
          </button>
        </div>
      )}
      {confirmDelete && (
        <ConfirmModal
          title="Delete Registration?"
          message={<>Delete <strong style={{ color: C.text }}>{t.name}</strong> and all registrations?</>}
          warning="This cannot be undone."
          onConfirm={() => { onDelete(t.id); setConfirmDelete(false); }}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// PUBLIC REGISTRATION PAGE (?register=TOURNAMENT_ID)
// ════════════════════════════════════════════════════════════════════
function RegistrationPage({ tournamentId }) {
  const [tournament, setTournament] = useState(null);
  const [allPlayers, setAllPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [newName, setNewName] = useState("");
  const [registered, setRegistered] = useState(false);
  const [regInfo, setRegInfo] = useState(null);
  const [pendingReg, setPendingReg] = useState(null); // mixed division: registrant awaiting gender answer

  useEffect(() => {
    let cancelled = false;
    api.getTournament(tournamentId)
      .then(data => { if (!cancelled) { setTournament(data); setLoading(false); } })
      .catch(err => { if (!cancelled) { setError(err.status === 404 ? "Tournament not found" : "Unable to load tournament"); setLoading(false); } });
    api.listPlayers()
      .then(ps => { if (!cancelled) setAllPlayers(ps.sort((a, b) => a.name.localeCompare(b.name))); })
      .catch(() => {});
    const rt = api.connectRealtime({ onTournamentUpdated: t => { if (!cancelled) setTournament(t); } });
    rt.watch(tournamentId);
    return () => { cancelled = true; rt.stop(); };
  }, [tournamentId]);

  if (loading) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: C.bg, fontFamily: font, color: C.textMuted }}>
      <style>{buildCSS()}</style>
      <div style={{ textAlign: "center" }}><img src="/dip-logo.png" alt="" style={{ maxWidth: 150, width: "50%", height: "auto", marginBottom: 10 }} /><div>Loading...</div></div>
    </div>
  );

  if (error || !tournament) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: C.bg, fontFamily: font, color: C.textMuted }}>
      <style>{buildCSS()}</style>
      <div style={{ textAlign: "center" }}><img src="/dip-logo.png" alt="" style={{ maxWidth: 150, width: "50%", height: "auto", marginBottom: 10 }} /><div>{error || "Tournament not found"}</div></div>
    </div>
  );

  const t = tournament;
  const pr = t.preRegistration;
  if (!pr || !pr.enabled) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: C.bg, fontFamily: font, color: C.textMuted }}>
      <style>{buildCSS()}</style>
      <div style={{ textAlign: "center" }}><img src="/dip-logo.png" alt="" style={{ maxWidth: 150, width: "50%", height: "auto", marginBottom: 10 }} /><div>Registration not available for this tournament.</div></div>
    </div>
  );

  const registrants = pr.registrants || [];
  const waitlist = pr.waitlist || [];
  const spotsLeft = Math.max(0, pr.maxPlayers - registrants.length);
  const isFull = registrants.length >= pr.maxPlayers;
  const allRegisteredIds = new Set([...registrants.map(r => r.playerId), ...waitlist.map(w => w.playerId)]);

  const division = t.division || "mens";

  const submitRegistration = async (player, gender) => {
    const entry = { playerId: player.id || null, playerName: player.name, registeredAt: new Date().toISOString() };
    if (gender === "M" || gender === "F") entry.gender = gender;
    const updated = await api.registerForTournament(tournamentId, entry);
    setTournament(updated);
    setPendingReg(null);
    // Server decides confirmed vs waitlist — find where we ended up
    const newPr = updated.preRegistration || {};
    const findEntry = (list) => (list || []).findIndex(e => player.id ? e.playerId === player.id : e.playerName === player.name);
    const ri = findEntry(newPr.registrants);
    if (ri >= 0) {
      setRegInfo({ status: "confirmed", position: ri + 1 });
    } else {
      const wi = findEntry(newPr.waitlist);
      setRegInfo({ status: "waitlist", position: (wi >= 0 ? wi : (newPr.waitlist || []).length - 1) + 1 });
    }
    setRegistered(true);
  };

  const handleRegister = async (player) => {
    if (player.id && allRegisteredIds.has(player.id)) return;
    // mens/womens: gender is implied by the division. Mixed: use the player's known
    // gender, otherwise ask before submitting.
    let gender = division === "mens" ? "M" : division === "womens" ? "F" : null;
    if (division === "mixed") {
      const known = player.id ? (allPlayers.find(p => p.id === player.id)?.gender || null) : null;
      if (known === "M" || known === "F") gender = known;
      else { setPendingReg(player); return; }
    }
    await submitRegistration(player, gender);
  };

  const handleNewPlayer = async () => {
    if (newName.trim().length < 2) return;
    await handleRegister({ id: null, name: newName.trim() });
  };

  const filtered = allPlayers.filter(p => !allRegisteredIds.has(p.id) && p.name.toLowerCase().includes(search.toLowerCase()));

  if (registered && regInfo) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: C.bg, fontFamily: font, color: C.text }}>
      <style>{buildCSS()}</style>
      <div style={{ textAlign: "center", padding: 24, maxWidth: 400, margin: "0 auto" }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>{regInfo.status === "confirmed" ? "✅" : "⏳"}</div>
        <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>{regInfo.status === "confirmed" ? "You're In!" : "Added to Waitlist"}</div>
        <div style={{ fontSize: 14, color: C.textSec, marginBottom: 20 }}>
          {regInfo.status === "confirmed"
            ? `You are #${regInfo.position} of ${pr.maxPlayers} confirmed.`
            : `You are #${regInfo.position} on the waitlist. You'll play if someone drops out.`}
        </div>
        <div className="card" style={{ padding: 16, textAlign: "left", marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>{t.name}</div>
          <div style={{ fontSize: 13, color: C.textSec }}>{new Date(t.createdAt).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}</div>
        </div>
        <div style={{ fontSize: 12, color: C.textMuted }}>You can close this page. The tournament admin manages check-in.</div>
      </div>
    </div>
  );

  return (
    <div style={{ fontFamily: font, background: C.bg, color: C.text, minHeight: "100vh", maxWidth: 520, margin: "0 auto" }}>
      <style>{buildCSS()}</style>
      <div style={{ padding: "20px 16px", borderBottom: `1px solid ${C.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <div style={{ fontSize: 20, fontWeight: 800, display: "flex", alignItems: "center", gap: 8 }}><img src="/dip-logo.png" alt="" style={{ height: 28, width: "auto" }} />{t.name}</div>
          <DivisionBadge division={t.division} />
        </div>
        <div style={{ fontSize: 13, color: C.textSec, marginTop: 4 }}>
          {isFull ? <span style={{ color: C.orange, fontWeight: 700 }}>Full — waitlist only</span> : <><span style={{ fontWeight: 700, color: C.accent }}>{spotsLeft}</span> spot{spotsLeft !== 1 ? "s" : ""} remaining</>}
          {" · "}{registrants.length}/{pr.maxPlayers} registered{waitlist.length > 0 && ` · ${waitlist.length} waitlisted`}
        </div>
      </div>

      <div style={{ padding: 16 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 10 }}>Select Your Name</div>
        <PlayerSearch value={search} onChange={setSearch} placeholder="Search roster..." />
        <div style={{ maxHeight: 300, overflowY: "auto", marginBottom: 16 }}>
          {filtered.length === 0 && search && <div style={{ padding: "12px 0", fontSize: 13, color: C.textMuted }}>No matching players. Add yourself below.</div>}
          {filtered.map(p => (
            <button key={p.id} className="btn btn-outline btn-full" style={{ marginBottom: 4, justifyContent: "flex-start", padding: "10px 14px" }} onClick={() => handleRegister(p)}>
              {p.name}{p.ranking != null && <span style={{ fontSize: 11, color: C.gold, marginLeft: 6 }}>★{p.ranking}</span>}
            </button>
          ))}
        </div>

        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.textSec, marginBottom: 8 }}>Not in the roster? Add yourself:</div>
          <div style={{ display: "flex", gap: 8 }}>
            <input className="input" placeholder="Your name" value={newName} onChange={e => setNewName(e.target.value)} onKeyDown={e => e.key === "Enter" && handleNewPlayer()} style={{ flex: 1 }} />
            <button className="btn btn-primary" disabled={newName.trim().length < 2} onClick={handleNewPlayer}>{isFull ? "Join Waitlist" : "Register"}</button>
          </div>
        </div>
      </div>

      {/* Show who's registered */}
      {registrants.length > 0 && (
        <div style={{ padding: "0 16px 16px" }}>
          <div className="section-title">Registered ({registrants.length}/{pr.maxPlayers})</div>
          {registrants.map((r, i) => (
            <div key={r.playerId} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 12px", background: C.card, borderRadius: 8, marginBottom: 3, fontSize: 13 }}>
              <span><span style={{ fontWeight: 700, color: C.textMuted, marginRight: 8, fontFamily: fontMono, fontSize: 11 }}>#{i + 1}</span>{r.playerName}</span>
              <span style={{ fontSize: 10, color: C.textMuted }}>{new Date(r.registeredAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}</span>
            </div>
          ))}
        </div>
      )}
      {waitlist.length > 0 && (
        <div style={{ padding: "0 16px 16px" }}>
          <div className="section-title" style={{ color: C.orange }}>Waitlist ({waitlist.length})</div>
          {waitlist.map((w, i) => (
            <div key={w.playerId} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 12px", background: C.card, borderRadius: 8, marginBottom: 3, fontSize: 13 }}>
              <span><span style={{ fontWeight: 700, color: C.orange, marginRight: 8, fontFamily: fontMono, fontSize: 11 }}>W{i + 1}</span>{w.playerName}</span>
              <span style={{ fontSize: 10, color: C.textMuted }}>{new Date(w.registeredAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ textAlign: "center", padding: "20px 16px 40px", color: C.textMuted, fontSize: 12 }}>Dinks In Paradise · dinksinparadise.com · v{APP_VERSION}<br /><span style={{ fontSize: 10.5 }}>© {new Date().getFullYear()} Games in Paradise · All rights reserved</span></div>

      {/* Mixed division: required gender step before submitting */}
      {pendingReg && (
        <Modal onClose={() => setPendingReg(null)}>
          <ModalHeader title="One More Thing" onClose={() => setPendingReg(null)} />
          <div style={{ fontSize: 14, color: C.textSec, marginBottom: 20 }}>
            This is a <strong style={{ color: divColor("mixed").color }}>Mixed Doubles</strong> tournament. Which are you registering as, <strong style={{ color: C.text }}>{pendingReg.name}</strong>?
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button className="btn btn-outline" style={{ flex: 1, padding: 14, fontSize: 15, color: C.accent, borderColor: C.accent }} onClick={() => submitRegistration(pendingReg, "M")}>♂ Male</button>
            <button className="btn btn-outline" style={{ flex: 1, padding: 14, fontSize: 15, color: C.orange, borderColor: C.orange }} onClick={() => submitRegistration(pendingReg, "F")}>♀ Female</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// SPECTATOR VIEW (read-only, no login required)
// ════════════════════════════════════════════════════════════════════
function SpectatorView({ tournamentId }) {
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.getTournament(tournamentId)
      .then(data => { if (!cancelled) { setTournament(data); setLoading(false); } })
      .catch(err => { if (!cancelled) { setError(err.status === 404 ? "Tournament not found" : "Unable to load tournament"); setLoading(false); } });
    const rt = api.connectRealtime({ onTournamentUpdated: t => { if (!cancelled) setTournament(t); } });
    rt.watch(tournamentId);
    return () => { cancelled = true; rt.stop(); };
  }, [tournamentId]);

  if (loading) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: C.bg, fontFamily: font, color: C.textMuted }}>
      <style>{buildCSS()}</style>
      <div style={{ textAlign: "center" }}><img src="/dip-logo.png" alt="" style={{ maxWidth: 150, width: "50%", height: "auto", marginBottom: 10 }} /><div>Loading tournament...</div></div>
    </div>
  );

  if (error || !tournament) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: C.bg, fontFamily: font, color: C.textMuted }}>
      <style>{buildCSS()}</style>
      <div style={{ textAlign: "center" }}><img src="/dip-logo.png" alt="" style={{ maxWidth: 150, width: "50%", height: "auto", marginBottom: 10 }} /><div>{error || "Tournament not found"}</div></div>
    </div>
  );

  const t = tournament;
  const isLive = t.status === "playing";
  const isPaused = t.status === "paused";
  const formatLabel = t.format === "roundrobin" ? "Round Robin" : t.format === "bracket" ? "Bracket" : "King of the Hill";

  // Build leaderboard from all available rounds
  const allRounds = [...(t.rounds || [])];
  if ((isLive || isPaused) && t.currentCourts && t.currentCourts.some(c => c.scoreA !== null || c.scoreB !== null)) {
    allRounds.push({ courts: t.currentCourts });
  }
  // For RR
  if ((isLive || isPaused) && t.format === "roundrobin" && t.schedule) {
    const compRounds = [...(t.completedRounds || [])];
    const currentIdx = t.currentMatchRound || 0;
    const currentMatches = t.schedule[currentIdx];
    if (currentMatches && currentMatches.some(m => m.scoreA !== null || m.scoreB !== null)) {
      compRounds.push({ courts: currentMatches });
    }
    allRounds.push(...compRounds);
  }
  const board = calcLeaderboard(allRounds);

  return (
    <div style={{ fontFamily: font, background: C.bg, color: C.text, minHeight: "100vh", maxWidth: 520, margin: "0 auto" }}>
      <style>{buildCSS()}</style>
      <div style={{ padding: "14px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, display: "flex", alignItems: "center", gap: 8 }}><img src="/dip-logo.png" alt="" style={{ height: 26, width: "auto" }} />{t.name}</div>
          <div style={{ fontSize: 12, color: C.textSec }}>{formatLabel} · {(t.players || []).length} players{t.currentRound ? ` · Round ${t.currentRound}` : ""}{t.format === "roundrobin" && t.schedule ? ` · Round ${(t.currentMatchRound || 0) + 1}/${t.schedule.length}` : ""}</div>
        </div>
        {isLive ? (
          <span className="badge live-badge" style={{ animation: "pulse 2s infinite" }}>LIVE</span>
        ) : isPaused ? (
          <span className="badge paused-badge"><I.PauseCircle size={12} /> PAUSED</span>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button className="btn btn-outline btn-sm" onClick={() => exportResultsImage({ name: t.name, format: t.format, rounds: allRounds, players: t.players, finishedAt: t.finishedAt, board, isTeamFormat: t.format === "bracket" || t.format === "roundrobin", teamWinner: null, indivWinner: board[0] })} style={{ fontSize: 11, color: C.blue, borderColor: C.blue }}>
              <I.Download size={12} /> Export
            </button>
            <span className="badge" style={{ background: C.surfaceHover, color: C.textSec }}>FINAL</span>
          </div>
        )}
      </div>

      {/* Synced timer (visible to all) */}
      {(isLive || isPaused) && t.gameMinutes && (
        <div style={{ padding: "12px 16px 0" }}>
          <GameTimer minutes={t.gameMinutes} timerState={t.timerState} isAdmin={false} />
        </div>
      )}

      {/* Current round (live or paused) — KotH */}
      {(isLive || isPaused) && t.currentCourts && (
        <div style={{ padding: "12px 16px" }}>
          <div className="section-title">Current Round {t.currentRound || ""}</div>
          {t.currentCourts.map((c, ci) => {
            const has = c.scoreA !== null && c.scoreB !== null;
            const aW = has && (c.scoreA||0) > (c.scoreB||0), bW = has && (c.scoreB||0) > (c.scoreA||0);
            return (
              <div key={ci} className="card" style={{ padding: "10px 14px", marginBottom: 6 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ flex: 1 }}>
                    {c.courtNumber && <span className="badge court-badge" style={{ marginRight: 8 }}>Ct {c.courtNumber}</span>}
                    <span style={{ fontWeight: aW?700:500, color: aW?C.accent:C.text, fontSize: 14 }}>{(c.teamA||[]).map(p => p.name).join(" & ")}</span>
                    <span style={{ color: C.textMuted, margin: "0 6px", fontSize: 13 }}>vs</span>
                    <span style={{ fontWeight: bW?700:500, color: bW?C.accent:C.text, fontSize: 14 }}>{(c.teamB||[]).map(p => p.name).join(" & ")}</span>
                  </div>
                  {has ? (
                    <span style={{ fontFamily: fontMono, fontWeight: 700, fontSize: 16, flexShrink: 0, marginLeft: 8 }}>
                      <span style={{ color: aW?C.accent:C.textMuted }}>{c.scoreA}</span>
                      <span style={{ color: C.textMuted, margin: "0 3px" }}>-</span>
                      <span style={{ color: bW?C.accent:C.textMuted }}>{c.scoreB}</span>
                    </span>
                  ) : (
                    <span style={{ fontSize: 12, color: C.textMuted, fontStyle: "italic", flexShrink: 0 }}>In progress</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* RR live/paused current matches */}
      {(isLive || isPaused) && t.format === "roundrobin" && t.schedule && (() => {
        const currentIdx = t.currentMatchRound || 0;
        const currentMatches = t.schedule[currentIdx] || [];
        return (
          <div style={{ padding: "12px 16px" }}>
            <div className="section-title">Round {currentIdx + 1} of {t.schedule.length}</div>
            {currentMatches.map((m, mi) => {
              const has = m.scoreA !== null && m.scoreB !== null;
              const aW = has && (m.scoreA||0) > (m.scoreB||0), bW = has && (m.scoreB||0) > (m.scoreA||0);
              return (
                <div key={mi} className="card" style={{ padding: "10px 14px", marginBottom: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ flex: 1, fontSize: 14 }}>
                      <span style={{ fontWeight: aW?700:500, color: aW?C.accent:C.text }}>{m.teamAName || "Team A"}</span>
                      <span style={{ color: C.textMuted, margin: "0 6px" }}>vs</span>
                      <span style={{ fontWeight: bW?700:500, color: bW?C.accent:C.text }}>{m.teamBName || "Team B"}</span>
                    </div>
                    {has ? (
                      <span style={{ fontFamily: fontMono, fontWeight: 700, fontSize: 16, flexShrink: 0 }}>
                        <span style={{ color: aW?C.accent:C.textMuted }}>{m.scoreA}</span>
                        <span style={{ color: C.textMuted, margin: "0 3px" }}>-</span>
                        <span style={{ color: bW?C.accent:C.textMuted }}>{m.scoreB}</span>
                      </span>
                    ) : <span style={{ fontSize: 12, color: C.textMuted, fontStyle: "italic" }}>In progress</span>}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Leaderboard with toggle */}
      {board.length > 0 && (
        <div style={{ padding: "0 16px 16px" }}>
          <div className="section-title">{isLive ? "Live Standings" : isPaused ? "Standings (Paused)" : "Final Standings"}</div>
          <LeaderboardTable rounds={allRounds} />
        </div>
      )}

      {/* Round history — KotH completed rounds */}
      {(t.rounds || []).length > 0 && (
        <div style={{ padding: "0 16px 16px" }}>
          <div className="section-title">Completed Rounds ({(t.rounds || []).length})</div>
          <RoundHistoryList rounds={t.rounds} />
        </div>
      )}

      {/* RR completed rounds */}
      {(t.completedRounds || []).length > 0 && (
        <div style={{ padding: "0 16px 16px" }}>
          <div className="section-title">Completed Rounds ({(t.completedRounds || []).length})</div>
          <RoundHistoryList rounds={t.completedRounds} />
        </div>
      )}

      <div style={{ textAlign: "center", padding: "20px 16px 40px", color: C.textMuted, fontSize: 12 }}>
        Spectator view · Scores update live · dinksinparadise.com
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// LEAGUES (team-vs-team club play)
// ════════════════════════════════════════════════════════════════════
// League play ships to production behind this gate so the season can be set
// up for real before the club sees it. Superadmins always get the live
// feature; everyone else sees the placeholder. Flip to true to launch.
const LEAGUES_PUBLIC = false;

function LeaguesComingSoon({ isSuperAdmin }) {
  return (
    <div style={{ padding: 16 }}>
      <div className="card" style={{ textAlign: "center", padding: "36px 22px" }}>
        <div style={{ fontSize: 40, lineHeight: 1, marginBottom: 14 }}>🏝️</div>
        <div style={{ fontSize: 21, fontWeight: 800, letterSpacing: "-0.02em", marginBottom: 8 }}>League Play is coming</div>
        <div style={{ fontSize: 14, color: C.textSec, lineHeight: 1.6, maxWidth: 360, margin: "0 auto" }}>
          Season-long team play is on the way — ability-level leagues, weekly
          match nights, live scoring and standings.
        </div>
        <div style={{ marginTop: 18, fontSize: 12.5, color: C.textMuted }}>
          Watch this space — your captain will let you know when sign-ups open.
        </div>
        {isSuperAdmin && (
          <div style={{ marginTop: 22, paddingTop: 16, borderTop: `1px solid ${C.border}`, fontSize: 12, color: C.textMuted }}>
            You're seeing this because the gate is on. Superadmins get the live
            feature; set <b style={{ color: C.text }}>LEAGUES_PUBLIC = true</b> to launch it for everyone.
          </div>
        )}
      </div>
    </div>
  );
}

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const levelLabel = (lvl) => (lvl == null ? "Open" : Number(lvl).toFixed(1));
// Men's leagues take men, women's take women, mixed takes both. Players with no
// gender on file are allowed through rather than silently hidden.
function divisionAllows(division, gender) {
  if (division === "mens") return gender !== "F";
  if (division === "womens") return gender !== "M";
  return true;
}

function LeagueStandings({ rows }) {
  if (!rows || rows.length === 0) return <div className="empty-state">No games played yet.</div>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <div style={{ display: "grid", gridTemplateColumns: "24px 1fr 54px 44px 66px", gap: 8, padding: "0 10px 6px", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: C.textMuted }}>
        <span>#</span><span>Team</span><span style={{ textAlign: "center" }}>Nights</span><span style={{ textAlign: "center" }}>Pts</span><span style={{ textAlign: "right" }}>Last 5</span>
      </div>
      {rows.map(r => (
        <div key={r.teamId} className="card" style={{ display: "grid", gridTemplateColumns: "24px 1fr 54px 44px 66px", gap: 8, alignItems: "center", padding: "10px" }}>
          <span style={{ fontFamily: fontMono, fontWeight: 700, color: r.rank <= 3 ? C.gold : C.textMuted, fontSize: 13 }}>{r.rank}</span>
          <span style={{ fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0 }}>
            {r.crestEmoji && <span>{r.crestEmoji}</span>}
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.teamName}</span>
          </span>
          <span style={{ textAlign: "center", fontFamily: fontMono, fontWeight: 700 }}>{r.nightsWon}&#8209;{r.nightsLost}</span>
          <span style={{ textAlign: "center", fontFamily: fontMono, color: C.textSec }}>{r.gamePoints}</span>
          <span style={{ display: "inline-flex", gap: 3, justifyContent: "flex-end" }}>
            {r.lastResults.map((w, i) => (
              <span key={i} style={{ width: 14, height: 14, borderRadius: 4, fontSize: 9, fontWeight: 800, display: "grid", placeItems: "center", color: "#fff", background: w === "W" ? "#1b9d62" : C.red }}>{w}</span>
            ))}
          </span>
        </div>
      ))}
    </div>
  );
}

// Suggest a full lineup from a roster: in mixed each pair is a man + a woman,
// otherwise players are paired in roster order. Returns [{p1,p2}, ...].
function suggestPairs(roster, division, pairsPerTeam) {
  const out = Array.from({ length: pairsPerTeam }, () => ({ p1: "", p2: "" }));
  if (division === "mixed") {
    const men = roster.filter(m => m.gender === "M").map(m => m.playerName);
    const women = roster.filter(m => m.gender === "F").map(m => m.playerName);
    const rest = roster.filter(m => m.gender !== "M" && m.gender !== "F").map(m => m.playerName);
    for (let i = 0; i < pairsPerTeam; i++) {
      out[i].p1 = men[i] || rest.shift() || "";
      out[i].p2 = women[i] || rest.shift() || "";
    }
  } else {
    const names = roster.map(m => m.playerName);
    for (let i = 0; i < pairsPerTeam; i++) {
      out[i].p1 = names[2 * i] || "";
      out[i].p2 = names[2 * i + 1] || "";
    }
  }
  return out;
}

function TeamLineupEditor({ fixture, teamId, teamName, pairsPerTeam, roster, division, onSave, canEdit }) {
  const buildInit = () => {
    const existing = fixture.lineup.filter(l => l.teamId === teamId).sort((a, b) => a.pairNumber - b.pairNumber);
    // No saved lineup yet? Start from a sensible suggestion instead of blanks.
    if (existing.length === 0 && roster.length >= pairsPerTeam * 2) return suggestPairs(roster, division, pairsPerTeam);
    return Array.from({ length: pairsPerTeam }, (_, i) => {
      const ex = existing.find(e => e.pairNumber === i + 1);
      return { p1: ex ? ex.player1Name : "", p2: ex ? ex.player2Name : "" };
    });
  };
  const [pairs, setPairs] = useState(buildInit);
  useEffect(() => { setPairs(buildInit()); /* eslint-disable-next-line */ }, [fixture.id, teamId]);
  const set = (i, k, v) => setPairs(ps => ps.map((p, j) => j === i ? { ...p, [k]: v } : p));
  const save = () => onSave(teamId, pairs.map((p, i) => ({ pairNumber: i + 1, player1Name: p.p1, player2Name: p.p2 })).filter(p => p.player1Name || p.player2Name));
  // Only roster players; a name already chosen on this team can't be picked twice.
  const chosen = new Set(pairs.flatMap(p => [p.p1, p.p2]).filter(Boolean));
  const optsFor = (cur) => roster.map(m => m.playerName).filter(n => n === cur || !chosen.has(n));
  const filled = pairs.filter(p => p.p1 && p.p2).length;
  const saved = fixture.lineup.some(l => l.teamId === teamId);
  return (
    <div className="card" style={{ marginBottom: 10 }}>
      <div className="section-title" style={{ marginBottom: 10 }}>{teamName} · lineup {roster.length > 0 && <span style={{ color: filled === pairsPerTeam ? C.textMuted : C.orange, fontWeight: 500 }}>· {filled}/{pairsPerTeam} pairs{saved ? " · saved" : ""}</span>}</div>
      {roster.length === 0 && <div style={{ fontSize: 12, color: C.orange, marginBottom: 6 }}>No roster yet — add players to {teamName} on the Teams tab first.</div>}
      {roster.length > 0 && !saved && filled === pairsPerTeam && <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 8 }}>Suggested from the roster — change anyone, then save.</div>}
      {pairs.map((p, i) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: "22px 1fr 1fr", gap: 6, marginBottom: 6, alignItems: "center" }}>
          <span style={{ fontFamily: fontMono, fontSize: 12, color: C.textMuted }}>{i + 1}</span>
          <select className="input" value={p.p1} onChange={e => set(i, "p1", e.target.value)} disabled={!canEdit || roster.length === 0} style={{ fontSize: 13, padding: "8px 10px" }}>
            <option value="">— player —</option>
            {optsFor(p.p1).map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <select className="input" value={p.p2} onChange={e => set(i, "p2", e.target.value)} disabled={!canEdit || roster.length === 0} style={{ fontSize: 13, padding: "8px 10px" }}>
            <option value="">— player —</option>
            {optsFor(p.p2).map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      ))}
      {canEdit && roster.length > 0 && <button className="btn btn-outline btn-sm btn-full" style={{ marginTop: 6 }} onClick={save}>Save {teamName} lineup</button>}
    </div>
  );
}

function LeagueGameRow({ game, homeLabel, awayLabel, canEdit, onScore, onForfeit }) {
  const [h, setH] = useState(game.homeScore ?? "");
  const [a, setA] = useState(game.awayScore ?? "");
  useEffect(() => { setH(game.homeScore ?? ""); setA(game.awayScore ?? ""); }, [game.id, game.homeScore, game.awayScore]);
  const decided = game.winnerSide != null;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 8, alignItems: "center", padding: "8px 0", borderTop: `1px solid ${C.borderLight}` }}>
      <span style={{ fontSize: 12, color: C.textSec, textAlign: "right", fontWeight: game.winnerSide === "Home" ? 800 : 500 }}>
        {game.isForfeit && game.forfeitToSide === "Home" ? "① " : ""}{homeLabel}
        {decided && <span style={{ marginLeft: 6, fontFamily: fontMono, color: C.textMuted }}>+{game.homePoints}</span>}
      </span>
      <div style={{ display: "flex", alignItems: "center", gap: 5, justifyContent: "center" }}>
        <input className="input-score" style={{ width: 46, fontSize: 16, padding: "6px 4px" }} value={h} onChange={e => setH(e.target.value)} inputMode="numeric" disabled={!canEdit} />
        <span style={{ color: C.textMuted, fontSize: 12, fontFamily: fontMono }}>G{game.gameNumber}</span>
        <input className="input-score" style={{ width: 46, fontSize: 16, padding: "6px 4px" }} value={a} onChange={e => setA(e.target.value)} inputMode="numeric" disabled={!canEdit} />
      </div>
      <span style={{ fontSize: 12, color: C.textSec, fontWeight: game.winnerSide === "Away" ? 800 : 500 }}>
        {game.isForfeit && game.forfeitToSide === "Away" ? "① " : ""}{awayLabel}
        {decided && <span style={{ marginLeft: 6, fontFamily: fontMono, color: C.textMuted }}>+{game.awayPoints}</span>}
      </span>
      {canEdit && (
        <div style={{ gridColumn: "1 / -1", display: "flex", gap: 6, justifyContent: "center", marginTop: 2 }}>
          <button className="btn btn-primary btn-sm" style={{ padding: "4px 14px", fontSize: 11 }} onClick={() => onScore(game.id, parseInt(h, 10) || 0, parseInt(a, 10) || 0)}>Save</button>
          {!decided && <button className="btn btn-ghost btn-sm" style={{ padding: "4px 8px", fontSize: 10 }} onClick={() => onForfeit(game.id, "Home")} title="Forfeit to home">FF◀</button>}
          {!decided && <button className="btn btn-ghost btn-sm" style={{ padding: "4px 8px", fontSize: 10 }} onClick={() => onForfeit(game.id, "Away")} title="Forfeit to away">FF▶</button>}
        </div>
      )}
    </div>
  );
}

function SetupStep({ n, label, hint, done, active, disabled, onClick }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ width: "100%", textAlign: "left", background: "none", border: "none", cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.45 : 1, display: "flex", alignItems: "center", gap: 10, padding: "8px 0", color: C.text, fontFamily: font }}>
      <span style={{ width: 22, height: 22, borderRadius: "50%", flexShrink: 0, display: "grid", placeItems: "center", fontSize: 11, fontWeight: 800, background: done ? C.accent : active ? C.accentDim : C.surfaceHover, color: done ? C.bg : active ? C.accent : C.textMuted, border: active && !done ? `1px solid ${C.accent}` : "none" }}>{done ? "✓" : n}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontWeight: 700, fontSize: 14, color: done ? C.textSec : C.text }}>{label}</span>
        {hint && active && <span style={{ display: "block", fontSize: 11, color: C.textMuted }}>{hint}</span>}
      </span>
      {!disabled && !done && <I.ChevRight size={14} />}
    </button>
  );
}

function LeaguesTab({ leagues, players, session, canManage, canEditLeague, canDeleteLeague, isSuperAdmin, realtime, fixtureCb }) {
  const [view, setView] = useState("hub");            // hub | season | fixture
  const [season, setSeason] = useState(null);
  const [fixture, setFixture] = useState(null);
  const [subtab, setSubtab] = useState("standings");  // standings | schedule | rosters
  const [busy, setBusy] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [seasonForLeague, setSeasonForLeague] = useState(null); // league obj -> new-season modal
  const [expandTeam, setExpandTeam] = useState(null);

  const run = async (fn) => { setBusy(true); try { return await fn(); } catch (e) { alert("Error: " + e.message); } finally { setBusy(false); } };
  const mayEditSeason = season && canEditLeague({ createdBy: season.createdBy });

  // Land on Teams whenever the season still needs teams/rosters, else Standings.
  const landingTab = (s) => {
    const need = ((s.rules && s.rules.pairsPerTeam) || 3) * 2;
    const incomplete = s.teams.length < 2 || s.teams.some(t => t.members.filter(m => m.role !== "bench").length < need);
    return (s.status === "setup" || incomplete) ? "rosters" : "standings";
  };
  const openLeague = (l) => run(async () => {
    if (l.currentSeason) { const s = await api.getSeason(l.currentSeason.id); setSeason(s); setSubtab(landingTab(s)); setExpandTeam(null); setView("season"); }
    else if (canEditLeague(l)) { setSeasonForLeague(l); }
    else alert("This league has no season yet.");
  });
  const openSeason = (id) => run(async () => { const s = await api.getSeason(id); setSeason(s); setSubtab(landingTab(s)); setExpandTeam(null); setView("season"); });
  const reloadSeason = async () => { if (season) { const s = await api.getSeason(season.id); setSeason(s); } };

  const openFixture = (id) => run(async () => {
    const f = await api.getFixture(id); setFixture(f); setView("fixture");
    fixtureCb.current = (dto) => { if (dto.id === id) setFixture(dto); };
    if (realtime.current) realtime.current.watchFixture(id);
  });
  const leaveFixture = () => {
    if (fixture && realtime.current) realtime.current.unwatchFixture(fixture.id);
    fixtureCb.current = null; setFixture(null); setView("season"); reloadSeason();
  };

  // ---------- FIXTURE (match night) ----------
  if (view === "fixture" && fixture) {
    const pairsPerTeam = (season && season.rules && season.rules.pairsPerTeam) || 3;
    const canScore = season ? canEditLeague({ createdBy: season.createdBy }) : false;
    const lineupByPair = Object.fromEntries(fixture.lineup.map(l => [l.id, l]));
    const pairLabel = (id) => { const p = lineupByPair[id]; return p ? `${p.player1Name}/${p.player2Name}` : "?"; };
    const homeHasLineup = fixture.lineup.some(l => l.teamId === fixture.homeTeamId);
    const awayHasLineup = fixture.lineup.some(l => l.teamId === fixture.awayTeamId);
    const hasPairings = fixture.pairings.length > 0;
    // Lineups may only be filled from that team's own roster.
    const rosterOf = (tid) => {
      const t = (season && season.teams || []).find(x => x.id === tid);
      return t ? t.members.filter(m => m.role !== "bench") : [];
    };
    const homeRoster = rosterOf(fixture.homeTeamId);
    const awayRoster = rosterOf(fixture.awayTeamId);
    // Enough people on both rosters is all that's required — the lineups are
    // suggested and saved for you when you start the night.
    const rostersOk = homeRoster.length >= pairsPerTeam * 2 && awayRoster.length >= pairsPerTeam * 2;
    const blockedReason = rostersOk ? null
      : `Both teams need ${pairsPerTeam * 2} players on their roster (Teams tab) before this night can be played.`;
    return (
      <div style={{ padding: 16 }}>
        <button className="btn btn-ghost btn-sm" onClick={leaveFixture} style={{ marginBottom: 8 }}><I.ChevLeft size={16} /> Back</button>
        <div className="card" style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 8 }}>Week {fixture.weekNumber} · {fixture.scheduledDate}{fixture.phase !== "regular" ? ` · ${fixture.phase}` : ""}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", gap: 10 }}>
            <span style={{ fontWeight: 800, textAlign: "right" }}>{fixture.homeTeamName}</span>
            <span style={{ fontFamily: fontMono, fontWeight: 800, fontSize: 26 }}>{fixture.homeLeaguePoints}<span style={{ color: C.textMuted, fontSize: 14 }}> – </span>{fixture.awayLeaguePoints}</span>
            <span style={{ fontWeight: 800 }}>{fixture.awayTeamName}</span>
          </div>
          <div style={{ textAlign: "center", fontSize: 11, color: C.textMuted, marginTop: 6 }}>league points · pairings {fixture.homePairingsWon}–{fixture.awayPairingsWon} · {fixture.status}</div>
          {fixture.status === "final" && !fixture.winnerTeamId && <div style={{ textAlign: "center", fontSize: 12, color: C.orange, fontWeight: 700, marginTop: 4 }}>Dead even — no winner</div>}
        </div>

        {!hasPairings && (
          <>
            <TeamLineupEditor fixture={fixture} teamId={fixture.homeTeamId} teamName={fixture.homeTeamName} pairsPerTeam={pairsPerTeam} roster={homeRoster} division={season.division} canEdit={canScore}
              onSave={(teamId, pairs) => run(async () => setFixture(await api.setLineup(fixture.id, { teamId, pairs })))} />
            <TeamLineupEditor fixture={fixture} teamId={fixture.awayTeamId} teamName={fixture.awayTeamName} pairsPerTeam={pairsPerTeam} roster={awayRoster} division={season.division} canEdit={canScore}
              onSave={(teamId, pairs) => run(async () => setFixture(await api.setLineup(fixture.id, { teamId, pairs })))} />
            {canScore && (
              <button className="btn btn-primary btn-full" disabled={busy || !rostersOk} style={{ marginTop: 6 }}
                onClick={() => run(async () => {
                  // Save whatever each side needs, then start — one click from a full roster.
                  let f = fixture;
                  for (const [tid, rr] of [[fixture.homeTeamId, homeRoster], [fixture.awayTeamId, awayRoster]]) {
                    if (f.lineup.some(l => l.teamId === tid)) continue;
                    const pairs = suggestPairs(rr, season.division, pairsPerTeam)
                      .map((p, i) => ({ pairNumber: i + 1, player1Name: p.p1, player2Name: p.p2 }))
                      .filter(p => p.player1Name && p.player2Name);
                    f = await api.setLineup(fixture.id, { teamId: tid, pairs });
                  }
                  setFixture(await api.generatePairings(fixture.id));
                })}>
                {homeHasLineup && awayHasLineup ? "Generate pairings & start night" : "Use these lineups & start night"}
              </button>
            )}
            {blockedReason && <div style={{ fontSize: 12, color: C.orange, textAlign: "center", marginTop: 8, fontWeight: 600 }}>{blockedReason}</div>}
          </>
        )}

        {hasPairings && (
          <>
            {fixture.pairings.map(p => (
              <div key={p.id} className="card" style={{ marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                  <span style={{ fontSize: 12, fontWeight: 700 }}>{pairLabel(p.homeLineupPairId)} <span style={{ color: C.textMuted }}>vs</span> {pairLabel(p.awayLineupPairId)}</span>
                  <span style={{ fontFamily: fontMono, fontSize: 12, color: C.textSec }}>{p.homePairPoints}–{p.awayPairPoints}{p.winnerLineupPairId ? (p.winnerLineupPairId === p.homeLineupPairId ? " ◀" : " ▶") : ""}</span>
                </div>
                {p.games.map(g => (
                  <LeagueGameRow key={g.id} game={g} homeLabel={pairLabel(p.homeLineupPairId)} awayLabel={pairLabel(p.awayLineupPairId)} canEdit={canScore}
                    onScore={(gid, hs, as) => run(async () => setFixture(await api.scoreGame(gid, hs, as)))}
                    onForfeit={(gid, side) => run(async () => setFixture(await api.forfeitGame(gid, side)))} />
                ))}
              </div>
            ))}
            {canScore && fixture.status !== "final" && (
              <button className="btn btn-outline btn-full" disabled={busy} onClick={() => run(async () => setFixture(await api.finalizeFixture(fixture.id)))}>Finalize night</button>
            )}
            {canScore && (
              <button className="btn btn-ghost btn-sm btn-full" style={{ marginTop: 8 }} disabled={busy}
                onClick={() => { if (window.confirm("Clear both lineups and reset this night? Scores will be lost.")) run(async () => { await api.setLineup(fixture.id, { teamId: fixture.awayTeamId, pairs: [] }); setFixture(await api.setLineup(fixture.id, { teamId: fixture.homeTeamId, pairs: [] })); }); }}>
                Reset lineups
              </button>
            )}
          </>
        )}
        <datalist id="lg-players">{players.map(p => <option key={p.id} value={p.name} />)}</datalist>
      </div>
    );
  }

  // ---------- SEASON ----------
  if (view === "season" && season) {
    const fixturesByWeek = {};
    season.fixtures.forEach(f => { (fixturesByWeek[f.weekNumber] = fixturesByWeek[f.weekNumber] || []).push(f); });
    const weeks = Object.keys(fixturesByWeek).map(Number).sort((a, b) => a - b);
    const hasTeams = season.teams.length >= 2;
    const hasSchedule = season.fixtures.length > 0;
    const isSetup = season.status === "setup";
    // A team can field a night only with enough non-bench players (pairs x2), and
    // in mixed with at least one man AND one woman per pair (the split beyond that
    // minimum is free). Players with no gender on file can fill either side.
    const pairsPerTeam = (season.rules && season.rules.pairsPerTeam) || 3;
    const needPerTeam = pairsPerTeam * 2;
    const activeOf = (t) => t.members.filter(m => m.role !== "bench");
    const activeCount = (t) => activeOf(t).length;
    const genderShortfall = (t) => {
      if (season.division !== "mixed") return null;
      const a = activeOf(t);
      const men = a.filter(m => m.gender === "M").length;
      const women = a.filter(m => m.gender === "F").length;
      const unknown = a.filter(m => m.gender !== "M" && m.gender !== "F").length;
      const needM = Math.max(0, pairsPerTeam - men - unknown);
      const needF = Math.max(0, pairsPerTeam - women - unknown);
      if (!needM && !needF) return null;
      return [
        needM ? `${needM} more ${needM === 1 ? "man" : "men"}` : null,
        needF ? `${needF} more ${needF === 1 ? "woman" : "women"}` : null,
      ].filter(Boolean).join(" and ");
    };
    const teamReady = (t) => activeCount(t) >= needPerTeam && !genderShortfall(t);
    const shortTeams = season.teams.filter(t => !teamReady(t));
    const rostersReady = hasTeams && shortTeams.length === 0;
    // A player belongs to one team per season — everyone else is unavailable.
    const takenNames = new Set(season.teams.flatMap(t => t.members.map(m => m.playerName)));
    // Eligible = right gender for the division, and not already rostered this season.
    const availablePlayers = players.filter(p => !takenNames.has(p.name) && divisionAllows(season.division, p.gender));
    // A captain leads one team and can't be rostered elsewhere; they may be on
    // this team's roster (playing captain) or on no roster at all.
    const captainChoicesFor = (team) => {
      const otherCaptains = new Set(season.teams.filter(x => x.id !== team.id).map(x => x.captainName).filter(Boolean));
      const otherRostered = new Set(season.teams.filter(x => x.id !== team.id).flatMap(x => x.members.map(m => m.playerName)));
      return players
        .filter(p => divisionAllows(season.division, p.gender))
        .filter(p => !otherCaptains.has(p.name) && !otherRostered.has(p.name))
        .map(p => p.name);
    };
    // Until the user picks one (null), open the first team still needing players.
    // Explicitly collapsing sets "", which stays collapsed.
    const openTeam = expandTeam === null ? (shortTeams.length ? shortTeams[0].id : null) : expandTeam;
    return (
      <div style={{ padding: 16 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => { setView("hub"); setSeason(null); }} style={{ marginBottom: 8 }}><I.ChevLeft size={16} /> Leagues</button>
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 19, fontWeight: 800 }}>{season.leagueName}</span>
            <DivisionBadge division={season.division} />
            <span className="badge live-badge">{levelLabel(season.abilityLevel)}</span>
          </div>
          <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>{season.label} · {season.status} · {DAYS[new Date(season.startDate + "T00:00:00").getDay()] || season.playNight} nights</div>
        </div>

        {mayEditSeason && (isSetup || !rostersReady || !hasSchedule) && (
          <div className="card" style={{ marginBottom: 12, border: `1px solid ${rostersReady && hasSchedule ? C.accent : C.orange}` }}>
            <div className="section-title" style={{ color: rostersReady && hasSchedule ? C.accent : C.orange, marginBottom: 8 }}>
              {isSetup ? "Set up this season" : "This season needs attention"}
            </div>
            <SetupStep n={1} done={hasTeams} active={!hasTeams} label={`Add teams${season.teams.length ? ` · ${season.teams.length} added` : ""}`} hint="Add at least 2 teams." onClick={() => setSubtab("rosters")} />
            <SetupStep n={2} done={rostersReady} active={hasTeams && !rostersReady} disabled={!hasTeams}
              label={`Add players to each team${hasTeams ? ` · ${season.teams.length - shortTeams.length}/${season.teams.length} ready` : ""}`}
              hint={shortTeams.length
                ? `Each team needs ${needPerTeam} players${season.division === "mixed" ? `, including at least ${pairsPerTeam} men and ${pairsPerTeam} women` : ""}. Still short: ${shortTeams.map(t => `${t.name} (${activeCount(t) < needPerTeam ? `${needPerTeam - activeCount(t)} more` : genderShortfall(t)})`).join(", ")}.`
                : `Each team needs ${needPerTeam} players${season.division === "mixed" ? `, including at least ${pairsPerTeam} men and ${pairsPerTeam} women` : ""}.`}
              onClick={() => setSubtab("rosters")} />
            <SetupStep n={3} done={hasSchedule} active={rostersReady && !hasSchedule} disabled={!hasTeams} label="Generate the schedule" hint="Builds the round-robin fixtures." onClick={() => run(async () => { await api.generateSchedule(season.id); await reloadSeason(); setSubtab("schedule"); })} />
            {isSetup && <SetupStep n={4} done={false} active={hasSchedule && rostersReady} disabled={!hasSchedule || !rostersReady} label="Start the season" hint="Opens match nights for scoring." onClick={() => run(async () => { await api.setSeasonStatus(season.id, "active"); await reloadSeason(); })} />}
          </div>
        )}

        <div className="tab-bar" style={{ borderRadius: 10, marginBottom: 12, border: `1px solid ${C.border}` }}>
          {[["standings", "Standings"], ["schedule", "Schedule"], ["rosters", "Teams"]].map(([k, lbl]) => (
            <button key={k} className={`tab ${subtab === k ? "active" : ""}`} onClick={() => setSubtab(k)}>{lbl}</button>
          ))}
        </div>

        {mayEditSeason && !isSetup && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
            <button className="btn btn-outline btn-sm" disabled={busy || !hasTeams} onClick={() => run(async () => { await api.generateSchedule(season.id); await reloadSeason(); })}>Regenerate schedule</button>
            <button className="btn btn-outline btn-sm" disabled={busy} onClick={() => run(async () => { await api.seedPlayoffs(season.id); await reloadSeason(); })}>Seed playoffs</button>
          </div>
        )}

        {subtab === "standings" && (season.teams.length === 0
          ? <div className="empty-state">No teams yet.{mayEditSeason ? " Use the setup steps above to add teams." : ""}</div>
          : <LeagueStandings rows={season.standings} />)}

        {subtab === "schedule" && (
          weeks.length === 0 ? <div className="empty-state">No schedule yet.{mayEditSeason ? " Add teams, then Generate schedule." : ""}</div> :
          weeks.map(wk => (
            <div key={wk} style={{ marginBottom: 14 }}>
              <div className="section-title">{fixturesByWeek[wk][0].phase !== "regular" ? fixturesByWeek[wk][0].phase : `Week ${wk}`} · {fixturesByWeek[wk][0].scheduledDate}</div>
              {fixturesByWeek[wk].map(f => (
                <button key={f.id} className="card" onClick={() => openFixture(f.id)} style={{ width: "100%", textAlign: "left", marginBottom: 6, display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 8, alignItems: "center", cursor: "pointer", border: `1px solid ${C.border}` }}>
                  <span style={{ fontWeight: 700, textAlign: "right", fontSize: 13 }}>{f.homeTeamName}</span>
                  <span style={{ fontFamily: fontMono, fontWeight: 700, color: f.status === "final" ? C.text : C.textMuted, fontSize: 13 }}>{f.status === "scheduled" ? "vs" : `${f.homeLeaguePoints}–${f.awayLeaguePoints}`}</span>
                  <span style={{ fontWeight: 700, fontSize: 13 }}>{f.awayTeamName}</span>
                </button>
              ))}
            </div>
          ))
        )}

        {subtab === "rosters" && (
          <>
            {season.teams.length === 0 && <div className="empty-state" style={{ padding: "16px 20px" }}>{mayEditSeason ? "Add your teams below to get the season going." : "No teams yet."}</div>}
            {season.teams.map(t => (
              <div key={t.id} className="card" style={{ marginBottom: 8 }}>
                <button onClick={() => setExpandTeam(openTeam === t.id ? "" : t.id)} style={{ width: "100%", background: "none", border: "none", color: C.text, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", padding: 0 }}>
                  <span style={{ fontWeight: 700, display: "inline-flex", gap: 6, alignItems: "center" }}>{t.crestEmoji}<span>{t.name}</span></span>
                  <span style={{ fontSize: 12, color: teamReady(t) ? C.textMuted : C.orange, display: "inline-flex", alignItems: "center", gap: 4, textAlign: "right" }}>
                    {teamReady(t)
                      ? `${activeCount(t)} players`
                      : activeCount(t) < needPerTeam
                        ? `${activeCount(t)}/${needPerTeam} players — needs ${needPerTeam - activeCount(t)} more`
                        : `needs ${genderShortfall(t)}`}
                    <I.ChevDown size={14} />
                  </span>
                </button>
                {openTeam === t.id && (
                  <div style={{ marginTop: 10, borderTop: `1px solid ${C.borderLight}`, paddingTop: 10 }}>
                    <div style={{ fontSize: 12, marginBottom: 8, color: C.textSec }}>
                      Captain: <b style={{ color: t.captainName ? C.text : C.orange }}>{t.captainName || "not set"}</b>
                      {t.captainName && !t.members.some(m => m.playerName === t.captainName) && (
                        <span style={{ color: C.textMuted }}> · not on the roster (non-playing)
                          {mayEditSeason && <button className="btn btn-ghost btn-sm" style={{ padding: "1px 6px", fontSize: 10 }} onClick={() => run(async () => { const p = players.find(pl => pl.name === t.captainName); await api.addMember(t.id, { playerId: p ? p.id : null, playerName: t.captainName, role: "player" }); await reloadSeason(); })}>Add to roster</button>}
                        </span>
                      )}
                    </div>
                    {t.members.length === 0 && <div style={{ fontSize: 12, color: C.textMuted }}>No roster members.</div>}
                    {t.members.map(m => (
                      <div key={m.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", fontSize: 13, gap: 6 }}>
                        <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{m.playerName} {m.playerName === t.captainName && <span className="badge admin-badge" style={{ fontSize: 8, padding: "1px 6px" }}>CAPTAIN</span>}{m.role === "injuryreplacement" && <span className="badge court-badge" style={{ fontSize: 8, padding: "1px 6px" }}>INJ SUB</span>}{m.role === "bench" && <span style={{ fontSize: 10, color: C.textMuted }}> bench</span>}</span>
                        {mayEditSeason && (
                          <button className="btn btn-ghost btn-sm" style={{ padding: 3, flexShrink: 0 }} onClick={() => run(async () => { await api.deleteMember(t.id, m.id); await reloadSeason(); })}><I.Trash size={12} /></button>
                        )}
                      </div>
                    ))}
                    {mayEditSeason && <AddMemberForm available={availablePlayers} onAdd={(m) => run(async () => { await api.addMember(t.id, m); await reloadSeason(); })} />}
                    {mayEditSeason && (
                      <TeamNameEditor team={t}
                        captainChoices={captainChoicesFor(t)}
                        onSave={(name, crest, captain) => run(async () => { const p = players.find(pl => pl.name === captain); await api.updateTeam(t.id, { name, seed: t.seed, captainPlayerId: p ? p.id : null, captainName: captain, crestEmoji: crest }); await reloadSeason(); })}
                        onDelete={() => { if (window.confirm(`Remove ${t.name} from this season?`)) run(async () => { await api.deleteTeam(t.id); await reloadSeason(); }); }} />
                    )}
                  </div>
                )}
              </div>
            ))}
            {mayEditSeason && <AddTeamForm onAdd={(t) => run(async () => { await api.addTeam(season.id, t); await reloadSeason(); })} />}
          </>
        )}
        <datalist id="lg-players">{players.map(p => <option key={p.id} value={p.name} />)}</datalist>
      </div>
    );
  }

  // ---------- HUB ----------
  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <span style={{ fontSize: 20, fontWeight: 800 }}>Leagues</span>
        {canManage && <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}><I.Plus size={14} /> New League</button>}
      </div>
      {leagues.length === 0 && <div className="empty-state">No leagues yet.{canManage ? " Create one to get started." : ""}</div>}
      {leagues.map(l => (
        <div key={l.id} className="card" style={{ marginBottom: 10 }}>
          <button onClick={() => openLeague(l)} style={{ width: "100%", background: "none", border: "none", color: C.text, cursor: "pointer", textAlign: "left", padding: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontWeight: 800, fontSize: 16 }}>{l.name}</span>
              <DivisionBadge division={l.division} />
              <span className="badge live-badge">{levelLabel(l.abilityLevel)}</span>
            </div>
            <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>
              {l.currentSeason ? `${l.currentSeason.label} · ${l.currentSeason.status} · Week ${l.currentSeason.currentWeek}/${l.currentSeason.weeks} · ${l.currentSeason.teamCount} teams` : "No season yet"}
            </div>
            {(!l.currentSeason || l.currentSeason.needsSetup) && canManage && (
              <div style={{ marginTop: 6, display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 700, color: C.orange, background: C.orangeDim, padding: "3px 10px", borderRadius: 20 }}>
                ⚠ {l.currentSeason ? l.currentSeason.setupHint : "Needs a season"} — tap to finish setup
              </div>
            )}
            {l.currentSeason && l.currentSeason.topStandings && l.currentSeason.topStandings.length > 0 && (
              <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 3 }}>
                {l.currentSeason.topStandings.map(r => (
                  <div key={r.teamId} style={{ display: "flex", gap: 8, fontSize: 12 }}>
                    <span style={{ fontFamily: fontMono, color: r.rank === 1 ? C.gold : C.textMuted, width: 14 }}>{r.rank}</span>
                    <span style={{ flex: 1, fontWeight: r.rank === 1 ? 700 : 500 }}>{r.crestEmoji} {r.teamName}</span>
                    <span style={{ fontFamily: fontMono, color: C.textSec }}>{r.nightsWon}–{r.nightsLost}</span>
                  </div>
                ))}
              </div>
            )}
          </button>
          {canDeleteLeague(l) && (
            <div style={{ marginTop: 8, textAlign: "right" }}>
              <button className="btn btn-ghost btn-sm" style={{ color: C.red }} onClick={() => { if (window.confirm(`Delete league "${l.name}" and all its data?`)) run(async () => { await api.deleteLeague(l.id); }); }}><I.Trash size={12} /> Delete</button>
            </div>
          )}
        </div>
      ))}

      {showCreate && <CreateLeagueModal onClose={() => setShowCreate(false)} onCreate={(l) => run(async () => { const created = await api.addLeague(l); setShowCreate(false); const nl = await api.getLeague(created.id); setSeasonForLeague(nl); })} />}
      {seasonForLeague && <CreateSeasonModal league={seasonForLeague} players={players} onClose={() => setSeasonForLeague(null)} onCreate={(s, teamDefs) => run(async () => {
        const created = await api.createSeason(seasonForLeague.id, s);
        for (const def of teamDefs) {
          const p = def.captain ? players.find(pl => pl.name === def.captain) : null;
          const withTeam = await api.addTeam(created.id, { name: def.name, captainName: def.captain, captainPlayerId: p ? p.id : null });
          // A captain who also plays goes on the roster too; a non-playing captain doesn't.
          if (def.captain && def.captainPlays) {
            const team = withTeam.teams.find(t => t.name === def.name);
            if (team) await api.addMember(team.id, { playerId: p ? p.id : null, playerName: def.captain, role: "player" });
          }
        }
        setSeasonForLeague(null);
        openSeason(created.id);
      })} />}
    </div>
  );
}

function TeamNameEditor({ team, captainChoices, onSave, onDelete }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(team.name);
  const [crest, setCrest] = useState(team.crestEmoji || "");
  const [captain, setCaptain] = useState(team.captainName || "");
  useEffect(() => { setName(team.name); setCrest(team.crestEmoji || ""); setCaptain(team.captainName || ""); },
    [team.id, team.name, team.crestEmoji, team.captainName]);
  if (!open) {
    return (
      <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-end" }}>
        <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, padding: "3px 8px" }} onClick={() => setOpen(true)}><I.Edit size={12} /> Edit team</button>
      </div>
    );
  }
  return (
    <div style={{ marginTop: 10, borderTop: `1px solid ${C.borderLight}`, paddingTop: 10 }}>
      <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
        <input className="input" value={name} onChange={e => setName(e.target.value)} style={{ flex: 1, fontSize: 13, padding: "8px 10px" }} placeholder="Team name" />
        <input className="input" value={crest} onChange={e => setCrest(e.target.value)} maxLength={2} style={{ width: 52, textAlign: "center", fontSize: 13, padding: "8px 4px" }} placeholder="🏝️" />
      </div>
      <select className="input" value={captain} onChange={e => setCaptain(e.target.value)} style={{ marginBottom: 6, fontSize: 13, padding: "8px 10px" }}>
        <option value="">— no captain —</option>
        {captainChoices.map(n => <option key={n} value={n}>{n}</option>)}
      </select>
      <div style={{ fontSize: 10.5, color: C.textMuted, marginBottom: 6 }}>The captain doesn't have to play — add them to the roster only if they do.</div>
      <div style={{ display: "flex", gap: 6 }}>
        <button className="btn btn-primary btn-sm" disabled={!name.trim()} onClick={() => { onSave(name.trim(), crest || null, captain || null); setOpen(false); }}>Save</button>
        <button className="btn btn-ghost btn-sm" onClick={() => { setName(team.name); setCrest(team.crestEmoji || ""); setCaptain(team.captainName || ""); setOpen(false); }}>Cancel</button>
        <span style={{ flex: 1 }} />
        <button className="btn btn-ghost btn-sm" style={{ color: C.red }} onClick={onDelete}><I.Trash size={12} /> Remove team</button>
      </div>
    </div>
  );
}

function AddTeamForm({ onAdd }) {
  const [name, setName] = useState(""); const [crest, setCrest] = useState(""); const [cap, setCap] = useState("");
  return (
    <div className="card" style={{ marginTop: 8 }}>
      <div className="section-title">Add team</div>
      <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
        <input className="input" placeholder="Team name" value={name} onChange={e => setName(e.target.value)} style={{ flex: 1 }} />
        <input className="input" placeholder="🏝️" value={crest} onChange={e => setCrest(e.target.value)} style={{ width: 56, textAlign: "center" }} maxLength={2} />
      </div>
      <input className="input" list="lg-players" placeholder="Captain (optional)" value={cap} onChange={e => setCap(e.target.value)} style={{ marginBottom: 6 }} />
      <button className="btn btn-primary btn-sm btn-full" disabled={!name.trim()} onClick={() => { onAdd({ name: name.trim(), crestEmoji: crest || null, captainName: cap || null }); setName(""); setCrest(""); setCap(""); }}>Add team</button>
    </div>
  );
}

// `available` = players not already rostered anywhere else in this season, so
// an unavailable player can never be picked (rather than erroring on save).
function AddMemberForm({ available, onAdd }) {
  const [name, setName] = useState(""); const [role, setRole] = useState("player");
  const [gf, setGf] = useState("all");   // all | M | F
  const submit = () => {
    const p = available.find(pl => pl.name === name);
    if (!p) return;
    onAdd({ playerId: p.id, playerName: p.name, role });
    setName("");
  };
  if (available.length === 0) {
    return <div style={{ fontSize: 11, color: C.textMuted, marginTop: 8 }}>Every player is already on a team this season.</div>;
  }
  // Only worth filtering when the pool actually has both (i.e. a mixed league).
  const hasBoth = available.some(p => p.gender === "M") && available.some(p => p.gender === "F");
  const shown = gf === "all" ? available : available.filter(p => p.gender === gf);
  return (
    <>
    {hasBoth && (
      <div style={{ display: "flex", gap: 5, marginTop: 8, alignItems: "center" }}>
        {[["all", "All"], ["M", "Men"], ["F", "Women"]].map(([k, lbl]) => (
          <button key={k} className={`toggle-btn ${gf === k ? "active" : ""}`} style={{ padding: "3px 10px", fontSize: 10 }}
            onClick={() => { setGf(k); setName(""); }}>
            {lbl} {k === "all" ? available.length : available.filter(p => p.gender === k).length}
          </button>
        ))}
      </div>
    )}
    <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
      <select className="input" value={name} onChange={e => setName(e.target.value)} style={{ flex: 1, fontSize: 13, padding: "8px 10px" }}>
        <option value="">— add player —</option>
        {shown.map(p => <option key={p.id} value={p.name}>{p.name}{p.gender ? ` (${p.gender})` : ""}</option>)}
      </select>
      <select className="input" value={role} onChange={e => setRole(e.target.value)} style={{ width: 118, fontSize: 12, padding: "8px" }}>
        <option value="player">Player</option>
        <option value="injuryreplacement">Injury sub</option>
        <option value="bench">Bench</option>
      </select>
      <button className="btn btn-primary btn-sm" disabled={!name} onClick={submit}><I.Plus size={14} /></button>
    </div>
    </>
  );
}

function CreateLeagueModal({ onClose, onCreate }) {
  const [name, setName] = useState(""); const [division, setDivision] = useState("mixed"); const [level, setLevel] = useState("3.5");
  return (
    <Modal onClose={onClose}>
      <ModalHeader title="New League" onClose={onClose} />
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div><div className="section-title">Name</div><input className="input" placeholder="Tuesday Mixed" value={name} onChange={e => setName(e.target.value)} /></div>
        <div><div className="section-title">Division</div>
          <div style={{ display: "flex", gap: 6 }}>{["mens", "womens", "mixed"].map(d => <button key={d} className={`toggle-btn ${division === d ? "active" : ""}`} onClick={() => setDivision(d)} style={{ flex: 1 }}>{DIVISIONS[d].short}</button>)}</div>
        </div>
        <div><div className="section-title">Ability level</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>{["2.5", "3.0", "3.5", "4.0", "4.5", ""].map(v => <button key={v || "open"} className={`toggle-btn ${level === v ? "active" : ""}`} onClick={() => setLevel(v)}>{v || "Open"}</button>)}</div>
        </div>
        <button className="btn btn-primary btn-full" disabled={!name.trim()} onClick={() => onCreate({ name: name.trim(), division, abilityLevel: level === "" ? null : parseFloat(level), format: "teamvsteam" })}>Create league</button>
      </div>
    </Modal>
  );
}

function CreateSeasonModal({ league, players, onClose, onCreate }) {
  const [label, setLabel] = useState("Fall " + new Date().getFullYear());
  const [start, setStart] = useState(new Date().toISOString().slice(0, 10));
  const [weeks, setWeeks] = useState(8);
  const [night, setNight] = useState("Tuesday");
  const [teams, setTeams] = useState([1, 2, 3, 4].map(i => ({ name: `Team ${i}`, captain: "" })));
  const setCount = (n) => setTeams(prev => {
    const next = prev.slice(0, n);
    while (next.length < n) next.push({ name: `Team ${next.length + 1}`, captain: "" });
    return next;
  });
  const setField = (i, k, v) => setTeams(ts => ts.map((t, j) => j === i ? { ...t, [k]: v } : t));
  // Only division-eligible players, and each captain can lead just one team.
  const eligible = players.filter(p => divisionAllows(league.division, p.gender));
  const takenCaptains = new Set(teams.map(t => t.captain).filter(Boolean));
  const names = teams.map(t => t.name);
  const valid = names.every(n => n.trim()) && new Set(names.map(n => n.trim().toLowerCase())).size === names.length;
  return (
    <Modal onClose={onClose}>
      <ModalHeader title={`New season · ${league.name}`} onClose={onClose} />
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div><div className="section-title">Season label</div><input className="input" value={label} onChange={e => setLabel(e.target.value)} /></div>
        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ flex: 1 }}><div className="section-title">First night</div><input className="input" type="date" value={start} onChange={e => setStart(e.target.value)} /></div>
          <div style={{ width: 90 }}><div className="section-title">Weeks</div><input className="input" type="number" value={weeks} onChange={e => setWeeks(e.target.value)} /></div>
        </div>
        <div><div className="section-title">Play night</div>
          <select className="input" value={night} onChange={e => setNight(e.target.value)}>{DAYS.map(d => <option key={d} value={d}>{d}</option>)}</select>
        </div>
        <div>
          <div className="section-title">How many teams?</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
            {[2, 4, 6, 8, 10, 12].map(n => (
              <button key={n} className={`toggle-btn ${names.length === n ? "active" : ""}`} onClick={() => setCount(n)}>{n}</button>
            ))}
          </div>
          {teams.map((t, i) => (
            <div key={i} style={{ marginBottom: 8 }}>
              <div style={{ display: "flex", gap: 6 }}>
                <input className="input" value={t.name} onChange={e => setField(i, "name", e.target.value)}
                  style={{ flex: 1, fontSize: 14, padding: "9px 12px" }} placeholder={`Team ${i + 1}`} />
                <select className="input" value={t.captain} onChange={e => setField(i, "captain", e.target.value)}
                  style={{ flex: 1, fontSize: 13, padding: "9px 8px" }}>
                  <option value="">— captain —</option>
                  {eligible.filter(p => p.name === t.captain || !takenCaptains.has(p.name)).map(p => (
                    <option key={p.id} value={p.name}>{p.name}</option>
                  ))}
                </select>
              </div>
              {t.captain && (
                <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, color: C.textSec, marginTop: 4, cursor: "pointer" }}>
                  <input type="checkbox" checked={!!t.captainPlays} onChange={e => setField(i, "captainPlays", e.target.checked)} />
                  {t.captain} also plays on this team
                </label>
              )}
            </div>
          ))}
          <div style={{ fontSize: 11, color: C.textMuted }}>
            A captain doesn't have to play — tick the box if they do. Either way they can't captain or play for another team this season. You can rename teams, change captains, and add more teams later.
          </div>
        </div>
        <button className="btn btn-primary btn-full" disabled={!valid}
          onClick={() => onCreate({ label: label.trim(), startDate: start, weeks: parseInt(weeks, 10) || 8, playNight: night, rules: null },
            teams.map(t => ({ name: t.name.trim(), captain: t.captain || null, captainPlays: !!t.captainPlays })))}>
          Create season with {teams.length} teams
        </button>
      </div>
    </Modal>
  );
}

export default function App() {
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(() => {
    try { return localStorage.getItem("dt_theme") !== "light"; } catch { return true; }
  });
  // Update theme colors
  C = darkMode ? DARK : LIGHT;

  const toggleTheme = () => {
    const next = !darkMode;
    setDarkMode(next);
    C = next ? DARK : LIGHT;
    try { localStorage.setItem("dt_theme", next ? "dark" : "light"); } catch {}
  };

  const [showGuide, setShowGuide] = useState(false);

  const [session, setSession] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [bootstrapNeeded, setBootstrapNeeded] = useState(false);
  const [players, setPlayers] = useState([]);
  const [courts, setCourts] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const rtRef = useRef(null);
  const leagueFixtureCb = useRef(null);
  const [activeTournament, setActiveTournament] = useState(null);
  const [viewingTournament, setViewingTournament] = useState(null);
  const [building, setBuilding] = useState(false);
  const [checkingIn, setCheckingIn] = useState(null); // tournament in "registration" status being checked in
  const [buildingFromReg, setBuildingFromReg] = useState(null); // { tournament, checkedInPlayers } for pre-reg → build flow
  const [tab, setTab] = useState("tournaments");

  // Calculate DPR from all finished tournaments
  const dprMap = useMemo(() => { try { return calculateAllDPR(tournaments, players); } catch(e) { return {}; } }, [tournaments, players]);

  // Restore session from stored tokens (the /me call validates them)
  useEffect(() => {
    if (api.hasSession()) {
      // Accounts without an email must complete the email step at login.
      api.me().then(s => { if (s.email) setSession(s); else api.logout(); }).catch(() => api.logout());
    }
  }, []);

  useEffect(() => {
    const loadPlayers = () => api.listPlayers().then(ps => setPlayers(ps.sort((a, b) => a.name.localeCompare(b.name)))).catch(() => {});
    const loadCourts = () => api.listCourts().then(cs => setCourts(cs.sort((a, b) => a.number - b.number))).catch(() => {});
    const loadTournaments = () => api.listTournaments().then(ts => { setTournaments(ts); setLoading(false); }).catch(() => setLoading(false));
    const loadLeagues = () => api.listLeagues().then(setLeagues).catch(() => {});
    api.anyUsersExist().then(any => { setBootstrapNeeded(!any); setUsersLoaded(true); }).catch(() => {});
    loadPlayers(); loadCourts(); loadTournaments(); loadLeagues();
    const rt = api.connectRealtime({
      onTournamentsChanged: loadTournaments, onPlayersChanged: loadPlayers, onCourtsChanged: loadCourts,
      onLeaguesChanged: loadLeagues,
      onLeagueFixtureUpdated: (dto) => { if (leagueFixtureCb.current) leagueFixtureCb.current(dto); },
    });
    rtRef.current = rt;
    return () => rt.stop();
  }, []);

  // Load users list for the admin Users tab
  useEffect(() => {
    if (session && (session.role === "admin" || session.role === "superadmin")) {
      api.listUsers().then(setUsers).catch(() => setUsers([]));
    } else {
      setUsers([]);
    }
  }, [session]);

  // Sync active tournament from Firestore
  useEffect(() => {
    if (activeTournament) {
      const latest = tournaments.find(t => t.id === activeTournament.id);
      if (latest) setActiveTournament(latest);
    }
  }, [tournaments]);

  const role = session?.role;
  const isSuperAdmin = role === "superadmin";
  const isAdmin = role === "admin" || isSuperAdmin;        // admin-or-super (edit any tournament, courts, users)
  const isManager = role === "manager";
  const canManageTournaments = isAdmin || isManager;       // create/score tournaments
  const canManagePlayers = isAdmin || isManager;           // add/edit players (delete = super only)
  const canManageCourts = isAdmin;                          // add/edit courts (delete = super only)
  const canViewCourts = isAdmin || isManager;               // managers see courts to assign them
  const canManageUsers = isAdmin;                            // add/edit users (delete = super only)
  const ownsTournament = (t) => !!t && !!session && t.createdBy === session.id;
  const canDeleteTournament = (t) => isSuperAdmin || ((isAdmin || isManager) && ownsTournament(t));
  // Admins/supers edit any tournament; managers edit only their own; viewers none.
  const canEditTournament = (t) => isAdmin || (isManager && ownsTournament(t));

  // Leagues mirror tournament ownership: commissioner = league creator.
  const canManageLeagues = isAdmin || isManager;
  const ownsLeague = (l) => !!l && !!session && l.createdBy === session.id;
  const canEditLeague = (l) => isAdmin || (isManager && ownsLeague(l));
  const canDeleteLeague = (l) => isSuperAdmin || ((isAdmin || isManager) && ownsLeague(l));

  const fbAddPlayer = async (p) => { await api.addPlayer(p); };
  const fbUpdatePlayer = async (p) => { await api.updatePlayer(p); };
  const fbDeletePlayer = async (id) => { await api.deletePlayer(id); };

  const fbAddCourt = async (c) => { await api.addCourt(c); };
  const fbDeleteCourt = async (id) => { await api.deleteCourt(id); };

  const refreshUsers = () => api.listUsers().then(setUsers).catch(() => {});
  const fbAddUser = async (u) => { await api.addUser(u); await refreshUsers(); };
  const fbUpdateUser = async (u) => {
    await api.updateUser(u);
    await refreshUsers();
    // Editing yourself (rename etc.) must be reflected in the session header
    if (session && u.id === session.id) api.me().then(setSession).catch(() => {});
  };
  const fbDeleteUser = async (id) => { await api.deleteUser(id); await refreshUsers(); };
  const fbUnlockUser = async (id) => {
    try { await api.unlockUser(id); await refreshUsers(); }
    catch (err) { alert("Error: " + err.message); }
  };

  const fbSaveTournament = async (t) => { await api.saveTournament(t); };
  const fbDeleteTournament = async (id) => { await api.deleteTournament(id); };

  const handleLogin = async (username, password) => {
    try {
      const s = await api.login(username, password);
      if (!s) return false;
      if (s.lockedOut) return `locked:${s.minutes}`;
      if (!s.email) return "needs-email"; // migrated account — collect email before proceeding
      setSession(s);
      return true;
    } catch { return false; }
  };
  const handleProvideEmail = async (email) => {
    try {
      await api.setMyEmail(email);
      setSession(await api.me());
      return true;
    } catch { return false; }
  };
  const handleBootstrap = async (username, email, password) => {
    const s = await api.bootstrapAdmin(username, email, password);
    setSession(s);
    setBootstrapNeeded(false);
  };
  const handleLogout = async () => { await api.logout(); setSession(null); };

  const handleStartTournament = async (setup) => {
    try {
      let t;
      if (setup.format === "kingofhill") {
        t = { ...setup, currentCourts: setup.courts, currentRound: 1, rounds: [], status: "playing" };
      } else if (setup.format === "roundrobin") {
        t = { ...setup, status: "playing" };
      } else if (setup.format === "bracket") {
        t = { ...setup, status: "playing" };
      } else if (setup.format === "rrplayer") {
        t = { ...setup, status: "playing" };
      } else {
        t = { ...setup, currentCourts: setup.courts, currentRound: 1, rounds: [], status: "playing" };
      }
      // If this was built from a registration, carry over the preRegistration data (for waitlist subs)
      if (buildingFromReg) {
        t.preRegistration = buildingFromReg.tournament.preRegistration;
        // Delete the registration-status tournament since it's now playing
        await fbDeleteTournament(buildingFromReg.tournament.id);
        setBuildingFromReg(null);
      }
      await fbSaveTournament(t);
      setActiveTournament(t);
      setBuilding(false);
    } catch (err) {
      console.error("Failed to start tournament:", err);
      alert("Error starting tournament: " + err.message);
    }
  };

  const handleCreateRegistration = async (setup) => {
    try {
      await fbSaveTournament(setup);
      setBuilding(false);
    } catch (err) {
      console.error("Failed to create registration:", err);
      alert("Error: " + err.message);
    }
  };

  const handleProceedToBuild = (regTournament, checkedInPlayers) => {
    setBuildingFromReg({ tournament: regTournament, checkedInPlayers });
    setCheckingIn(null);
    setBuilding(true);
  };

  const handleUpdateTournament = async (t) => {
    setActiveTournament(t);
    await fbSaveTournament(t);
  };

  // Update without setting as active tournament (for check-in screen edits)
  const handleUpdateTournamentQuiet = async (t) => {
    await fbSaveTournament(t);
    // Update checkingIn so the CheckInScreen re-renders with fresh data
    if (checkingIn && checkingIn.id === t.id) setCheckingIn(t);
  };

  const handlePauseTournament = async (t) => {
    const paused = { ...t, status: "paused", pausedAt: new Date().toISOString() };
    await fbSaveTournament(paused);
    setActiveTournament(null);
  };

  const handleResumeTournament = async (t) => {
    // Registration status → go to check-in screen
    if (t.status === "registration") {
      setCheckingIn(t);
      return;
    }
    // If paused, only admins can resume (changes status to playing)
    if (t.status === "paused") {
      if (!isAdmin) { setActiveTournament(t); return; } // viewers can view but not change status
      const resumed = { ...t, status: "playing" };
      await fbSaveTournament(resumed);
      setActiveTournament(resumed);
    } else {
      setActiveTournament(t);
    }
  };

  const handleEndTournament = async (finished) => {
    await fbSaveTournament(finished);
    setActiveTournament(null);
  };

  const handleSeedToBracket = async (rrTournament, seededTeams) => {
    // Save the RR tournament as finished
    const allDone = [...(rrTournament.completedRounds || []), { courts: (rrTournament.schedule || [])[rrTournament.currentMatchRound || 0] || [] }];
    const finishedRR = { ...rrTournament, rounds: allDone, status: "finished", finishedAt: new Date().toISOString() };
    await fbSaveTournament(finishedRR);
    // Create a new bracket tournament seeded from RR standings
    const bracketRounds = buildBracketRounds(seededTeams);
    const bracketT = {
      id: uid(), name: rrTournament.name + " — Bracket", format: "bracket",
      division: rrTournament.division || "mens",
      gameMinutes: rrTournament.gameMinutes, bracketRounds, teams: seededTeams,
      players: seededTeams.flatMap(t => t.players), status: "playing",
      seededFrom: rrTournament.id, createdAt: new Date().toISOString(),
    };
    await fbSaveTournament(bracketT);
    setActiveTournament(bracketT);
  };

  // ── RENDER ──

  // Spectator view: ?spectate=TOURNAMENT_ID bypasses login
  const spectateId = new URLSearchParams(window.location.search).get("spectate");
  if (spectateId) {
    return <SpectatorView tournamentId={spectateId} />;
  }

  // Registration page: ?register=TOURNAMENT_ID bypasses login
  const registerId = new URLSearchParams(window.location.search).get("register");
  if (registerId) {
    return <RegistrationPage tournamentId={registerId} />;
  }

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: C.bg, fontFamily: font, color: C.textMuted }}>
        <style>{buildCSS()}</style>
        <div style={{ textAlign: "center" }}><img src="/dip-logo.png" alt="" style={{ maxWidth: 150, width: "50%", height: "auto", marginBottom: 10 }} /><div>Loading...</div></div>
      </div>
    );
  }

  if (!session) {
    return <><style>{buildCSS()}</style><LoginScreen usersLoaded={usersLoaded} bootstrapNeeded={bootstrapNeeded} onLogin={handleLogin} onBootstrap={handleBootstrap} onProvideEmail={handleProvideEmail} /></>;
  }

  // Active tournament view (King of the Hill)
  if (activeTournament && (!activeTournament.format || activeTournament.format === "kingofhill")) {
    return (
      <div style={{ fontFamily: font, background: C.bg, color: C.text, minHeight: "100vh", maxWidth: 520, margin: "0 auto" }}>
        <style>{buildCSS()}</style>
        <div style={{ padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${C.border}`, position: "sticky", top: 0, background: C.bg, zIndex: 90 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setActiveTournament(null)}><I.ChevLeft size={18} /></button>
            <span style={{ fontSize: 18, fontWeight: 800, display: "inline-flex", alignItems: "center", gap: 7 }}><img src="/dip-logo.png" alt="" style={{ height: 26, width: "auto" }} />Dinks In Paradise</span>
          </div>
          <span className="badge live-badge">LIVE</span>
        </div>
        <ActiveTournament tournament={activeTournament} allPlayers={players} onUpdateTournament={handleUpdateTournament} onEnd={handleEndTournament} onPause={handlePauseTournament} isAdmin={canEditTournament(activeTournament)} />
      </div>
    );
  }

  // Active round robin
  if (activeTournament && activeTournament.format === "roundrobin") {
    return (
      <div style={{ fontFamily: font, background: C.bg, color: C.text, minHeight: "100vh", maxWidth: 520, margin: "0 auto" }}>
        <style>{buildCSS()}</style>
        <div style={{ padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${C.border}`, position: "sticky", top: 0, background: C.bg, zIndex: 90 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setActiveTournament(null)}><I.ChevLeft size={18} /></button>
            <span style={{ fontSize: 18, fontWeight: 800, display: "inline-flex", alignItems: "center", gap: 7 }}><img src="/dip-logo.png" alt="" style={{ height: 26, width: "auto" }} />Dinks In Paradise</span>
          </div>
          <span className="badge live-badge">ROUND ROBIN</span>
        </div>
        <ActiveRoundRobin tournament={activeTournament} onUpdateTournament={handleUpdateTournament} onEnd={handleEndTournament} onPause={handlePauseTournament} onSeedToBracket={handleSeedToBracket} isAdmin={canEditTournament(activeTournament)} />
      </div>
    );
  }

  // Active bracket
  if (activeTournament && activeTournament.format === "bracket") {
    return (
      <div style={{ fontFamily: font, background: C.bg, color: C.text, minHeight: "100vh", margin: "0 auto", overflowX: "hidden" }}>
        <style>{buildCSS()}</style>
        <div style={{ padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${C.border}`, position: "sticky", top: 0, background: C.bg, zIndex: 90, maxWidth: 520, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setActiveTournament(null)}><I.ChevLeft size={18} /></button>
            <span style={{ fontSize: 18, fontWeight: 800, display: "inline-flex", alignItems: "center", gap: 7 }}><img src="/dip-logo.png" alt="" style={{ height: 26, width: "auto" }} />Dinks In Paradise</span>
          </div>
          <span className="badge live-badge">BRACKET</span>
        </div>
        <ActiveBracket tournament={activeTournament} onUpdateTournament={handleUpdateTournament} onEnd={handleEndTournament} onPause={handlePauseTournament} isAdmin={canEditTournament(activeTournament)} />
      </div>
    );
  }

  // Active PBR
  if (activeTournament && activeTournament.format === "rrplayer") {
    return (
      <div style={{ fontFamily: font, background: C.bg, color: C.text, minHeight: "100vh", maxWidth: 520, margin: "0 auto" }}>
        <style>{buildCSS()}</style>
        <div style={{ padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${C.border}`, position: "sticky", top: 0, background: C.bg, zIndex: 90 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setActiveTournament(null)}><I.ChevLeft size={18} /></button>
            <span style={{ fontSize: 18, fontWeight: 800, display: "inline-flex", alignItems: "center", gap: 7 }}><img src="/dip-logo.png" alt="" style={{ height: 26, width: "auto" }} />Dinks In Paradise</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span className="badge live-badge">PBR</span>
          </div>
        </div>
        <ActivePBR tournament={activeTournament} onUpdateTournament={handleUpdateTournament} onEnd={handleEndTournament} isAdmin={canEditTournament(activeTournament)} isSuperAdmin={isSuperAdmin} />
      </div>
    );
  }

  // Viewing finished tournament
  if (viewingTournament) {
    return (
      <div style={{ fontFamily: font, background: C.bg, color: C.text, minHeight: "100vh", maxWidth: 520, margin: "0 auto" }}>
        <style>{buildCSS()}</style>
        <div style={{ padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${C.border}`, position: "sticky", top: 0, background: C.bg, zIndex: 90 }}>
          <span style={{ fontSize: 18, fontWeight: 800, display: "inline-flex", alignItems: "center", gap: 7 }}><img src="/dip-logo.png" alt="" style={{ height: 26, width: "auto" }} />Dinks In Paradise</span>
          <span className="badge done-badge">COMPLETE</span>
        </div>
        <ViewTournament tournament={viewingTournament} onBack={() => setViewingTournament(null)} isAdmin={canEditTournament(viewingTournament)} isSuperAdmin={isSuperAdmin} onUpdate={async (t) => { await fbSaveTournament(t); setViewingTournament(t); }} />
      </div>
    );
  }

  // Check-in screen for registration tournaments
  if (checkingIn) {
    const latestReg = tournaments.find(t => t.id === checkingIn.id) || checkingIn;
    return (
      <div style={{ fontFamily: font, background: C.bg, color: C.text, minHeight: "100vh", maxWidth: 520, margin: "0 auto" }}>
        <style>{buildCSS()}</style>
        <div style={{ padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${C.border}`, position: "sticky", top: 0, background: C.bg, zIndex: 90 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setCheckingIn(null)}><I.ChevLeft size={18} /></button>
            <span style={{ fontSize: 18, fontWeight: 800, display: "inline-flex", alignItems: "center", gap: 7 }}><img src="/dip-logo.png" alt="" style={{ height: 26, width: "auto" }} />Check-In</span>
          </div>
          <span className="badge" style={{ background: C.blueDim, color: C.blue }}>REGISTRATION</span>
        </div>
        <CheckInScreen tournament={latestReg} allPlayers={players} onUpdateTournament={handleUpdateTournamentQuiet} onProceedToBuild={handleProceedToBuild} onDelete={(id) => { fbDeleteTournament(id); setCheckingIn(null); }} onBack={() => setCheckingIn(null)} isAdmin={canEditTournament(latestReg)} />
      </div>
    );
  }

  // Building tournament
  if (building) {
    return (
      <div style={{ fontFamily: font, background: C.bg, color: C.text, minHeight: "100vh", maxWidth: 520, margin: "0 auto" }}>
        <style>{buildCSS()}</style>
        <div style={{ padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${C.border}`, position: "sticky", top: 0, background: C.bg, zIndex: 90 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setBuilding(false)}><I.ChevLeft size={18} /></button>
            <span style={{ fontSize: 18, fontWeight: 800, display: "inline-flex", alignItems: "center", gap: 7 }}><img src="/dip-logo.png" alt="" style={{ height: 26, width: "auto" }} />Build Tournament</span>
          </div>
        </div>
        <BuildTournament players={players} courts={courts} onStart={handleStartTournament} onCancel={() => { setBuilding(false); setBuildingFromReg(null); }} onCreateRegistration={handleCreateRegistration} onUpdatePlayer={fbUpdatePlayer} preSelectedPlayers={buildingFromReg?.checkedInPlayers || null} preRegTournament={buildingFromReg?.tournament || null} dprMap={dprMap} />
      </div>
    );
  }

  // Main tab view
  // Tabs by role: everyone gets Tourneys + Players; Courts adds managers;
  // Users is admin/super only. Viewers get the first two, read-only.
  const tabs = [
    { key: "tournaments", label: "Tourneys", icon: I.Trophy },
    { key: "leagues", label: "Leagues", icon: I.Medal },
    { key: "players", label: "Players", icon: I.Users },
    ...(canViewCourts ? [{ key: "courts", label: "Courts", icon: I.Grid }] : []),
    ...(canManageUsers ? [{ key: "users", label: "Users", icon: I.Settings }] : []),
  ];

  return (
    <div style={{ fontFamily: font, background: C.bg, color: C.text, minHeight: "100vh", maxWidth: 520, margin: "0 auto" }}>
      <style>{buildCSS()}</style>
      <div style={{ padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${C.border}` }}>
        <span style={{ fontSize: 18, fontWeight: 800, display: "inline-flex", alignItems: "center", gap: 7 }}><img src="/dip-logo.png" alt="" style={{ height: 26, width: "auto" }} />Dinks In Paradise</span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowGuide(true)} title="Help" style={{ padding: 4 }}><I.Help size={16} /></button>
          <button className="btn btn-ghost btn-sm" onClick={toggleTheme} title={darkMode ? "Light mode" : "Dark mode"} style={{ padding: 4 }}>{darkMode ? <I.Sun size={15} /> : <I.Moon size={15} />}</button>
          <span style={{ fontSize: 12, color: C.textSec }}>{session.username}</span>
          <span className={`badge ${isSuperAdmin ? "admin-badge" : isAdmin ? "admin-badge" : "viewer-badge"}`}>{session.role === "superadmin" ? "super" : session.role}</span>
          <button className="btn btn-ghost btn-sm" onClick={handleLogout} title="Logout"><I.Logout size={16} /></button>
        </div>
      </div>
      {showGuide && <UserGuide onClose={() => setShowGuide(false)} />}
      <div className="tab-bar">
        {tabs.map(t => <button key={t.key} className={`tab ${tab === t.key ? "active" : ""}`} onClick={() => setTab(t.key)}><t.icon size={18} />{t.label}</button>)}
      </div>
      {tab === "tournaments" && <TournamentsTab tournaments={tournaments} onResume={handleResumeTournament} onView={t => setViewingTournament(t)} onBuild={() => setBuilding(true)} onDelete={fbDeleteTournament} onImport={{ players, handler: async (tournament, newPlayers) => { for (const p of newPlayers) { await fbAddPlayer(p); } await fbSaveTournament(tournament); }}} isAdmin={canManageTournaments} isSuperAdmin={isSuperAdmin} canManageAny={isSuperAdmin || isAdmin || isManager} canDelete={canDeleteTournament} canEdit={canEditTournament} onEditTournament={fbSaveTournament} currentUserId={session?.id} />}
      {tab === "players" && <PlayersTab players={players} onAddPlayer={fbAddPlayer} onUpdatePlayer={fbUpdatePlayer} onDeletePlayer={fbDeletePlayer} tournaments={tournaments} isAdmin={canManagePlayers} isSuperAdmin={isSuperAdmin} dprMap={dprMap} />}
      {tab === "leagues" && !(LEAGUES_PUBLIC || isSuperAdmin) && <LeaguesComingSoon isSuperAdmin={isSuperAdmin} />}
      {tab === "leagues" && (LEAGUES_PUBLIC || isSuperAdmin) && <LeaguesTab leagues={leagues} players={players} session={session} canManage={canManageLeagues} canEditLeague={canEditLeague} canDeleteLeague={canDeleteLeague} isSuperAdmin={isSuperAdmin} realtime={rtRef} fixtureCb={leagueFixtureCb} />}
      {tab === "courts" && canViewCourts && <CourtsTab courts={courts} onAddCourt={fbAddCourt} onDeleteCourt={fbDeleteCourt} isAdmin={canManageCourts} isSuperAdmin={isSuperAdmin} />}
      {tab === "users" && canManageUsers && <UsersTab users={users} onAddUser={fbAddUser} onUpdateUser={fbUpdateUser} onDeleteUser={fbDeleteUser} onUnlockUser={fbUnlockUser} currentUser={session} />}
    </div>
  );
}
